import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api from "../api";
import Breadcrumb from "../components/Breadcrumb";
import Card from "../components/cards/Card";
import styles from "./MarketingReports.module.css";
import EasePicker, { AmpPlugin, LockPlugin, PresetPlugin, RangePlugin } from "react-easepick";
import MultipleSelect from "../components/MultipleSelect";
import { CourseCategories, CourseType, CourseTypes } from "../common/constants";
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout";
import { addDays } from "date-fns";
import { formatNumber, formatSeconds } from "../utils";
import typo from "../typography.module.css";
import Skeleton from "../components/Skeleton";
import MultiStateSwitch from "../components/MultiStateSwitch";
import TextInput from "../components/TextInput";
import Select from "../components/Select";
import Button from "../components/Button";
import Dialog from "../components/dialogs/Dialog";
import PercentageBadge from "../components/analytics/PercentageBadge";
import MetricStatBlock from "../components/analytics/MetricStatBlock";
import { Helmet, HelmetProvider } from "react-helmet-async";
import Table from "../components/Table";
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    Legend,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Cell,
} from 'recharts';
import { ReactComponent as ExportIcon } from "../assets/images/icons/ic-export.svg";

const WEB_VITALS_DEVICE_OPTIONS = [
    { id: 'desktop', name: 'Desktop', value: 'desktop' },
    { id: 'mobile', name: 'Mobile', value: 'mobile' },
];
const WEB_VITALS_THRESHOLDS_BY_DEVICE = {
    mobile: {
        lcp_p75: { good: 2000, poor: 3500 },
        cls_p75: { good: 0.1, poor: 0.25 },
        inp_p75: { good: 200, poor: 500 },
        fcp_p75: { good: 1800, poor: 3000 },
        ttfb_p75: { good: 800, poor: 1800 },
    },
    desktop: {
        lcp_p75: { good: 2500, poor: 4000 },
        cls_p75: { good: 0.1, poor: 0.25 },
        inp_p75: { good: 200, poor: 500 },
        fcp_p75: { good: 2200, poor: 3600 },
        ttfb_p75: { good: 1000, poor: 2200 },
    },
};
const LEAD_DEMOGRAPHICS_BAR_COLOR = 'var(--primary)';
const LEAD_DEMOGRAPHICS_LINE_COLOR = '#64748b';
const LEAD_DEMOGRAPHICS_BAR_COLORS = ['#63bca3', '#79c6b0', '#90d0bd', '#a6daca', '#bde4d8', '#d3eee5'];

const HEAVY_ANALYTICS_TIMEOUT_MS = 180000;

const getMinAnalyticsDate = () => new Date(2025, 6, 11, 9, 18);

const calculatePercentageDifference = (actualTotal, previousTotal) => {
    const actual = Number(actualTotal ?? 0);
    const previous = Number(previousTotal ?? 0);

    if (previous === 0) {
        return actual > 0 ? Infinity : actual < 0 ? -Infinity : 0;
    }

    return ((actual - previous) / Math.abs(previous)) * 100;
};

const formatCurrencyFromCents = (value) => {
    const cents = Number(value ?? 0);
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(cents / 100);
};

const formatPercentage = (value) => `${Number(value || 0).toFixed(2)}%`;

const formatDemographicLabel = (rawLabel) => {
    const label = String(rawLabel || '').trim().toLowerCase();
    if (label === 'male') return 'Uomo';
    if (label === 'female') return 'Donna';
    if (label === 'other') return 'Altro';
    if (label === 'non disponibile') return 'Non disponibile';
    return rawLabel || 'N/D';
};

const DemographicTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) {
        return null;
    }

    const row = payload[0]?.payload || {};
    const percentage = Number(row?.percentage || 0);
    const conversionRate = Number(row?.conversion_rate || 0);
    const count = Number(row?.count || 0);
    const won = Number(row?.won || 0);

    return (
        <div className={styles.demographicTooltip} style={{ backgroundColor: '#FFFFFF' }}>
            <div className={styles.demographicTooltipTitle}>{label || 'N/D'}</div>
            <div className={styles.demographicTooltipRow}>
                <span>% Lead</span>
                <span>{formatPercentage(percentage)}</span>
            </div>
            <div className={styles.demographicTooltipRow}>
                <span>Conv %</span>
                <span>{formatPercentage(conversionRate)}</span>
            </div>
            <div className={styles.demographicTooltipRow}>
                <span>Lead</span>
                <span>{formatNumber(count)}</span>
            </div>
            <div className={styles.demographicTooltipRow}>
                <span>Lead vinti</span>
                <span>{formatNumber(won)}</span>
            </div>
        </div>
    );
};

const formatWebVitalMetricValue = (metricKey, rawValue) => {
    const value = Number(rawValue ?? 0);

    if (metricKey === 'score') {
        return `${Math.round(value)}/100`;
    }

    if (metricKey === 'cls_p75') {
        return value.toFixed(3);
    }

    if (value > 1000) {
        const seconds = value / 1000;
        return `${Number(seconds.toFixed(2))} s`;
    }

    return `${Math.round(value)} ms`;
};

const getWebVitalStatusColor = (status) => {
    switch (status) {
        case 'good':
            return 'var(--sf-green)';
        case 'warning':
            return 'var(--sf-orange)';
        case 'poor':
            return 'var(--sf-red)';
        default:
            return 'rgba(var(--text-color-rgb), 88%)';
    }
};

const getWebVitalStatus = (metricKey, rawValue, deviceType) => {
    const value = Number(rawValue);
    if (!Number.isFinite(value)) {
        return 'neutral';
    }

    if (metricKey === 'score') {
        if (value >= 90) return 'good';
        if (value >= 50) return 'warning';
        return 'poor';
    }

    const thresholds = WEB_VITALS_THRESHOLDS_BY_DEVICE?.[deviceType] || WEB_VITALS_THRESHOLDS_BY_DEVICE.mobile;
    const metricThresholds = thresholds?.[metricKey];
    if (!metricThresholds) {
        return 'neutral';
    }

    if (value <= metricThresholds.good) return 'good';
    if (value <= metricThresholds.poor) return 'warning';
    return 'poor';
};

const formatDateAsRomeDay = (value) => {
    if (!value) {
        return null;
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Rome',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);

    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;

    if (!year || !month || !day) {
        return null;
    }

    return `${year}-${month}-${day}`;
};

const formatEditionShortDate = (value) => {
    if (!value) return '--';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    return new Intl.DateTimeFormat('it-IT', {
        day: '2-digit',
        month: 'short',
        year: '2-digit',
    }).format(date);
};

const toCsvCell = (value) => {
    const normalized = value === null || value === undefined ? '' : String(value);
    return `"${normalized.replace(/"/g, '""')}"`;
};

const buildCsvContent = (headers, rows) => {
    const csvRows = [headers, ...rows];
    return csvRows.map((row) => row.map(toCsvCell).join(',')).join('\n');
};

const downloadCsv = (filename, content) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const MarketingReports = ({ topContent = null }) => {
    // Debounce helper to avoid spamming API calls on rapid MultipleSelect changes
    function useDebounce(value, delay = 500) {
        const [debouncedValue, setDebouncedValue] = useState(value);
        useEffect(() => {
            const handler = setTimeout(() => setDebouncedValue(value), delay);
            return () => clearTimeout(handler);
        }, [value, delay]);
        return debouncedValue;
    }

    const [filters, setFilters] = useState(() => {
        const minDate = getMinAnalyticsDate();
        const thirtyDaysAgo = addDays(new Date(), -30);
        thirtyDaysAgo.setHours(0, 0, 0, 0);

        const fromDateTimestamp = Math.max(thirtyDaysAgo.getTime(), minDate.getTime());
        const initialFromDate = new Date(fromDateTimestamp);

        const initialToDate = (() => {
            const now = new Date();
            now.setHours(23, 59, 59, 999);
            return now;
        })();

        return {
            from_date: initialFromDate,
            to_date: initialToDate,
            courses: [],
        };
    });

    const [courses, setCourses] = useState([])
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState({ general: false, views: false, users: false, sessions: false, leads: false, lead_staying: false, utm: false, referrers: false, lead_demographics: false, product_lead_stats: false, product_lead_stats_12m: false, lead_since_last_edition: false, conversion_by_edition: false, source_lead_stats: false, lead_score_quality: false, lead_score_quality_utm: false, non_application_course_sales: false, non_application_course_demographics: false, non_application_course_sales_utm: false, behavioral_stats: false, web_vitals: false });
    const [utmBreakdownState, setUtmBreakdownState] = useState("source");
    const [directSalesUtmBreakdownState, setDirectSalesUtmBreakdownState] = useState("source");
    const [behavioralUtmBreakdownState, setBehavioralUtmBreakdownState] = useState("source");
    const [expandedProductLeadRows, setExpandedProductLeadRows] = useState({});
    const [expandedSourceLeadRows, setExpandedSourceLeadRows] = useState({});
    const [expandedReferrerRows, setExpandedReferrerRows] = useState({});
    const [expandedUtmRows, setExpandedUtmRows] = useState({});
    const [expandedDirectSalesUtmRows, setExpandedDirectSalesUtmRows] = useState({});
    const [leadScoreSnapshotDialogRow, setLeadScoreSnapshotDialogRow] = useState(null);
    const [leadScoreSnapshotSourceBreakdownByRow, setLeadScoreSnapshotSourceBreakdownByRow] = useState({});
    const [leadScoreSnapshotSourceLoadingByRow, setLeadScoreSnapshotSourceLoadingByRow] = useState({});
    const [leadScoreSnapshotSourceErrorByRow, setLeadScoreSnapshotSourceErrorByRow] = useState({});
    const [productLeadSearch, setProductLeadSearch] = useState('');
    const [sourceLeadSearch, setSourceLeadSearch] = useState('');
    const [sourceLeadBreakdown, setSourceLeadBreakdown] = useState({ id: 'source', name: 'Source', value: 'source' });
    const [leadScoreQualityBreakdown, setLeadScoreQualityBreakdown] = useState({ id: 'source', name: 'Source', value: 'source' });
    const [leadScoreQualityUtm, setLeadScoreQualityUtm] = useState({ rows: [], summary: {} });
    const [leadDemographicsSearch, setLeadDemographicsSearch] = useState('');
    const [directDemographicsSearch, setDirectDemographicsSearch] = useState('');
    const [behavioralCourseSearch, setBehavioralCourseSearch] = useState('');
    const [productLeadType, setProductLeadType] = useState(null);
    const [productLeadCategory, setProductLeadCategory] = useState(null);
    const [leadDemographicsType, setLeadDemographicsType] = useState(null);
    const [leadDemographicsCategory, setLeadDemographicsCategory] = useState(null);
    const [directDemographicsType, setDirectDemographicsType] = useState(null);
    const [directDemographicsCategory, setDirectDemographicsCategory] = useState(null);
    const [behavioralCourseType, setBehavioralCourseType] = useState(null);
    const [behavioralCourseCategory, setBehavioralCourseCategory] = useState(null);
    const [selectedWebVitalsDevice, setSelectedWebVitalsDevice] = useState(WEB_VITALS_DEVICE_OPTIONS[0]);
    const [leadTargetEnrollments, setLeadTargetEnrollments] = useState(30);
    const [productLeadStatsLast12Months, setProductLeadStatsLast12Months] = useState([]);
    const [hasStartedEditionByCourseId, setHasStartedEditionByCourseId] = useState({});
    const [lastPastEditionStartByCourseId, setLastPastEditionStartByCourseId] = useState({});
    const [leadSinceLastEditionByCourseId, setLeadSinceLastEditionByCourseId] = useState({});
    const [courseEditionConversionTrendRows, setCourseEditionConversionTrendRows] = useState([]);
    const [expandedCourseEditionRows, setExpandedCourseEditionRows] = useState({});
    const [expandedDirectPackRows, setExpandedDirectPackRows] = useState({});
    const [directPackBreakdownByRow, setDirectPackBreakdownByRow] = useState({});
    const [directPackBreakdownLoadingByRow, setDirectPackBreakdownLoadingByRow] = useState({});
    const [directPackBreakdownErrorByRow, setDirectPackBreakdownErrorByRow] = useState({});
    const behavioralBreakdownKey = useMemo(() => {
        const rawValue = behavioralUtmBreakdownState && typeof behavioralUtmBreakdownState === 'object'
            ? behavioralUtmBreakdownState?.value
            : behavioralUtmBreakdownState;
        const normalized = String(rawValue || 'source').trim().toLowerCase();
        return ['source', 'medium', 'campaign'].includes(normalized) ? normalized : 'source';
    }, [behavioralUtmBreakdownState]);
    const selectedCourses = useMemo(() => {
        const selectedIds = new Set((filters?.courses || []).map((id) => String(id)));
        return (courses || []).filter((course) => selectedIds.has(String(course?.id)));
    }, [courses, filters?.courses]);

    const coursesById = useMemo(() => {
        return (courses || []).reduce((acc, course) => {
            if (course?.id !== null && course?.id !== undefined) {
                acc[String(course.id)] = course;
            }
            return acc;
        }, {});
    }, [courses]);

    const selectedOnlyNonApplicationCourses = useMemo(() => {
        if (!filters?.courses?.length || !selectedCourses.length) {
            return false;
        }

        const toBoolean = (value) => {
            if (typeof value === 'boolean') return value;
            if (typeof value === 'number') return value === 1;
            if (typeof value === 'string') {
                const normalized = value.trim().toLowerCase();
                return normalized === 'true' || normalized === '1';
            }
            return Boolean(value);
        };

        return selectedCourses.every((course) => {
            const requiresApplication = course?.requires_application ?? course?.requiresApplication;
            return !toBoolean(requiresApplication);
        });
    }, [filters?.courses?.length, selectedCourses]);

    const showLeadAnalyticsCards = !selectedOnlyNonApplicationCourses;
    const debouncedCourses = useDebounce(filters.courses, 500);
    const analyticsAbortRef = useRef(new Set());

    // Cancella richieste analytics in corso solo quando cambiano i filtri base (date/prodotti).
    useEffect(() => {
        analyticsAbortRef.current.forEach((controller) => controller.abort());
        analyticsAbortRef.current.clear();
    }, [filters.from_date, filters.to_date, filters.courses]);

    useEffect(() => {
        const getCourses = async () => {
            try {
                const coursesResponse = await api.get("/admin/courses");

                const normalizedCourses = Array.isArray(coursesResponse) ? coursesResponse : [];
                setCourses(normalizedCourses.sort((a, b) => a.name.localeCompare(b.name)));
            }
            catch (e) {
                console.error(e)
            }
        }

        getCourses()
    }, [])

    const normalizeAnalyticsResponse = useCallback((path, response) => {
        if (path === "product_lead_stats") {
            return {
                actual: {
                    product_lead_stats: Array.isArray(response) ? response : [],
                },
                previous: {},
            };
        }

        if (path === "source_lead_stats") {
            const sourceLeadStatsByBreakdown = {
                source: [],
                medium: [],
                campaign: [],
            };

            if (Array.isArray(response)) {
                sourceLeadStatsByBreakdown.source = response;
            }
            else if (response && typeof response === 'object') {
                sourceLeadStatsByBreakdown.source = Array.isArray(response?.source) ? response.source : [];
                sourceLeadStatsByBreakdown.medium = Array.isArray(response?.medium) ? response.medium : [];
                sourceLeadStatsByBreakdown.campaign = Array.isArray(response?.campaign) ? response.campaign : [];
            }

            return {
                actual: {
                    source_lead_stats: sourceLeadStatsByBreakdown.source,
                    source_lead_stats_by_breakdown: sourceLeadStatsByBreakdown,
                },
                previous: {},
            };
        }

        if (path === "lead_score_quality") {
            return {
                actual: {
                    lead_score_quality: response && typeof response === 'object'
                        ? response
                        : { rows: [], summary: {} },
                },
                previous: {},
            };
        }

        if (path === "non_application_course_sales") {
            return {
                actual: {
                    non_application_course_sales: Array.isArray(response) ? response : [],
                },
                previous: {},
            };
        }

        if (path === "non_application_course_demographics") {
            return {
                actual: {
                    non_application_course_demographics: Array.isArray(response) ? response : [],
                },
                previous: {},
            };
        }

        if (path === "non_application_course_sales_utm") {
            return {
                actual: {
                    non_application_course_sales_utm: response?.utm || {},
                },
                previous: {},
            };
        }

        if (path === "behavioral_stats") {
            const actualPayload = response?.actual || response || {};
            const previousPayload = response?.previous || {};
            return {
                actual: {
                    page_views: Number(actualPayload?.page_view_total || 0),
                    page_views_anonymous: Number(actualPayload?.page_view_anonymous || 0),
                    page_views_registered: Number(actualPayload?.page_view_authenticated || 0),
                    unique_users: Number(actualPayload?.unique_users_total || 0),
                    unique_anonymous_users: Number(actualPayload?.unique_users_anonymous || 0),
                    unique_registered_users: Number(actualPayload?.unique_users_authenticated || 0),
                    sessions: Number(actualPayload?.unique_sessions || 0),
                    session_duration: Number(actualPayload?.avg_time_on_course_seconds || 0),
                    behavioral_stats: actualPayload,
                },
                previous: {
                    page_views: Number(previousPayload?.page_view_total || 0),
                    page_views_anonymous: Number(previousPayload?.page_view_anonymous || 0),
                    page_views_registered: Number(previousPayload?.page_view_authenticated || 0),
                    unique_users: Number(previousPayload?.unique_users_total || 0),
                    unique_anonymous_users: Number(previousPayload?.unique_users_anonymous || 0),
                    unique_registered_users: Number(previousPayload?.unique_users_authenticated || 0),
                    sessions: Number(previousPayload?.unique_sessions || 0),
                    session_duration: Number(previousPayload?.avg_time_on_course_seconds || 0),
                    behavioral_stats: previousPayload,
                },
            };
        }

        const safeResponse = (response && typeof response === 'object') ? response : {};
        const safeActual = (safeResponse.actual && typeof safeResponse.actual === 'object')
            ? safeResponse.actual
            : safeResponse;
        const safePrevious = (safeResponse.previous && typeof safeResponse.previous === 'object')
            ? safeResponse.previous
            : {};

        return {
            actual: safeActual,
            previous: safePrevious,
        };
    }, []);

    const getParametricAnalytics = useCallback(async (path, extraParams = {}) => {
        setLoading((prev) => ({ ...prev, [path]: true }));
        const controller = new AbortController();
        analyticsAbortRef.current.add(controller);
        try {

            const params = new URLSearchParams();

            if (filters.from_date) {
                const fromDate = filters.from_date instanceof Date ? filters.from_date : new Date(filters.from_date);
                const fromDateRome = formatDateAsRomeDay(fromDate);
                if (fromDateRome) {
                    params.append("from_date", fromDateRome);
                }
            }
            if (filters.to_date) {
                const toDate = filters.to_date instanceof Date ? filters.to_date : new Date(filters.to_date);
                const toDateRome = formatDateAsRomeDay(toDate);
                if (toDateRome) {
                    params.append("to_date", toDateRome);
                }
            }
            if (debouncedCourses.length > 0) {
                params.append("courses", debouncedCourses.join(","));
            }
            Object.entries(extraParams).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, value);
                }
            });
            const response = await api.get(`/admin/analytics/${path}?${params.toString()}`, {
                signal: controller.signal,
                timeout: HEAVY_ANALYTICS_TIMEOUT_MS
            });

            const normalizedResponse = normalizeAnalyticsResponse(path, response);

            setAnalytics((prev) => ({
                ...prev,
                actual: {
                    ...prev?.actual,
                    ...(normalizedResponse?.actual || {})
                },
                previous: {
                    ...prev?.previous,
                    ...(normalizedResponse?.previous || {})
                }
            }))

        }
        catch (e) {
            // Ignore canceled requests, log others
            if (e?.code !== 'ERR_CANCELED') {
                console.error(e)
            }
        }
        finally {
            analyticsAbortRef.current.delete(controller);
            setLoading((prev) => ({ ...prev, [path]: false }));
        }
    }, [filters.from_date, filters.to_date, debouncedCourses, normalizeAnalyticsResponse]);

    const getParametricAnalyticsDateOnly = useCallback(async (path, extraParams = {}) => {
        setLoading((prev) => ({ ...prev, [path]: true }));
        const controller = new AbortController();
        analyticsAbortRef.current.add(controller);
        try {
            const params = new URLSearchParams();

            if (filters.from_date) {
                const fromDate = filters.from_date instanceof Date ? filters.from_date : new Date(filters.from_date);
                const fromDateRome = formatDateAsRomeDay(fromDate);
                if (fromDateRome) {
                    params.append("from_date", fromDateRome);
                }
            }
            if (filters.to_date) {
                const toDate = filters.to_date instanceof Date ? filters.to_date : new Date(filters.to_date);
                const toDateRome = formatDateAsRomeDay(toDate);
                if (toDateRome) {
                    params.append("to_date", toDateRome);
                }
            }

            if (debouncedCourses.length > 0) {
                params.append("courses", debouncedCourses.join(","));
            }

            Object.entries(extraParams).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, value);
                }
            });

            const response = await api.get(`/admin/analytics/${path}?${params.toString()}`, {
                signal: controller.signal,
                timeout: 60000
            });

            const normalizedResponse = normalizeAnalyticsResponse(path, response);

            setAnalytics((prev) => ({
                ...prev,
                actual: {
                    ...prev?.actual,
                    ...(normalizedResponse?.actual || {})
                },
                previous: {
                    ...prev?.previous,
                    ...(normalizedResponse?.previous || {})
                }
            }))
        }
        catch (e) {
            if (e?.code !== 'ERR_CANCELED') {
                console.error(e)
            }
        }
        finally {
            analyticsAbortRef.current.delete(controller);
            setLoading((prev) => ({ ...prev, [path]: false }));
        }
    }, [filters.from_date, filters.to_date, debouncedCourses, normalizeAnalyticsResponse]);

    const getLeadScoreQualityByBreakdown = useCallback(async () => {
        setLoading((prev) => ({ ...prev, lead_score_quality_utm: true }));
        const controller = new AbortController();
        analyticsAbortRef.current.add(controller);

        try {
            const params = new URLSearchParams();

            if (filters.from_date) {
                const fromDate = filters.from_date instanceof Date ? filters.from_date : new Date(filters.from_date);
                const fromDateRome = formatDateAsRomeDay(fromDate);
                if (fromDateRome) {
                    params.append('from_date', fromDateRome);
                }
            }

            if (filters.to_date) {
                const toDate = filters.to_date instanceof Date ? filters.to_date : new Date(filters.to_date);
                const toDateRome = formatDateAsRomeDay(toDate);
                if (toDateRome) {
                    params.append('to_date', toDateRome);
                }
            }

            if (debouncedCourses.length > 0) {
                params.append('courses', debouncedCourses.join(','));
            }

            params.append('breakdown', leadScoreQualityBreakdown?.value || 'source');

            const response = await api.get(`/admin/analytics/lead_score_quality?${params.toString()}`, {
                signal: controller.signal,
                timeout: 60000,
            });

            if (response && typeof response === 'object') {
                setLeadScoreQualityUtm({
                    rows: Array.isArray(response?.rows) ? response.rows : [],
                    summary: response?.summary && typeof response.summary === 'object' ? response.summary : {},
                });
            }
            else {
                setLeadScoreQualityUtm({ rows: [], summary: {} });
            }
        }
        catch (e) {
            if (e?.code !== 'ERR_CANCELED') {
                console.error(e);
            }
        }
        finally {
            analyticsAbortRef.current.delete(controller);
            setLoading((prev) => ({ ...prev, lead_score_quality_utm: false }));
        }
    }, [filters.from_date, filters.to_date, debouncedCourses, leadScoreQualityBreakdown]);

    const getWebVitalsReport = useCallback(async () => {
        setLoading((prev) => ({ ...prev, web_vitals: true }));
        const controller = new AbortController();
        analyticsAbortRef.current.add(controller);

        try {
            const params = new URLSearchParams();

            if (filters.from_date) {
                const fromDate = filters.from_date instanceof Date ? filters.from_date : new Date(filters.from_date);
                const fromDateRome = formatDateAsRomeDay(fromDate);
                if (fromDateRome) {
                    params.append('from_date', fromDateRome);
                }
            }

            if (filters.to_date) {
                const toDate = filters.to_date instanceof Date ? filters.to_date : new Date(filters.to_date);
                const toDateRome = formatDateAsRomeDay(toDate);
                if (toDateRome) {
                    params.append('to_date', toDateRome);
                }
            }

            if (debouncedCourses.length > 0) {
                params.append('courses', debouncedCourses.join(','));
            }

            const response = await api.get(`/admin/analytics/web_vitals?${params.toString()}`, {
                signal: controller.signal,
                timeout: 60000,
            });

            setAnalytics((prev) => ({
                ...prev,
                actual: {
                    ...prev?.actual,
                    ...response?.actual,
                },
                previous: {
                    ...prev?.previous,
                    ...response?.previous,
                }
            }));
        }
        catch (e) {
            if (e?.code !== 'ERR_CANCELED') {
                console.error(e)
            }
        }
        finally {
            analyticsAbortRef.current.delete(controller);
            setLoading((prev) => ({ ...prev, web_vitals: false }));
        }
    }, [filters.from_date, filters.to_date, debouncedCourses]);

    const getProductLeadStatsLast12Months = useCallback(async () => {
        setLoading((prev) => ({ ...prev, product_lead_stats_12m: true }));
        const controller = new AbortController();
        analyticsAbortRef.current.add(controller);

        try {
            const params = new URLSearchParams();
            const minDate = getMinAnalyticsDate();

            const toDate = new Date();
            toDate.setHours(23, 59, 59, 999);

            const fromDate = new Date(toDate);
            fromDate.setFullYear(fromDate.getFullYear() - 1);
            fromDate.setHours(0, 0, 0, 0);

            const effectiveFromDate = fromDate.getTime() < minDate.getTime() ? minDate : fromDate;

            const fromDateRome = formatDateAsRomeDay(effectiveFromDate);
            const toDateRome = formatDateAsRomeDay(toDate);

            if (fromDateRome) {
                params.append('from_date', fromDateRome);
            }
            if (toDateRome) {
                params.append('to_date', toDateRome);
            }

            if (debouncedCourses.length > 0) {
                params.append('courses', debouncedCourses.join(','));
            }

            const response = await api.get(`/admin/analytics/product_lead_stats?${params.toString()}`, {
                signal: controller.signal,
                timeout: HEAVY_ANALYTICS_TIMEOUT_MS,
            });

            setProductLeadStatsLast12Months(Array.isArray(response) ? response : []);
        }
        catch (e) {
            if (e?.code !== 'ERR_CANCELED') {
                console.error(e)
            }
        }
        finally {
            analyticsAbortRef.current.delete(controller);
            setLoading((prev) => ({ ...prev, product_lead_stats_12m: false }));
        }
    }, [debouncedCourses]);

    const editionTrendCourseIds = useMemo(() => {
        const source = courses || [];
        return source
            .filter((course) => {
                if (course?.id === null || course?.id === undefined) {
                    return false;
                }

                if (String(course?.type) === String(CourseType.Webinar)) {
                    return false;
                }

                return true;
            })
            .map((course) => String(course.id));
    }, [courses]);

    const sourceLeadBreakdownOptions = useMemo(() => ([
        { id: 'source', name: 'Source', value: 'source' },
        { id: 'medium', name: 'Medium', value: 'medium' },
        { id: 'campaign', name: 'Campaign', value: 'campaign' },
    ]), []);

    const behavioralUtmBreakdownOptions = useMemo(() => ([
        { id: 'source', name: 'Source', value: 'source' },
        { id: 'medium', name: 'Medium', value: 'medium' },
        { id: 'campaign', name: 'Campaign', value: 'campaign' },
    ]), []);


    const options = useMemo(
        () => ({
            css: [
                'https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css',
                'https://cdn.jsdelivr.net/npm/@easepick/range-plugin@1.2.1/dist/index.css',
                '/css/easepick-range.css',
            ],
            zIndex: 1000,
            plugins: [
                RangePlugin,
                PresetPlugin,
                AmpPlugin,
                LockPlugin
            ],
            lang: 'it-IT',
            format: "DD MMM YYYY",
            LockPlugin: {
                minDate: getMinAnalyticsDate()
            },
            AmpPlugin: {
                darkMode: false,
                dropdown: {
                    months: true,
                    years: true,
                    minYear: 2020
                }
            },
            PresetPlugin: {
                customPreset: {
                    'Oggi': [
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    '7 giorni': [
                        (() => {
                            const date = new Date();
                            date.setDate(date.getDate() - 6); // 6 giorni fa
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    'Settimana': [
                        (() => {
                            const now = new Date();
                            const day = now.getDay(); // 0 (domenica) - 6 (sabato)
                            const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Lunedì
                            const monday = new Date(now.setDate(diff));
                            return new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    '30 giorni': [
                        (() => {
                            const date = new Date();
                            date.setDate(date.getDate() - 29); // 29 giorni fa
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    'Mese': [
                        (() => {
                            const now = new Date();
                            return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0); // Primo giorno del mese
                        })(),
                        (() => {
                            const now = new Date();
                            return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999); // Ultimo giorno del mese
                        })(),
                    ],
                    'Anno': [
                        new Date(new Date().getFullYear(), 0, 1, 0, 0, 0, 0),
                        new Date(new Date().getFullYear(), 11, 31, 23, 59, 59, 999),
                    ]
                }
            },
            setup(picker) {
                picker.on("select", (e) => {
                    const { start, end } = e.detail;
                    const startDate = new Date(start.getTime());
                    startDate.setHours(0, 0, 0, 0);
                    const endDate = new Date(end.getTime());
                    endDate.setHours(23, 59, 59, 999);

                    setFilters((prev) => ({
                        ...prev,
                        from_date: startDate,
                        to_date: endDate,
                    }))
                });
            },
        }),
        []
    );

    useEffect(() => {
        let cancelled = false;

        const loadAnalytics = async () => {
            await getWebVitalsReport();

            const parallelRequests = showLeadAnalyticsCards
                ? Promise.all([
                    getParametricAnalytics("leads"),
                    getParametricAnalytics("lead_staying"),
                    getParametricAnalytics("lead_demographics"),
                ])
                : Promise.resolve();

            const sequentialRequests = [
                "behavioral_stats",
                "utm",
                "referrers",
            ];

            for (const path of sequentialRequests) {
                if (cancelled) {
                    return;
                }
                await getParametricAnalytics(path);
            }

            await parallelRequests;
        };

        loadAnalytics();

        return () => {
            cancelled = true;
        };
    }, [getParametricAnalytics, getWebVitalsReport, showLeadAnalyticsCards]);

    useEffect(() => {
        if (!showLeadAnalyticsCards) {
            return;
        }

        getParametricAnalyticsDateOnly("product_lead_stats");
        getParametricAnalyticsDateOnly("source_lead_stats");
        getParametricAnalyticsDateOnly("lead_score_quality");
    }, [getParametricAnalyticsDateOnly, showLeadAnalyticsCards]);

    useEffect(() => {
        if (!showLeadAnalyticsCards) {
            setLeadScoreQualityUtm({ rows: [], summary: {} });
            return;
        }

        getLeadScoreQualityByBreakdown();
    }, [showLeadAnalyticsCards, getLeadScoreQualityByBreakdown]);

    useEffect(() => {
        if (!showLeadAnalyticsCards) {
            return;
        }

        getProductLeadStatsLast12Months();
    }, [getProductLeadStatsLast12Months, showLeadAnalyticsCards]);

    useEffect(() => {
        if (!showLeadAnalyticsCards) {
            setHasStartedEditionByCourseId({});
            setLastPastEditionStartByCourseId({});
            return;
        }

        const courseIds = Array.from(new Set(
            (Array.isArray(productLeadStatsLast12Months) ? productLeadStatsLast12Months : [])
                .map((item, index) => String(item?.course?.id ?? `forecast-course-${index}`))
                .filter((id) => id && id !== 'undefined' && id !== 'null' && !id.startsWith('forecast-course-'))
        ));

        if (courseIds.length === 0) {
            setHasStartedEditionByCourseId({});
            setLastPastEditionStartByCourseId({});
            return;
        }

        let cancelled = false;

        const parseDateIfValid = (value) => {
            if (!value) return null;
            const date = value instanceof Date ? value : new Date(value);
            return Number.isNaN(date.getTime()) ? null : date;
        };

        const getFirstLessonDateFromEdition = (edition) => {
            return parseDateIfValid(edition?.first_lesson_at || edition?.start_date || edition?.starts_at || edition?.date);
        };

        const loadStartedEditions = async () => {
            const now = new Date();

            let editionsByCourseId = {};
            try {
                const bulkResponse = await api.post('/admin/courses/editions/bulk', {
                    course_ids: courseIds,
                    public_only: false,
                });
                editionsByCourseId = bulkResponse?.editions_by_course_id && typeof bulkResponse.editions_by_course_id === 'object'
                    ? bulkResponse.editions_by_course_id
                    : {};
            }
            catch (e) {
                console.error(e);
            }

            const results = courseIds.map((courseId) => {
                const editions = Array.isArray(editionsByCourseId?.[String(courseId)]) ? editionsByCourseId[String(courseId)] : [];
                const firstLessonDates = editions
                    .map((edition) => getFirstLessonDateFromEdition(edition))
                    .filter(Boolean)
                    .sort((a, b) => a.getTime() - b.getTime());

                const pastFirstLessonDates = firstLessonDates.filter((date) => date.getTime() < now.getTime());
                const hasStartedEdition = pastFirstLessonDates.length > 0;
                const lastPastEditionStart = hasStartedEdition ? pastFirstLessonDates[pastFirstLessonDates.length - 1] : null;

                return [courseId, hasStartedEdition, lastPastEditionStart];
            });

            if (cancelled) {
                return;
            }

            const startedMap = results.reduce((acc, [courseId, value]) => {
                acc[String(courseId)] = Boolean(value);
                return acc;
            }, {});

            const lastStartMap = results.reduce((acc, [courseId, _value, lastPastEditionStart]) => {
                acc[String(courseId)] = lastPastEditionStart || null;
                return acc;
            }, {});

            setHasStartedEditionByCourseId(startedMap);
            setLastPastEditionStartByCourseId(lastStartMap);
        };

        loadStartedEditions();

        return () => {
            cancelled = true;
        };
    }, [showLeadAnalyticsCards, productLeadStatsLast12Months]);

    useEffect(() => {
        if (!showLeadAnalyticsCards) {
            setLeadSinceLastEditionByCourseId({});
            return;
        }

        const courseIds = Array.from(new Set(
            (Array.isArray(productLeadStatsLast12Months) ? productLeadStatsLast12Months : [])
                .map((item, index) => String(item?.course?.id ?? `forecast-course-${index}`))
                .filter((id) => id && id !== 'undefined' && id !== 'null' && !id.startsWith('forecast-course-'))
        ));

        if (courseIds.length === 0) {
            setLeadSinceLastEditionByCourseId({});
            return;
        }

        let cancelled = false;
        const controller = new AbortController();
        analyticsAbortRef.current.add(controller);

        const loadLeadSinceLastEdition = async () => {
            setLoading((prev) => ({ ...prev, lead_since_last_edition: true }));
            try {
                const nowRomeDay = formatDateAsRomeDay(new Date());
                const minAnalyticsRomeDay = formatDateAsRomeDay(getMinAnalyticsDate());

                const bulkRequests = courseIds.map((courseId) => {
                    const lastPastStart = lastPastEditionStartByCourseId[String(courseId)] || null;
                    const fromDateRome = formatDateAsRomeDay(lastPastStart);
                    const effectiveFromDate = fromDateRome || minAnalyticsRomeDay;

                    return {
                        course_id: courseId,
                        from_date: effectiveFromDate,
                        to_date: nowRomeDay,
                    };
                }).filter((row) => row?.course_id && row?.from_date && row?.to_date);

                let leadTotalByCourseId = {};
                try {
                    const bulkResponse = await api.post('/admin/analytics/product_lead_stats/bulk', {
                        requests: bulkRequests,
                    }, {
                        signal: controller.signal,
                        timeout: 60000,
                    });
                    leadTotalByCourseId = bulkResponse?.lead_total_by_course_id && typeof bulkResponse.lead_total_by_course_id === 'object'
                        ? bulkResponse.lead_total_by_course_id
                        : {};
                }
                catch (e) {
                    if (e?.code !== 'ERR_CANCELED') {
                        console.error(e);
                    }
                }

                if (cancelled) {
                    return;
                }

                const map = courseIds.reduce((acc, courseId) => {
                    acc[String(courseId)] = Number(leadTotalByCourseId?.[String(courseId)] || 0);
                    return acc;
                }, {});

                setLeadSinceLastEditionByCourseId(map);
            }
            finally {
                analyticsAbortRef.current.delete(controller);
                if (!cancelled) {
                    setLoading((prev) => ({ ...prev, lead_since_last_edition: false }));
                }
            }
        };

        loadLeadSinceLastEdition();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [showLeadAnalyticsCards, productLeadStatsLast12Months, lastPastEditionStartByCourseId]);

    useEffect(() => {
        if (!showLeadAnalyticsCards || !editionTrendCourseIds.length) {
            setCourseEditionConversionTrendRows([]);
            return;
        }

        let cancelled = false;
        const controller = new AbortController();
        analyticsAbortRef.current.add(controller);

        const loadEditionTrend = async () => {
            setLoading((prev) => ({ ...prev, conversion_by_edition: true }));
            try {
                const response = await api.post('/admin/analytics/conversion_by_edition/bulk', {
                    course_ids: editionTrendCourseIds,
                }, {
                    signal: controller.signal,
                    timeout: 300000,
                });

                const rowsByCourse = Array.isArray(response?.rows) ? response.rows : [];

                if (cancelled) {
                    return;
                }

                setCourseEditionConversionTrendRows(rowsByCourse);
            }
            catch (e) {
                if (e?.code !== 'ERR_CANCELED') {
                    console.error(e);
                }
                if (!cancelled) {
                    setCourseEditionConversionTrendRows([]);
                }
            }
            finally {
                analyticsAbortRef.current.delete(controller);
                if (!cancelled) {
                    setLoading((prev) => ({ ...prev, conversion_by_edition: false }));
                }
            }
        };

        loadEditionTrend();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [showLeadAnalyticsCards, editionTrendCourseIds]);

    useEffect(() => {
        getParametricAnalyticsDateOnly("non_application_course_sales");
    }, [getParametricAnalyticsDateOnly]);

    useEffect(() => {
        getParametricAnalyticsDateOnly("non_application_course_demographics");
    }, [getParametricAnalyticsDateOnly]);

    useEffect(() => {
        getParametricAnalyticsDateOnly("non_application_course_sales_utm");
    }, [getParametricAnalyticsDateOnly]);


    // Cleanup: abort any in-flight request on unmount
    useEffect(() => {
        const abortRef = analyticsAbortRef;
        return () => {
            abortRef.current.forEach((controller) => controller.abort());
            abortRef.current.clear();
        };
    }, []);

    const metricPercentageDifference = useMemo(() => {
        if (!analytics?.actual || !analytics?.previous) {
            return {};
        }

        const keys = [
            'page_views',
            'unique_users',
            'sessions',
            'session_duration',
            'view_lead',
            'close_lead',
            'lead',
            'lead_pending',
            'lead_won',
            'lead_ghost',
            'lead_message_sent',
            'lead_call_scheduled',
            'lead_approved',
            'lead_rejected',
            'lead_next_edition'
        ];

        return keys.reduce((acc, key) => {
            acc[key] = calculatePercentageDifference(analytics.actual[key], analytics.previous[key]);
            return acc;
        }, {});
    }, [analytics?.actual, analytics?.previous]);

    const pageViewsPercentageDifference = metricPercentageDifference.page_views ?? null;
    const uniqueUsersPercentageDifference = metricPercentageDifference.unique_users ?? null;
    const sessionsPercentageDifference = metricPercentageDifference.sessions ?? null;
    const sessionsDurationPercentageDifference = metricPercentageDifference.session_duration ?? null;
    const leadGeneratedPercentageDifference = metricPercentageDifference.lead ?? null;

    const selectedWebVitalsDeviceKey = selectedWebVitalsDevice?.value || 'desktop';

    const selectedWebVitalsActual = useMemo(() => {
        return analytics?.actual?.web_vitals?.[selectedWebVitalsDeviceKey] || null;
    }, [analytics?.actual?.web_vitals, selectedWebVitalsDeviceKey]);

    const selectedWebVitalsPrevious = useMemo(() => {
        return analytics?.previous?.web_vitals?.[selectedWebVitalsDeviceKey] || null;
    }, [analytics?.previous?.web_vitals, selectedWebVitalsDeviceKey]);

    const webVitalsMetricBlocks = useMemo(() => {
        const metrics = [
            { key: 'score', title: 'SCORE', isLossOnIncrease: false },
            { key: 'fcp_p75', title: 'FCP', isLossOnIncrease: true },
            { key: 'lcp_p75', title: 'LCP', isLossOnIncrease: true },
            { key: 'cls_p75', title: 'CLS', isLossOnIncrease: true },
            { key: 'ttfb_p75', title: 'TTFB', isLossOnIncrease: true },
            { key: 'inp_p75', title: 'INP', isLossOnIncrease: true },
        ];

        return metrics.map((metric) => {
            const actualValue = Number(selectedWebVitalsActual?.[metric.key]);
            const previousValue = Number(selectedWebVitalsPrevious?.[metric.key]);

            const hasActualValue = Number.isFinite(actualValue);
            const hasPreviousValue = Number.isFinite(previousValue);

            const percentageValue = hasActualValue && hasPreviousValue
                ? calculatePercentageDifference(actualValue, previousValue)
                : null;

            const status = getWebVitalStatus(metric.key, actualValue, selectedWebVitalsDeviceKey);
            const statusColor = getWebVitalStatusColor(status);

            return {
                ...metric,
                actualValue: hasActualValue ? <span style={{ color: statusColor }}>{formatWebVitalMetricValue(metric.key, actualValue)}</span> : null,
                previousValue: hasPreviousValue ? formatWebVitalMetricValue(metric.key, previousValue) : null,
                percentageValue,
                isLoss: metric.isLossOnIncrease
                    ? (Number.isFinite(percentageValue) ? percentageValue > 0 : false)
                    : (Number.isFinite(percentageValue) ? percentageValue < 0 : false),
                titleColor: statusColor,
            };
        });
    }, [selectedWebVitalsActual, selectedWebVitalsDeviceKey, selectedWebVitalsPrevious]);

    const toggleProductLeadRow = useCallback((rowId) => {
        setExpandedProductLeadRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
    }, []);

    const toggleSourceLeadRow = useCallback((rowId) => {
        setExpandedSourceLeadRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
    }, []);

    const toggleReferrerRow = useCallback((rowId) => {
        setExpandedReferrerRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
    }, []);

    const toggleUtmRow = useCallback((rowId) => {
        setExpandedUtmRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
    }, []);

    const toggleDirectSalesUtmRow = useCallback((rowId) => {
        setExpandedDirectSalesUtmRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
    }, []);

    const toggleCourseEditionRow = useCallback((rowId) => {
        setExpandedCourseEditionRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
    }, []);

    const onLeadScoreSnapshotRowClick = useCallback(async (row) => {
        const rowId = row?.id;
        const courseId = row?.course_id;

        if (!rowId || !courseId) {
            return;
        }

        setLeadScoreSnapshotDialogRow(row);
        if (leadScoreSnapshotSourceBreakdownByRow[rowId] || leadScoreSnapshotSourceLoadingByRow[rowId]) {
            return;
        }

        const controller = new AbortController();
        analyticsAbortRef.current.add(controller);
        setLeadScoreSnapshotSourceLoadingByRow((prev) => ({ ...prev, [rowId]: true }));
        setLeadScoreSnapshotSourceErrorByRow((prev) => ({ ...prev, [rowId]: null }));

        try {
            const params = new URLSearchParams();

            if (filters.from_date) {
                const fromDate = filters.from_date instanceof Date ? filters.from_date : new Date(filters.from_date);
                const fromDateRome = formatDateAsRomeDay(fromDate);
                if (fromDateRome) {
                    params.append('from_date', fromDateRome);
                }
            }

            if (filters.to_date) {
                const toDate = filters.to_date instanceof Date ? filters.to_date : new Date(filters.to_date);
                const toDateRome = formatDateAsRomeDay(toDate);
                if (toDateRome) {
                    params.append('to_date', toDateRome);
                }
            }

            params.append('courses', String(courseId));
            params.append('breakdown', 'source');

            const response = await api.get(`/admin/analytics/lead_score_quality?${params.toString()}`, {
                signal: controller.signal,
                timeout: 60000,
            });

            const rows = Array.isArray(response?.rows) ? response.rows : [];
            setLeadScoreSnapshotSourceBreakdownByRow((prev) => ({ ...prev, [rowId]: rows }));
        } catch (error) {
            if (error?.code !== 'ERR_CANCELED') {
                console.error(error);
                setLeadScoreSnapshotSourceErrorByRow((prev) => ({ ...prev, [rowId]: 'Impossibile caricare il breakdown UTM source.' }));
            }
        } finally {
            analyticsAbortRef.current.delete(controller);
            setLeadScoreSnapshotSourceLoadingByRow((prev) => ({ ...prev, [rowId]: false }));
        }
    }, [
        leadScoreSnapshotSourceBreakdownByRow,
        leadScoreSnapshotSourceLoadingByRow,
        filters.from_date,
        filters.to_date,
    ]);

    const productLeadStatsTableData = useMemo(() => {
        const rows = Array.isArray(analytics?.actual?.product_lead_stats) ? analytics.actual.product_lead_stats : [];

        const buildSourceBreakdown = (title, rowId, total, sourceMap) => {
            const entries = Object.entries(sourceMap || {});
            const isExpanded = Boolean(expandedProductLeadRows[rowId]);

            const handleToggle = (ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                toggleProductLeadRow(rowId);
            };

            return {
                value: total || 0,
                type: 'number',
                component: (
                    <details open={isExpanded} onClick={(ev) => ev.stopPropagation()} className={styles.productLeadDetails}>
                        <summary onClick={handleToggle} className={styles.productLeadSummary}>
                            <div className={styles.productLeadSummaryMain}>{formatNumber(total || 0)}</div>
                        </summary>
                        <div className={styles.productLeadBreakdown}>
                            {entries.length === 0 && (
                                <div className={styles.productLeadEmpty}>
                                    Nessun dato source
                                </div>
                            )}
                            {entries.map(([source, sourceStats]) => (
                                <div key={`${title}-${rowId}-${source}`} className={styles.productLeadBreakdownRow}>
                                    <span className={styles.productLeadSourceName}>{source}</span>
                                    <span className={styles.productLeadSourceStats}>{formatNumber(sourceStats?.count || 0)} • {(sourceStats?.percentage || 0).toFixed(2)}%</span>
                                </div>
                            ))}
                        </div>
                    </details>
                )
            };
        };

        return rows.map((item, index) => {
            const rowId = item?.course?.id ?? `course-${index}`;

            return {
                id: rowId,
                course_name: {
                    value: item?.course?.name || 'Corso sconosciuto',
                    type: 'string',
                    component: <div>{item?.course?.name || 'Corso sconosciuto'}</div>,
                },
                lead_total: buildSourceBreakdown('lead', rowId, item?.lead?.total || 0, item?.lead?.source),
                lead_won_total: buildSourceBreakdown('lead_won', rowId, item?.lead_won?.total || 0, item?.lead_won?.source),
                lead_won_rate: {
                    value: Number(item?.lead_won_rate || 0),
                    type: 'number',
                    component: (
                        <div
                            className={`${styles.productLeadConversionBadge} ${
                                Number(item?.lead_won_rate || 0) >= 20
                                    ? styles.high
                                    : Number(item?.lead_won_rate || 0) >= 10
                                        ? styles.medium
                                        : styles.low
                                }`}
                        >
                            {Number(item?.lead_won_rate || 0).toFixed(2)}%
                        </div>
                    ),
                },
            };
        });
    }, [analytics?.actual?.product_lead_stats, expandedProductLeadRows, toggleProductLeadRow]);

    const filteredProductLeadStatsTableData = useMemo(() => {
        const normalizedSearch = productLeadSearch.trim().toLowerCase();
        const selectedType = productLeadType?.value;
        const selectedCategory = productLeadCategory?.value;

        return productLeadStatsTableData.filter((row) => {
            const courseName = row?.course_name?.value || '';
            const courseMeta = coursesById[String(row?.id)];

            if (selectedType && String(courseMeta?.type) !== String(selectedType)) {
                return false;
            }

            if (selectedCategory && String(courseMeta?.category) !== String(selectedCategory)) {
                return false;
            }

            if (!normalizedSearch) {
                return true;
            }

            return courseName.toLowerCase().includes(normalizedSearch);
        });
    }, [productLeadStatsTableData, productLeadSearch, productLeadType, productLeadCategory, coursesById]);

    const filteredSourceLeadStatsRows = useMemo(() => {
        const sourceLeadStatsByBreakdown = analytics?.actual?.source_lead_stats_by_breakdown || {};
        const breakdownKey = sourceLeadBreakdown?.value || 'source';
        const rows = Array.isArray(sourceLeadStatsByBreakdown?.[breakdownKey]) ? sourceLeadStatsByBreakdown[breakdownKey] : [];
        const normalizedSearch = sourceLeadSearch.trim().toLowerCase();

        return rows.filter((item) => {
            if (!normalizedSearch) {
                return true;
            }

            const breakdownValue = (item?.value || item?.source || '').toString().toLowerCase();
            if (breakdownValue.includes(normalizedSearch)) {
                return true;
            }

            const leadCourses = Array.isArray(item?.lead?.courses) ? item.lead.courses : [];
            const wonCourses = Array.isArray(item?.lead_won?.courses) ? item.lead_won.courses : [];

            return [...leadCourses, ...wonCourses].some((entry) => {
                const courseName = (entry?.course?.name || '').toString().toLowerCase();
                return courseName.includes(normalizedSearch);
            });
        });
    }, [analytics?.actual?.source_lead_stats_by_breakdown, sourceLeadBreakdown, sourceLeadSearch]);

    const sourceLeadStatsTableData = useMemo(() => {
        const rows = filteredSourceLeadStatsRows;

        const buildCourseBreakdown = (title, rowId, total, coursesBreakdown) => {
            const entries = Array.isArray(coursesBreakdown) ? coursesBreakdown : [];
            const isExpanded = Boolean(expandedSourceLeadRows[rowId]);

            const handleToggle = (ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                toggleSourceLeadRow(rowId);
            };

            return {
                value: total || 0,
                type: 'number',
                component: (
                    <details open={isExpanded} onClick={(ev) => ev.stopPropagation()} className={styles.productLeadDetails}>
                        <summary onClick={handleToggle} className={styles.productLeadSummary}>
                            <div className={styles.productLeadSummaryMain}>{formatNumber(total || 0)}</div>
                        </summary>
                        <div className={styles.productLeadBreakdown}>
                            {entries.length === 0 && (
                                <div className={styles.productLeadEmpty}>
                                    Nessun dato corso
                                </div>
                            )}
                            {entries.map((entry, index) => {
                                const courseId = entry?.course?.id ?? `unknown-${index}`;
                                const courseName = entry?.course?.name || 'Corso sconosciuto';
                                return (
                                    <div key={`${title}-${rowId}-${courseId}`} className={styles.productLeadBreakdownRow}>
                                        <span className={styles.productLeadSourceName}>{courseName}</span>
                                        <span className={styles.productLeadSourceStats}>{formatNumber(entry?.count || 0)} • {(entry?.percentage || 0).toFixed(2)}%</span>
                                    </div>
                                );
                            })}
                        </div>
                    </details>
                )
            };
        };

        return rows.map((item, index) => {
            const breakdownValue = (item?.value || item?.source || 'direct').toString();
            const rowId = `source-lead-${breakdownValue}-${index}`;

            return {
                id: rowId,
                utm_source: {
                    value: breakdownValue,
                    type: 'string',
                    component: <div>{breakdownValue}</div>,
                },
                lead_total: buildCourseBreakdown('lead', rowId, item?.lead?.total || 0, item?.lead?.courses),
                lead_won_total: buildCourseBreakdown('lead_won', rowId, item?.lead_won?.total || 0, item?.lead_won?.courses),
                lead_won_rate: {
                    value: Number(item?.lead_won_rate || 0),
                    type: 'number',
                    component: (
                        <div
                            className={`${styles.productLeadConversionBadge} ${
                                Number(item?.lead_won_rate || 0) >= 20
                                    ? styles.high
                                    : Number(item?.lead_won_rate || 0) >= 10
                                        ? styles.medium
                                        : styles.low
                                }`}
                        >
                            {Number(item?.lead_won_rate || 0).toFixed(2)}%
                        </div>
                    ),
                },
            };
        });
    }, [filteredSourceLeadStatsRows, expandedSourceLeadRows, toggleSourceLeadRow]);

    const mapLeadScoreQualityRows = useCallback((rows, groupMode = 'course') => {
        const safeRows = Array.isArray(rows) ? rows : [];

        const scoreBadgeClass = (value) => {
            const numeric = Number(value || 0);
            if (numeric >= 70) return styles.high;
            if (numeric >= 40) return styles.medium;
            return styles.low;
        };

        const toFixed = (value, decimals = 2) => Number(value || 0).toFixed(decimals);

        return safeRows.map((item, index) => {
            const courseId = item?.course?.id ?? `lead-score-quality-${groupMode}-${index}`;
            const segmentValue = (item?.segment?.value || 'unknown').toString();
            const rowId = groupMode === 'course' ? String(courseId) : `segment-${segmentValue}-${index}`;
            const rowLabel = groupMode === 'course'
                ? (item?.course?.name || 'Corso sconosciuto')
                : segmentValue;

            const leadTotal = Number(item?.lead_total || 0);
            const avgScore = Number(item?.score?.avg || 0);
            const avgInitialScore = Number(item?.score?.initial_avg || 0);
            const hotRate = Number(item?.score?.hot_rate || 0);
            const warmRate = Number(item?.score?.warm_rate || 0);
            const coldRate = Number(item?.score?.cold_rate || 0);
            const avgDelta = Number(item?.score?.avg_delta || 0);
            const avgTouches = Number(item?.touches?.avg_touch_count || 0);
            const touchedRate = Number(item?.touches?.touched_rate || 0);
            const respondedTotal = Number(item?.touches?.responded_total || 0);
            const respondedRate = Number(item?.touches?.responded_rate || 0);
            const respondedAvgTouches = Number(item?.touches?.responded_avg_touch_count || 0);
            const respondedPurchaseProneRate = Number(item?.purchase_propensity?.responded_purchase_prone_rate || 0);
            const hotArrivalRate = Number(item?.temperature_trend?.arrived_hot_rate || 0);
            const warmedRate = Number(item?.temperature_trend?.warmed_rate || 0);
            const cooledRate = Number(item?.temperature_trend?.cooled_rate || 0);
            const statusSnapshot = item?.status_snapshot && typeof item.status_snapshot === 'object' ? item.status_snapshot : {};
            const statusPending = Number(statusSnapshot?.pending || 0);
            const statusMessageSent = Number(statusSnapshot?.message_sent || 0);
            const statusCallScheduled = Number(statusSnapshot?.call_scheduled || 0);
            const statusApproved = Number(statusSnapshot?.approved || 0);
            const statusRejected = Number(statusSnapshot?.rejected || 0);
            const statusNextEdition = Number(statusSnapshot?.next_edition || 0);
            const statusWon = Number(statusSnapshot?.won || 0);
            const statusGhost = Number(statusSnapshot?.ghost || 0);

            const mixTotal = hotRate + warmRate + coldRate;
            const hotWidth = mixTotal > 0 ? (hotRate / mixTotal) * 100 : 0;
            const warmWidth = mixTotal > 0 ? (warmRate / mixTotal) * 100 : 0;
            const coldWidth = mixTotal > 0 ? (coldRate / mixTotal) * 100 : 0;

            return {
                id: rowId,
                course_id: courseId,
                course_name: {
                    value: rowLabel,
                    type: 'string',
                    component: <div>{rowLabel}</div>,
                },
                lead_total: {
                    value: leadTotal,
                    type: 'number',
                    component: <div>{formatNumber(leadTotal)}</div>,
                },
                status_pending: {
                    value: statusPending,
                    type: 'number',
                    component: <div>{formatNumber(statusPending)}</div>,
                },
                status_message_sent: {
                    value: statusMessageSent,
                    type: 'number',
                    component: <div>{formatNumber(statusMessageSent)}</div>,
                },
                status_call_scheduled: {
                    value: statusCallScheduled,
                    type: 'number',
                    component: <div>{formatNumber(statusCallScheduled)}</div>,
                },
                status_approved: {
                    value: statusApproved,
                    type: 'number',
                    component: <div>{formatNumber(statusApproved)}</div>,
                },
                status_rejected: {
                    value: statusRejected,
                    type: 'number',
                    component: <div>{formatNumber(statusRejected)}</div>,
                },
                status_next_edition: {
                    value: statusNextEdition,
                    type: 'number',
                    component: <div>{formatNumber(statusNextEdition)}</div>,
                },
                status_won: {
                    value: statusWon,
                    type: 'number',
                    component: <div>{formatNumber(statusWon)}</div>,
                },
                status_ghost: {
                    value: statusGhost,
                    type: 'number',
                    component: <div>{formatNumber(statusGhost)}</div>,
                },
                avg_score: {
                    value: avgScore,
                    type: 'number',
                    component: (
                        <div className={`${styles.productLeadConversionBadge} ${scoreBadgeClass(avgScore)}`}>
                            {toFixed(avgScore)}
                        </div>
                    ),
                },
                avg_initial_score: {
                    value: avgInitialScore,
                    type: 'number',
                    component: (
                        <div className={`${styles.productLeadConversionBadge} ${scoreBadgeClass(avgInitialScore)}`}>
                            {toFixed(avgInitialScore)}
                        </div>
                    ),
                },
                score_mix: {
                    value: `${toFixed(hotRate)}|${toFixed(warmRate)}|${toFixed(coldRate)}`,
                    type: 'string',
                    component: (
                        <div className={styles.leadScoreMixWrap}>
                            <div className={styles.leadScoreMixTrack}>
                                <div className={styles.leadScoreMixHot} style={{ width: `${hotWidth}%` }} />
                                <div className={styles.leadScoreMixWarm} style={{ width: `${warmWidth}%` }} />
                                <div className={styles.leadScoreMixCold} style={{ width: `${coldWidth}%` }} />
                            </div>
                            <div className={styles.leadScoreMixLegend}>
                                <span>H {toFixed(hotRate)}%</span>
                                <span>W {toFixed(warmRate)}%</span>
                                <span>C {toFixed(coldRate)}%</span>
                            </div>
                        </div>
                    ),
                },
                touch: {
                    value: avgTouches,
                    type: 'number',
                    component: (
                        <div className={styles.leadScoreMetricWrap}>
                            <div className={styles.leadScoreMetricTop}>Media {toFixed(avgTouches)} touch</div>
                            <div className={styles.leadScoreMetricTrack}>
                                <div className={styles.leadScoreMetricFillBlue} style={{ width: `${Math.max(0, Math.min(100, touchedRate))}%` }} />
                            </div>
                            <div className={styles.leadScoreMetricBottom}>{toFixed(touchedRate)}% con touch</div>
                        </div>
                    ),
                },
                touch_quality: {
                    value: respondedRate,
                    type: 'number',
                    component: (
                        <div className={styles.leadScoreMetricWrap}>
                            <div className={styles.leadScoreMetricTop}>{formatNumber(respondedTotal)} risposti</div>
                            <div className={styles.leadScoreMetricTrack}>
                                <div className={styles.leadScoreMetricFillOrange} style={{ width: `${Math.max(0, Math.min(100, respondedRate))}%` }} />
                            </div>
                            <div className={styles.leadScoreMetricBottom}>{toFixed(respondedRate)}% • {toFixed(respondedAvgTouches)} touch</div>
                        </div>
                    ),
                },
                purchase_propensity: {
                    value: respondedPurchaseProneRate,
                    type: 'number',
                    component: (
                        <div className={styles.leadScoreMetricWrap}>
                            <div className={styles.leadScoreMetricTrack}>
                                <div className={styles.leadScoreMetricFillGreen} style={{ width: `${Math.max(0, Math.min(100, respondedPurchaseProneRate))}%` }} />
                            </div>
                            <div className={styles.leadScoreMetricBottom}>{toFixed(respondedPurchaseProneRate)}%</div>
                        </div>
                    ),
                },
                arrival_heat: {
                    value: hotArrivalRate,
                    type: 'number',
                    component: <div>{toFixed(hotArrivalRate)}%</div>,
                },
                warming_rate: {
                    value: warmedRate,
                    type: 'number',
                    component: <div style={{ color: 'var(--sf-green)' }}>{toFixed(warmedRate)}%</div>,
                },
                cooling_rate: {
                    value: cooledRate,
                    type: 'number',
                    component: <div style={{ color: 'var(--sf-red)' }}>{toFixed(cooledRate)}%</div>,
                },
                avg_delta: {
                    value: avgDelta,
                    type: 'number',
                    component: (
                        <div style={{ color: avgDelta >= 0 ? 'var(--sf-green)' : 'var(--sf-red)' }}>
                            {avgDelta >= 0 ? '+' : ''}{toFixed(avgDelta)}
                        </div>
                    ),
                },
            };
        });
    }, []);

    const leadScoreQualityTableData = useMemo(() => {
        const rows = Array.isArray(analytics?.actual?.lead_score_quality?.rows)
            ? analytics.actual.lead_score_quality.rows
            : [];
        return mapLeadScoreQualityRows(rows, 'course');
    }, [analytics?.actual?.lead_score_quality?.rows, mapLeadScoreQualityRows]);

    const leadScoreQualitySnapshotTableData = useMemo(() => {
        const rows = Array.isArray(analytics?.actual?.lead_score_quality?.rows)
            ? analytics.actual.lead_score_quality.rows
            : [];

        return mapLeadScoreQualityRows(rows, 'course');
    }, [
        analytics?.actual?.lead_score_quality?.rows,
        mapLeadScoreQualityRows,
    ]);

    const leadScoreSnapshotSourceBreakdownTableDataByRow = useMemo(() => {
        return Object.entries(leadScoreSnapshotSourceBreakdownByRow || {}).reduce((acc, [rowId, rows]) => {
            acc[rowId] = mapLeadScoreQualityRows(rows, 'segment');
            return acc;
        }, {});
    }, [leadScoreSnapshotSourceBreakdownByRow, mapLeadScoreQualityRows]);

    const leadScoreSnapshotColumns = useMemo(() => ([
        { key: 'course_name', label: 'CORSO', type: 'custom', wrap: true, style: { width: '100%', minWidth: '260px', padding: '0.85rem 1rem', alignItems: 'flex-start' } },
        { key: 'lead_total', label: 'LEAD', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
        { key: 'status_pending', label: 'DA CONTATTARE', type: 'custom', style: { minWidth: '130px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
        { key: 'status_message_sent', label: 'MESSAGGIO INVIATO', type: 'custom', style: { minWidth: '160px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
        { key: 'status_call_scheduled', label: 'CHIAMATA SCHEDULATA', type: 'custom', style: { minWidth: '170px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
        { key: 'status_approved', label: 'ACCETTATI', type: 'custom', style: { minWidth: '120px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
        { key: 'status_rejected', label: 'RIFIUTATI', type: 'custom', style: { minWidth: '120px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
        { key: 'status_next_edition', label: 'PROSSIMA EDIZIONE', type: 'custom', style: { minWidth: '160px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
        { key: 'status_won', label: 'VINTI', type: 'custom', style: { minWidth: '100px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
        { key: 'status_ghost', label: 'PERSI', type: 'custom', style: { minWidth: '100px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
        { key: 'touch_quality', label: 'LEAD RISPOSTI', type: 'custom', wrap: true, style: { minWidth: '210px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
        { key: 'purchase_propensity', label: 'PROPENSIONE ACQUISTO', type: 'custom', wrap: true, style: { minWidth: '210px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
        { key: 'score_mix', label: 'MIX HOT/WARM/COLD', type: 'custom', wrap: true, style: { minWidth: '220px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
        { key: 'avg_score', label: 'SCORE MEDIO', type: 'custom', style: { minWidth: '130px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
        { key: 'avg_initial_score', label: 'SCORE MEDIO INIZIALE', type: 'custom', style: { minWidth: '170px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
    ]), []);

    const leadScoreSnapshotSourceColumns = useMemo(() => (
        leadScoreSnapshotColumns.map((column) => (
            column.key === 'course_name'
                ? { ...column, label: 'UTM SOURCE' }
                : column
        ))
    ), [leadScoreSnapshotColumns]);

    const leadScoreSnapshotDialogRows = useMemo(() => {
        const rowId = leadScoreSnapshotDialogRow?.id;
        if (!rowId) {
            return [];
        }
        return Array.isArray(leadScoreSnapshotSourceBreakdownTableDataByRow?.[rowId])
            ? leadScoreSnapshotSourceBreakdownTableDataByRow[rowId]
            : [];
    }, [leadScoreSnapshotDialogRow?.id, leadScoreSnapshotSourceBreakdownTableDataByRow]);

    const leadScoreSnapshotDialogLoading = useMemo(() => (
        Boolean(leadScoreSnapshotSourceLoadingByRow?.[leadScoreSnapshotDialogRow?.id])
    ), [leadScoreSnapshotDialogRow?.id, leadScoreSnapshotSourceLoadingByRow]);

    const leadScoreSnapshotDialogError = useMemo(() => (
        leadScoreSnapshotSourceErrorByRow?.[leadScoreSnapshotDialogRow?.id]
    ), [leadScoreSnapshotDialogRow?.id, leadScoreSnapshotSourceErrorByRow]);

    const leadScoreQualityUtmTableData = useMemo(() => {
        const rows = Array.isArray(leadScoreQualityUtm?.rows) ? leadScoreQualityUtm.rows : [];
        return mapLeadScoreQualityRows(rows, 'segment');
    }, [leadScoreQualityUtm?.rows, mapLeadScoreQualityRows]);

    const touchOutcomeLabels = useMemo(() => ({
        answered: 'Risposto',
        not_answered: 'Non risposto',
        awaiting_response: 'In attesa',
        postponed: 'Rimandato',
        wrong_contact: 'Contatto errato',
        unknown: 'Non indicato',
    }), []);

    const touchFeedbackLabels = useMemo(() => ({
        purchase_intent: 'Propenso acquisto',
        interested: 'Interessato',
        not_interested: 'Non interessato',
        no_budget: 'No budget',
        wrong_target: 'Target errato',
        needs_time: 'Serve tempo',
        unknown: 'Non indicato',
    }), []);

    const renderTouchDistribution = useCallback((items, emptyLabel = 'Nessun dato') => {
        const safeItems = Array.isArray(items) ? items : [];
        return (
            <div className={styles.productLeadBreakdown}>
                {safeItems.length === 0 && (
                    <div className={styles.productLeadEmpty}>{emptyLabel}</div>
                )}
                {safeItems.map((item) => (
                    <div key={`${item?.key || 'unknown'}-${item?.count || 0}`} className={styles.productLeadBreakdownRow}>
                        <span className={styles.productLeadSourceName}>{item?.label || item?.key || 'Non indicato'}</span>
                        <span className={styles.productLeadSourceStats}>{formatNumber(item?.count || 0)} • {Number(item?.rate || 0).toFixed(2)}%</span>
                    </div>
                ))}
            </div>
        );
    }, []);

    const mapTouchBreakdownRows = useCallback((rows, mode = 'course') => {
        const safeRows = Array.isArray(rows) ? rows : [];
        return safeRows.map((item, index) => {
            const label = mode === 'course'
                ? (item?.course?.name || 'Corso sconosciuto')
                : (item?.source || 'direct');
            const touchTotal = Number(item?.touch_total || 0);
            const answeredTotal = Number(item?.answered_total || 0);
            const outcomes = (Array.isArray(item?.outcomes) ? item.outcomes : []).map((entry) => ({
                ...entry,
                label: touchOutcomeLabels[entry?.key] || entry?.key || 'Non indicato',
            }));
            const feedback = (Array.isArray(item?.feedback) ? item.feedback : []).map((entry) => ({
                ...entry,
                label: touchFeedbackLabels[entry?.key] || entry?.key || 'Non indicato',
            }));

            return {
                id: `touch-breakdown-${mode}-${item?.course?.id || item?.source || index}`,
                dimension: {
                    value: label,
                    type: 'string',
                    component: <div>{label}</div>,
                },
                touch_total: {
                    value: touchTotal,
                    type: 'number',
                    component: <div>{formatNumber(touchTotal)}</div>,
                },
                answered_total: {
                    value: answeredTotal,
                    type: 'number',
                    component: <div>{formatNumber(answeredTotal)}</div>,
                },
                outcomes: {
                    value: touchTotal,
                    type: 'number',
                    component: renderTouchDistribution(outcomes, 'Nessun outcome'),
                },
                feedback: {
                    value: answeredTotal,
                    type: 'number',
                    component: renderTouchDistribution(feedback, 'Nessun feedback'),
                },
            };
        });
    }, [renderTouchDistribution, touchFeedbackLabels, touchOutcomeLabels]);

    const leadTouchBreakdownCourseTableData = useMemo(() => {
        const rows = Array.isArray(analytics?.actual?.lead_score_quality?.touch_breakdown?.by_course)
            ? analytics.actual.lead_score_quality.touch_breakdown.by_course
            : [];
        return mapTouchBreakdownRows(rows, 'course');
    }, [analytics?.actual?.lead_score_quality?.touch_breakdown?.by_course, mapTouchBreakdownRows]);

    const leadTouchBreakdownSourceTableData = useMemo(() => {
        const rows = Array.isArray(analytics?.actual?.lead_score_quality?.touch_breakdown?.by_source)
            ? analytics.actual.lead_score_quality.touch_breakdown.by_source
            : [];
        return mapTouchBreakdownRows(rows, 'source');
    }, [analytics?.actual?.lead_score_quality?.touch_breakdown?.by_source, mapTouchBreakdownRows]);

    const leadTouchBreakdownColumns = useMemo(() => ([
        { key: 'dimension', label: 'CORSO', type: 'custom', wrap: true, style: { width: '100%', minWidth: '280px', padding: '0.85rem 1rem', alignItems: 'flex-start' } },
        { key: 'touch_total', label: 'TOUCH', type: 'custom', style: { minWidth: '100px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
        { key: 'answered_total', label: 'RISPOSTI', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
        { key: 'outcomes', label: 'CONTACT OUTCOME', type: 'custom', wrap: true, style: { minWidth: '250px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
        { key: 'feedback', label: 'FEEDBACK', type: 'custom', wrap: true, style: { minWidth: '250px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
    ]), []);

    const leadTouchBreakdownSourceColumns = useMemo(() => (
        leadTouchBreakdownColumns.map((column) => (
            column.key === 'dimension'
                ? { ...column, label: 'SOURCE' }
                : column
        ))
    ), [leadTouchBreakdownColumns]);

    useEffect(() => {
        setLeadScoreSnapshotDialogRow(null);
        setLeadScoreSnapshotSourceBreakdownByRow({});
        setLeadScoreSnapshotSourceLoadingByRow({});
        setLeadScoreSnapshotSourceErrorByRow({});
    }, [filters.from_date, filters.to_date, debouncedCourses]);

    const handleExportLeadScoreQualityCsv = useCallback(() => {
        const rows = Array.isArray(analytics?.actual?.lead_score_quality?.rows)
            ? analytics.actual.lead_score_quality.rows
            : [];

        const rangeStart = formatDateAsRomeDay(filters?.from_date) || 'na';
        const rangeEnd = formatDateAsRomeDay(filters?.to_date) || 'na';
        const suffix = `${rangeStart}_${rangeEnd}`;

        const headers = [
            'Corso',
            'Lead',
            'Score Medio',
            'Score Medio Iniziale',
            'Hot %',
            'Warm %',
            'Cold %',
            'Touch Medi',
            'Touch Rate %',
            'Lead Risposti',
            'Touch Quality Risposti %',
            'Touch Medi (Risposti)',
            'Propensione Acquisto (Risposti) %',
            'Arriva Hot %',
            'Warmed %',
            'Cooled %',
            'Delta Medio Score',
            'Intent Medio',
            'Fit Medio',
            'Process Medio',
            'Interest Medio',
        ];

        const csvRows = rows.map((item) => ([
            item?.course?.name || 'Corso sconosciuto',
            Number(item?.lead_total || 0),
            Number(item?.score?.avg || 0).toFixed(2),
            Number(item?.score?.initial_avg || 0).toFixed(2),
            Number(item?.score?.hot_rate || 0).toFixed(2),
            Number(item?.score?.warm_rate || 0).toFixed(2),
            Number(item?.score?.cold_rate || 0).toFixed(2),
            Number(item?.touches?.avg_touch_count || 0).toFixed(2),
            Number(item?.touches?.touched_rate || 0).toFixed(2),
            Number(item?.touches?.responded_total || 0),
            Number(item?.touches?.responded_rate || 0).toFixed(2),
            Number(item?.touches?.responded_avg_touch_count || 0).toFixed(2),
            Number(item?.purchase_propensity?.responded_purchase_prone_rate || 0).toFixed(2),
            Number(item?.temperature_trend?.arrived_hot_rate || 0).toFixed(2),
            Number(item?.temperature_trend?.warmed_rate || 0).toFixed(2),
            Number(item?.temperature_trend?.cooled_rate || 0).toFixed(2),
            Number(item?.score?.avg_delta || 0).toFixed(2),
            Number(item?.components?.intent_avg || 0).toFixed(2),
            Number(item?.components?.fit_avg || 0).toFixed(2),
            Number(item?.components?.process_avg || 0).toFixed(2),
            Number(item?.components?.interest_avg || 0).toFixed(2),
        ]));

        downloadCsv(`lead_score_quality_${suffix}.csv`, buildCsvContent(headers, csvRows));
    }, [analytics?.actual?.lead_score_quality?.rows, filters?.from_date, filters?.to_date]);

    const courseLeadForecastTableData = useMemo(() => {
        const rows = Array.isArray(productLeadStatsLast12Months) ? productLeadStatsLast12Months : [];
        const normalizedTarget = Math.max(0, Number(leadTargetEnrollments) || 0);

        const aggregateLeadTotal = rows.reduce((acc, item) => acc + Number(item?.lead?.total || 0), 0);
        const aggregateLeadWonTotal = rows.reduce((acc, item) => acc + Number(item?.lead_won?.total || 0), 0);
        const aggregateConversionRate = aggregateLeadTotal > 0 ? (aggregateLeadWonTotal / aggregateLeadTotal) * 100 : 0;

        const normalizedRows = rows.map((item, index) => {
            const leadTotal = Number(item?.lead?.total || 0);
            const leadWonTotal = Number(item?.lead_won?.total || 0);
            const rawLeadWonRate = Number(item?.lead_won_rate);
            const leadWonRateFromCourse = Number.isFinite(rawLeadWonRate)
                ? rawLeadWonRate
                : leadTotal > 0
                    ? (leadWonTotal / leadTotal) * 100
                    : 0;
            const courseId = String(item?.course?.id ?? `forecast-course-${index}`);
            const hasStartedEdition = Boolean(hasStartedEditionByCourseId[courseId]);
            const effectiveLeadWonRate = hasStartedEdition && leadWonRateFromCourse > 0
                ? leadWonRateFromCourse
                : aggregateConversionRate;

            return {
                id: courseId,
                courseName: item?.course?.name || 'Corso sconosciuto',
                leadTotal,
                leadWonTotal,
                leadWonRateFromCourse,
                effectiveLeadWonRate,
                hasStartedEdition,
            };
        });

        return normalizedRows.map((row) => {
            const targetEnrollmentsForCourse = normalizedTarget;
            const conversionRateDecimal = Number(row.effectiveLeadWonRate || 0) / 100;
            const requiredLeads = conversionRateDecimal > 0
                ? Math.ceil(targetEnrollmentsForCourse / conversionRateDecimal)
                : null;
            const leadsFromLastEdition = Number(leadSinceLastEditionByCourseId[row.id] || 0);
            const leadGap = requiredLeads === null ? null : requiredLeads - leadsFromLastEdition;

            return {
                id: row.id,
                course_name: {
                    value: row.courseName,
                    type: 'string',
                    component: <div>{row.courseName}</div>,
                },
                historical_leads: {
                    value: row.leadTotal,
                    type: 'number',
                    component: <div>{formatNumber(row.leadTotal)}</div>,
                },
                lead_from_last_edition: {
                    value: leadsFromLastEdition,
                    type: 'number',
                    component: <div>{formatNumber(leadsFromLastEdition)}</div>,
                },
                historical_won: {
                    value: row.leadWonTotal,
                    type: 'number',
                    component: <div>{formatNumber(row.leadWonTotal)}</div>,
                },
                historical_conversion: {
                    value: row.effectiveLeadWonRate,
                    type: 'number',
                    component: (
                        <div className={`${styles.productLeadConversionBadge} ${row.effectiveLeadWonRate >= 20 ? styles.high : row.effectiveLeadWonRate >= 10 ? styles.medium : styles.low}`}>
                            {row.effectiveLeadWonRate.toFixed(2)}%
                        </div>
                    ),
                },
                conversion_source: {
                    value: row.hasStartedEdition ? 'course' : 'aggregate',
                    type: 'string',
                    component: <div>{row.hasStartedEdition ? 'Corso' : 'Media corsi'}</div>,
                },
                target_enrollments: {
                    value: targetEnrollmentsForCourse,
                    type: 'number',
                    component: <div>{targetEnrollmentsForCourse.toFixed(1)}</div>,
                },
                required_leads: {
                    value: requiredLeads === null ? null : requiredLeads,
                    type: 'number',
                    component: <div>{requiredLeads === null ? '--' : formatNumber(requiredLeads)}</div>,
                },
                lead_gap: {
                    value: leadGap === null ? null : leadGap,
                    type: 'number',
                    component: (
                        <div style={{ color: leadGap === null ? 'rgba(var(--text-color-rgb), 56%)' : leadGap > 0 ? 'var(--sf-red)' : 'var(--sf-green)' }}>
                            {leadGap === null ? '--' : `${leadGap > 0 ? '+' : ''}${formatNumber(leadGap)}`}
                        </div>
                    ),
                },
            };
        });
    }, [productLeadStatsLast12Months, leadTargetEnrollments, hasStartedEditionByCourseId, leadSinceLastEditionByCourseId]);

    const leadForecastTotals = useMemo(() => {
        const totalRequiredLeads = courseLeadForecastTableData.reduce((acc, row) => {
            const value = Number(row?.required_leads?.value);
            return Number.isFinite(value) ? acc + value : acc;
        }, 0);

        const totalLeadsFromLastEdition = courseLeadForecastTableData.reduce((acc, row) => {
            return acc + Number(row?.lead_from_last_edition?.value || 0);
        }, 0);

        return {
            totalRequiredLeads,
            totalLeadsFromLastEdition,
            hasRows: courseLeadForecastTableData.length > 0,
        };
    }, [courseLeadForecastTableData]);

    const editionConversionTrendTableData = useMemo(() => {
        const rows = Array.isArray(courseEditionConversionTrendRows) ? courseEditionConversionTrendRows : [];

        return rows.map((courseRow, index) => {
            const rowId = courseRow?.id || `course-edition-row-${index}`;
            const isExpanded = Boolean(expandedCourseEditionRows[rowId]);
            const editions = Array.isArray(courseRow?.editions) ? courseRow.editions : [];

            const handleToggle = (ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                toggleCourseEditionRow(rowId);
            };

            const renderEditionBreakdown = (renderValue) => (
                <div className={styles.productLeadBreakdown}>
                    {editions.length === 0 && (
                        <div className={styles.productLeadEmpty}>--</div>
                    )}
                    {editions.map((edition, editionIndex) => (
                        <div key={`${rowId}-edition-col-${edition?.id || editionIndex}`} className={styles.productLeadBreakdownRow}>
                            <span className={styles.productLeadSourceStats}>{renderValue(edition, editionIndex)}</span>
                        </div>
                    ))}
                </div>
            );

            return {
                id: rowId,
                course_name: {
                    value: courseRow?.course_name || `Corso ${index + 1}`,
                    type: 'string',
                    component: (
                        <details open={isExpanded} onClick={(ev) => ev.stopPropagation()} className={styles.productLeadDetails}>
                            <summary onClick={handleToggle} className={styles.productLeadSummary}>
                                <div className={styles.productLeadSummaryMain}>{courseRow?.course_name || `Corso ${index + 1}`}</div>
                            </summary>
                            <div className={styles.productLeadBreakdown}>
                                {editions.length === 0 && (
                                    <div className={styles.productLeadEmpty}>Nessuna edizione disponibile</div>
                                )}
                                {editions.map((edition, editionIndex) => {
                                    const editionName = edition?.edition_name || `Edizione ${editionIndex + 1}`;
                                    const firstLessonDateLabel = formatEditionShortDate(edition?.start_date);

                                    return (
                                        <div key={`${rowId}-edition-${edition?.id || editionIndex}`} className={styles.productLeadBreakdownRow}>
                                            <span className={styles.productLeadSourceName}>{editionName}{firstLessonDateLabel !== '--' ? ` • ${firstLessonDateLabel}` : ''}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </details>
                    ),
                },
                conversion_rate: {
                    value: Number(courseRow?.conversion_rate || 0),
                    type: 'number',
                    component: (
                        <div>
                            <div>{Number(courseRow?.conversion_rate || 0).toFixed(2)}%</div>
                            {isExpanded && renderEditionBreakdown((edition) => `${Number(edition?.conversion_rate || 0).toFixed(2)}%`)}
                        </div>
                    ),
                },
                lead_total: {
                    value: Number(courseRow?.lead_total || 0),
                    type: 'number',
                    component: (
                        <div>
                            <div>{formatNumber(courseRow?.lead_total || 0)}</div>
                            {isExpanded && renderEditionBreakdown((edition) => formatNumber(edition?.lead_total || 0))}
                        </div>
                    ),
                },
                enrolled_total: {
                    value: Number(courseRow?.enrolled_total || 0),
                    type: 'number',
                    component: (
                        <div>
                            <div>{formatNumber(courseRow?.enrolled_total || 0)}</div>
                            {isExpanded && renderEditionBreakdown((edition) => formatNumber(edition?.enrolled_total || 0))}
                        </div>
                    ),
                },
            };
        });
    }, [courseEditionConversionTrendRows, expandedCourseEditionRows, toggleCourseEditionRow]);

    const leadDemographics = useMemo(() => {
        return analytics?.actual?.lead_demographics || null;
    }, [analytics?.actual?.lead_demographics]);

    const leadDemographicsSummary = useMemo(() => {
        return leadDemographics?.summary || { lead_total: 0, lead_won_total: 0, conversion_rate: 0, avg_age: null };
    }, [leadDemographics]);

    const leadDemographicsAgeItems = useMemo(() => Array.isArray(leadDemographics?.age) ? leadDemographics.age : [], [leadDemographics]);
    const leadDemographicsRegionItems = useMemo(() => Array.isArray(leadDemographics?.regions) ? leadDemographics.regions : [], [leadDemographics]);
    const leadDemographicsGenderItems = useMemo(() => Array.isArray(leadDemographics?.gender) ? leadDemographics.gender : [], [leadDemographics]);
    const leadDemographicsEmploymentItems = useMemo(() => Array.isArray(leadDemographics?.employment) ? leadDemographics.employment : [], [leadDemographics]);
    const leadDemographicsCourseItems = useMemo(() => Array.isArray(leadDemographics?.courses) ? leadDemographics.courses : [], [leadDemographics]);

    const leadDemographicsRegionTopItems = useMemo(() => leadDemographicsRegionItems.slice(0, 10), [leadDemographicsRegionItems]);

    const leadDemographicsAgeChartData = useMemo(() => {
        return leadDemographicsAgeItems.map((item) => ({
            label: formatDemographicLabel(item?.label),
            percentage: Number(item?.percentage || 0),
            conversion_rate: Number(item?.conversion_rate || 0),
            count: Number(item?.count || 0),
            won: Number(item?.won || 0),
        }));
    }, [leadDemographicsAgeItems]);

    const leadDemographicsRegionChartData = useMemo(() => {
        return leadDemographicsRegionTopItems.map((item) => ({
            label: formatDemographicLabel(item?.label),
            percentage: Number(item?.percentage || 0),
            conversion_rate: Number(item?.conversion_rate || 0),
            count: Number(item?.count || 0),
            won: Number(item?.won || 0),
        }));
    }, [leadDemographicsRegionTopItems]);

    const leadDemographicsGenderChartData = useMemo(() => {
        return leadDemographicsGenderItems.map((item) => ({
            label: formatDemographicLabel(item?.label),
            percentage: Number(item?.percentage || 0),
            conversion_rate: Number(item?.conversion_rate || 0),
            count: Number(item?.count || 0),
            won: Number(item?.won || 0),
        }));
    }, [leadDemographicsGenderItems]);

    const leadDemographicsOccupationChartData = useMemo(() => {
        return leadDemographicsEmploymentItems.map((item) => ({
            label: formatDemographicLabel(item?.label),
            percentage: Number(item?.percentage || 0),
            conversion_rate: Number(item?.conversion_rate || 0),
            count: Number(item?.count || 0),
            won: Number(item?.won || 0),
        }));
    }, [leadDemographicsEmploymentItems]);

    const renderBreakdownColumn = useCallback((items, limit = 4) => {
        const rows = Array.isArray(items) ? items : [];
        if (rows.length === 0) {
            return <div className={styles.demographicBreakdownCell}>--</div>;
        }
        return (
            <div className={styles.demographicBreakdownColumn}>
                {rows.slice(0, limit).map((entry, index) => (
                    <div key={`${entry?.label || 'nd'}-${index}`} className={styles.demographicBreakdownRow}>
                        <span>{formatDemographicLabel(entry?.label)}</span>
                        <span>{formatPercentage(entry?.percentage)}</span>
                    </div>
                ))}
            </div>
        );
    }, []);

    const leadDemographicsCourseTableData = useMemo(() => {
        const pickBestSegment = (items = [], mode = 'all') => {
            const validRows = (Array.isArray(items) ? items : []).filter((entry) => {
                const label = String(entry?.label || '').trim().toLowerCase();
                return label && label !== 'non disponibile';
            });

            if (validRows.length === 0) {
                return null;
            }

            const getPrimaryValue = (entry) => {
                if (mode === 'won') {
                    return Number(entry?.won || 0);
                }
                return Number(entry?.count || 0);
            };

            const sortedRows = [...validRows].sort((a, b) => {
                const primaryDiff = getPrimaryValue(b) - getPrimaryValue(a);
                if (primaryDiff !== 0) return primaryDiff;
                const convDiff = Number(b?.conversion_rate || 0) - Number(a?.conversion_rate || 0);
                if (convDiff !== 0) return convDiff;
                const countDiff = Number(b?.count || 0) - Number(a?.count || 0);
                if (countDiff !== 0) return countDiff;
                return String(a?.label || '').localeCompare(String(b?.label || ''));
            });

            const topRow = sortedRows[0];
            const topPrimary = getPrimaryValue(topRow);
            if (topPrimary <= 0) {
                return null;
            }

            const hasPrimaryTie = sortedRows.slice(1).some((entry) => getPrimaryValue(entry) === topPrimary);
            if (hasPrimaryTie) {
                return null;
            }

            return topRow;
        };

        const buildTypeLabel = (item, mode = 'all') => {
            const bestGender = pickBestSegment(item?.gender, mode);
            const bestAge = pickBestSegment(item?.age, mode);
            const bestEmployment = pickBestSegment(item?.employment, mode);
            const bestRegion = pickBestSegment(item?.regions, mode);

            const genderLabel = formatDemographicLabel(bestGender?.label) || null;
            const ageLabel = formatDemographicLabel(bestAge?.label) || null;
            const employmentLabel = formatDemographicLabel(bestEmployment?.label) || null;
            const normalizedEmploymentLabel = (employmentLabel || '').trim().toLowerCase();
            const includeEmployment = Boolean(employmentLabel && !normalizedEmploymentLabel.startsWith('nessuno'));

            if (!genderLabel || !ageLabel) {
                return '--';
            }

            const profileParts = [
                genderLabel,
                ageLabel,
                includeEmployment ? employmentLabel : null,
            ].filter(Boolean);

            return `${profileParts.join(' ')}${bestRegion?.label ? `, ${formatDemographicLabel(bestRegion.label)}` : ''}`;
        };

        return leadDemographicsCourseItems.map((item, index) => {
            const courseId = item?.course?.id ?? `course-demographic-${index}`;
            const courseName = item?.course?.name || 'Corso sconosciuto';
            const wonLeadsTotal = Number(item?.lead_won_total || 0);
            const customerTypeLabel = wonLeadsTotal > 0 ? buildTypeLabel(item, 'won') : '--';
            const leadTypeLabel = buildTypeLabel(item, 'all');

            return {
                id: String(courseId),
                course_name: {
                    value: courseName,
                    type: 'string',
                    component: <div>{courseName}</div>,
                },
                lead_type: {
                    value: leadTypeLabel,
                    type: 'string',
                    component: <div className={styles.demographicBreakdownCell}>{leadTypeLabel}</div>,
                },
                customer_type: {
                    value: customerTypeLabel,
                    type: 'string',
                    component: <div className={styles.demographicBreakdownCell}>{customerTypeLabel}</div>,
                },
                lead_total: {
                    value: Number(item?.lead_total || 0),
                    type: 'number',
                    component: <div>{formatNumber(item?.lead_total || 0)}</div>,
                },
                lead_won_total: {
                    value: Number(item?.lead_won_total || 0),
                    type: 'number',
                    component: <div>{formatNumber(item?.lead_won_total || 0)}</div>,
                },
                conversion_rate: {
                    value: Number(item?.conversion_rate || 0),
                    type: 'number',
                    component: <div>{formatPercentage(item?.conversion_rate)}</div>,
                },
                age_breakdown: {
                    value: (item?.age || []).map((entry) => `${entry?.label || 'N/D'} ${formatPercentage(entry?.percentage)}`).join(' | '),
                    type: 'string',
                    component: renderBreakdownColumn(item?.age),
                },
                region_breakdown: {
                    value: (item?.regions || []).map((entry) => `${entry?.label || 'N/D'} ${formatPercentage(entry?.percentage)}`).join(' | '),
                    type: 'string',
                    component: renderBreakdownColumn(item?.regions),
                },
                gender_breakdown: {
                    value: (item?.gender || []).map((entry) => `${entry?.label || 'N/D'} ${formatPercentage(entry?.percentage)}`).join(' | '),
                    type: 'string',
                    component: renderBreakdownColumn(item?.gender),
                },
                employment_breakdown: {
                    value: (item?.employment || []).map((entry) => `${entry?.label || 'N/D'} ${formatPercentage(entry?.percentage)}`).join(' | '),
                    type: 'string',
                    component: renderBreakdownColumn(item?.employment),
                },
            };
        });
    }, [leadDemographicsCourseItems, renderBreakdownColumn]);

    const filteredLeadDemographicsCourseTableData = useMemo(() => {
        const normalizedSearch = leadDemographicsSearch.trim().toLowerCase();
        const selectedType = leadDemographicsType?.value;
        const selectedCategory = leadDemographicsCategory?.value;

        return leadDemographicsCourseTableData.filter((row) => {
            const courseName = (row?.course_name?.value || '').toString();
            const courseMeta = coursesById[String(row?.id)];

            if (selectedType && String(courseMeta?.type) !== String(selectedType)) {
                return false;
            }

            if (selectedCategory && String(courseMeta?.category) !== String(selectedCategory)) {
                return false;
            }

            if (!normalizedSearch) {
                return true;
            }

            return courseName.toLowerCase().includes(normalizedSearch);
        });
    }, [leadDemographicsCourseTableData, leadDemographicsSearch, leadDemographicsType, leadDemographicsCategory, coursesById]);

    const handleExportLeadDemographicsCsv = useCallback(() => {
        const rangeStart = formatDateAsRomeDay(filters?.from_date) || 'na';
        const rangeEnd = formatDateAsRomeDay(filters?.to_date) || 'na';
        const suffix = `${rangeStart}_${rangeEnd}`;

        const overviewHeaders = ['Dimensione', 'Segmento', 'Lead', 'Lead Vinti', 'Percentuale Lead'];

        const buildOverviewRows = (dimensionLabel, items) => {
            const rows = Array.isArray(items) ? items : [];
            return rows.map((item) => ([
                dimensionLabel,
                formatDemographicLabel(item?.label) || 'N/D',
                Number(item?.count || 0),
                Number(item?.won || 0),
                Number(item?.percentage || 0).toFixed(2),
            ]));
        };

        const overviewRows = [
            ['Summary', 'Lead Totali', Number(leadDemographicsSummary?.lead_total || 0), '', ''],
            ['Summary', 'Lead Vinti', Number(leadDemographicsSummary?.lead_won_total || 0), '', ''],
            ['Summary', 'Età Media', Number.isFinite(Number(leadDemographicsSummary?.avg_age)) ? Number(leadDemographicsSummary.avg_age).toFixed(1) : '--', '', ''],
            ...buildOverviewRows('Età', leadDemographicsAgeItems),
            ...buildOverviewRows('Regione', leadDemographicsRegionItems),
            ...buildOverviewRows('Genere', leadDemographicsGenderItems),
            ...buildOverviewRows('Occupazione', leadDemographicsEmploymentItems),
        ];

        const courseHeaders = ['Corso', 'Lead Tipo', 'Cliente Tipo', 'Lead', 'Lead Vinti', 'Conversione', 'Età', 'Provenienza', 'Genere', 'Occupazione'];
        const courseRows = (Array.isArray(leadDemographicsCourseTableData) ? leadDemographicsCourseTableData : []).map((row) => ([
            row?.course_name?.value || 'Corso sconosciuto',
            row?.lead_type?.value || '--',
            row?.customer_type?.value || '--',
            Number(row?.lead_total?.value || 0),
            Number(row?.lead_won_total?.value || 0),
            Number(row?.conversion_rate?.value || 0).toFixed(2),
            row?.age_breakdown?.value || '--',
            row?.region_breakdown?.value || '--',
            row?.gender_breakdown?.value || '--',
            row?.employment_breakdown?.value || '--',
        ]));

        downloadCsv(`lead_demographics_overview_${suffix}.csv`, buildCsvContent(overviewHeaders, overviewRows));
        downloadCsv(`lead_demographics_courses_${suffix}.csv`, buildCsvContent(courseHeaders, courseRows));
    }, [
        filters?.from_date,
        filters?.to_date,
        leadDemographicsSummary,
        leadDemographicsAgeItems,
        leadDemographicsRegionItems,
        leadDemographicsGenderItems,
        leadDemographicsEmploymentItems,
        leadDemographicsCourseTableData,
    ]);

    const handleExportProductLeadCsv = useCallback(() => {
        const rawRows = Array.isArray(analytics?.actual?.product_lead_stats) ? analytics.actual.product_lead_stats : [];
        const visibleRowIds = new Set(filteredProductLeadStatsTableData.map((row) => String(row?.id)));
        const filteredRows = rawRows.filter((item, index) => {
            const rowId = item?.course?.id ?? `course-${index}`;
            return visibleRowIds.has(String(rowId));
        });

        const rangeStart = formatDateAsRomeDay(filters?.from_date) || 'na';
        const rangeEnd = formatDateAsRomeDay(filters?.to_date) || 'na';
        const suffix = `${rangeStart}_${rangeEnd}`;

        const summaryHeaders = ['Corso', 'Lead Totali', 'Lead Vinti', 'Conv %'];
        const summaryRows = filteredRows.map((item) => ([
            item?.course?.name || 'Corso sconosciuto',
            Number(item?.lead?.total || 0),
            Number(item?.lead_won?.total || 0),
            Number(item?.lead_won_rate || 0).toFixed(2),
        ]));

        const detailsHeaders = ['Corso', 'Source', 'Lead', 'Lead %', 'Lead Vinti', 'Lead Vinti %'];
        const detailsRows = filteredRows.flatMap((item) => {
            const courseName = item?.course?.name || 'Corso sconosciuto';
            const leadSources = item?.lead?.source || {};
            const wonSources = item?.lead_won?.source || {};
            const allSources = Array.from(new Set([...Object.keys(leadSources), ...Object.keys(wonSources)]));

            return allSources.map((source) => {
                const leadStats = leadSources[source] || {};
                const wonStats = wonSources[source] || {};
                return [
                    courseName,
                    source,
                    Number(leadStats?.count || 0),
                    Number(leadStats?.percentage || 0).toFixed(2),
                    Number(wonStats?.count || 0),
                    Number(wonStats?.percentage || 0).toFixed(2),
                ];
            });
        });

        downloadCsv(`corsi_lead_summary_${suffix}.csv`, buildCsvContent(summaryHeaders, summaryRows));
        downloadCsv(`corsi_lead_details_${suffix}.csv`, buildCsvContent(detailsHeaders, detailsRows));
    }, [analytics?.actual?.product_lead_stats, filteredProductLeadStatsTableData, filters?.from_date, filters?.to_date]);

    const handleExportSourceLeadCsv = useCallback(() => {
        const rows = Array.isArray(filteredSourceLeadStatsRows) ? filteredSourceLeadStatsRows : [];
        const breakdownLabel = (sourceLeadBreakdown?.name || 'Source').toString();

        const rangeStart = formatDateAsRomeDay(filters?.from_date) || 'na';
        const rangeEnd = formatDateAsRomeDay(filters?.to_date) || 'na';
        const suffix = `${rangeStart}_${rangeEnd}`;

        const summaryHeaders = [breakdownLabel, 'Lead Totali', 'Lead Vinti', 'Conv %'];
        const summaryRows = rows.map((item) => ([
            (item?.value || item?.source || 'direct').toString(),
            Number(item?.lead?.total || 0),
            Number(item?.lead_won?.total || 0),
            Number(item?.lead_won_rate || 0).toFixed(2),
        ]));

        const detailsHeaders = [breakdownLabel, 'Corso', 'Lead', 'Lead %', 'Lead Vinti', 'Lead Vinti %'];
        const detailsRows = rows.flatMap((item) => {
            const breakdownValue = (item?.value || item?.source || 'direct').toString();
            const leadCourses = Array.isArray(item?.lead?.courses) ? item.lead.courses : [];
            const wonCourses = Array.isArray(item?.lead_won?.courses) ? item.lead_won.courses : [];

            const leadByCourseId = leadCourses.reduce((acc, entry, index) => {
                const id = entry?.course?.id ?? `unknown-${index}`;
                acc[String(id)] = entry;
                return acc;
            }, {});

            const wonByCourseId = wonCourses.reduce((acc, entry, index) => {
                const id = entry?.course?.id ?? `unknown-${index}`;
                acc[String(id)] = entry;
                return acc;
            }, {});

            const allCourseIds = Array.from(new Set([...Object.keys(leadByCourseId), ...Object.keys(wonByCourseId)]));

            return allCourseIds.map((courseId) => {
                const leadEntry = leadByCourseId[courseId] || {};
                const wonEntry = wonByCourseId[courseId] || {};
                const courseName = leadEntry?.course?.name || wonEntry?.course?.name || 'Corso sconosciuto';

                return [
                    breakdownValue,
                    courseName,
                    Number(leadEntry?.count || 0),
                    Number(leadEntry?.percentage || 0).toFixed(2),
                    Number(wonEntry?.count || 0),
                    Number(wonEntry?.percentage || 0).toFixed(2),
                ];
            });
        });

        downloadCsv(`source_lead_summary_${suffix}.csv`, buildCsvContent(summaryHeaders, summaryRows));
        downloadCsv(`source_lead_details_${suffix}.csv`, buildCsvContent(detailsHeaders, detailsRows));
    }, [filteredSourceLeadStatsRows, filters?.from_date, filters?.to_date, sourceLeadBreakdown]);

    const referrerTableData = useMemo(() => {
        const rows = Array.isArray(analytics?.actual?.referrer) ? analytics.actual.referrer : [];

        return rows.map((item, index) => {
            const referrer = (item?.value || 'unknown').toString();
            const rowId = `referrer-${referrer}-${index}`;
            const isExpanded = Boolean(expandedReferrerRows[rowId]);
            const coursesBreakdown = Array.isArray(item?.courses) ? item.courses : [];
            const count = Number(item?.count || 0);
            const percentage = Number(item?.percentage || 0);

            return {
                id: rowId,
                referrer: {
                    value: referrer,
                    type: 'string',
                    component: (
                        <div>
                            <div style={{ fontWeight: 600 }}>{referrer}</div>
                            {isExpanded && (
                                <div className={styles.productLeadBreakdown}>
                                    {coursesBreakdown.length === 0 && (
                                        <div className={styles.productLeadEmpty}>Nessun dato corso</div>
                                    )}
                                    {coursesBreakdown.map((entry, entryIndex) => {
                                        const courseId = entry?.course?.id ?? `unknown-${entryIndex}`;
                                        const courseName = entry?.course?.name || 'Corso sconosciuto';
                                        return (
                                            <div key={`${rowId}-${courseId}`} className={styles.productLeadBreakdownRow}>
                                                <span className={styles.productLeadSourceName}>{courseName}</span>
                                                <span className={styles.productLeadSourceStats}>{formatNumber(entry?.count || 0)} • {(entry?.percentage || 0).toFixed(2)}%</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    ),
                },
                count: {
                    value: count,
                    type: 'number',
                    component: <div>{formatNumber(count)}</div>,
                },
                percentage: {
                    value: percentage,
                    type: 'number',
                    component: <div>{percentage.toFixed(2)}%</div>,
                },
            };
        });
    }, [analytics?.actual?.referrer, expandedReferrerRows]);

    const utmTableData = useMemo(() => {
        const rows = Array.isArray(analytics?.actual?.utm?.[utmBreakdownState])
            ? analytics.actual.utm[utmBreakdownState]
            : [];

        return rows.map((item, index) => {
            const utmValue = (item?.value || 'unknown').toString();
            const rowId = `utm-${utmBreakdownState}-${utmValue}-${index}`;
            const isExpanded = Boolean(expandedUtmRows[rowId]);
            const coursesBreakdown = Array.isArray(item?.courses) ? item.courses : [];
            const count = Number(item?.count || 0);
            const percentage = Number(item?.percentage || 0);

            const renderExpandedBreakdown = (renderItem) => (
                <div className={styles.productLeadBreakdown}>
                    {coursesBreakdown.length === 0 && (
                        <div className={styles.productLeadEmpty}>Nessun dato corso</div>
                    )}
                    {coursesBreakdown.map((entry, entryIndex) => {
                        const courseId = entry?.course?.id ?? `unknown-${entryIndex}`;
                        return (
                            <div key={`${rowId}-${courseId}`} className={styles.productLeadBreakdownRow}>
                                <span className={styles.productLeadSourceStats}>{renderItem(entry)}</span>
                            </div>
                        );
                    })}
                </div>
            );

            return {
                id: rowId,
                utm_value: {
                    value: utmValue,
                    type: 'string',
                    component: (
                        <div>
                            <div style={{ fontWeight: 600 }}>{utmValue}</div>
                            {isExpanded && (
                                <div className={styles.productLeadBreakdown}>
                                    {coursesBreakdown.length === 0 && (
                                        <div className={styles.productLeadEmpty}>Nessun dato corso</div>
                                    )}
                                    {coursesBreakdown.map((entry, entryIndex) => {
                                        const courseId = entry?.course?.id ?? `unknown-${entryIndex}`;
                                        const courseName = entry?.course?.name || 'Corso sconosciuto';
                                        return (
                                            <div key={`${rowId}-${courseId}`} className={styles.productLeadBreakdownRow}>
                                                <span className={styles.productLeadSourceName}>{courseName}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    ),
                },
                count: {
                    value: count,
                    type: 'number',
                    component: (
                        <div>
                            <div>{formatNumber(count)}</div>
                            {isExpanded && renderExpandedBreakdown((entry) => formatNumber(entry?.count || 0))}
                        </div>
                    ),
                },
                percentage: {
                    value: percentage,
                    type: 'number',
                    component: (
                        <div>
                            <div>{percentage.toFixed(2)}%</div>
                            {isExpanded && renderExpandedBreakdown((entry) => `${Number(entry?.percentage || 0).toFixed(2)}%`)}
                        </div>
                    ),
                },
            };
        });
    }, [analytics?.actual?.utm, utmBreakdownState, expandedUtmRows]);

    const directDemographicsCourseItems = useMemo(() => {
        return Array.isArray(analytics?.actual?.non_application_course_demographics)
            ? analytics.actual.non_application_course_demographics
            : [];
    }, [analytics?.actual?.non_application_course_demographics]);

    const directDemographicsCourseTableData = useMemo(() => {
        const pickBestSegment = (items = []) => {
            const validRows = (Array.isArray(items) ? items : []).filter((entry) => {
                const label = String(entry?.label || '').trim().toLowerCase();
                return label && label !== 'non disponibile';
            });

            if (validRows.length === 0) {
                return null;
            }

            const sortedRows = [...validRows].sort((a, b) => {
                const countDiff = Number(b?.count || 0) - Number(a?.count || 0);
                if (countDiff !== 0) return countDiff;
                const convDiff = Number(b?.conversion_rate || 0) - Number(a?.conversion_rate || 0);
                if (convDiff !== 0) return convDiff;
                return String(a?.label || '').localeCompare(String(b?.label || ''));
            });

            const topRow = sortedRows[0];
            const topCount = Number(topRow?.count || 0);
            if (topCount <= 0) {
                return null;
            }

            const hasTie = sortedRows.slice(1).some((entry) => Number(entry?.count || 0) === topCount);
            if (hasTie) {
                return null;
            }

            return topRow;
        };

        const buildBuyerTypeLabel = (item) => {
            const bestGender = pickBestSegment(item?.gender);
            const bestAge = pickBestSegment(item?.age);
            const bestEmployment = pickBestSegment(item?.employment);
            const bestRegion = pickBestSegment(item?.regions);

            const genderLabel = formatDemographicLabel(bestGender?.label) || null;
            const ageLabel = formatDemographicLabel(bestAge?.label) || null;
            const employmentLabel = formatDemographicLabel(bestEmployment?.label) || null;
            const normalizedEmploymentLabel = (employmentLabel || '').trim().toLowerCase();
            const includeEmployment = Boolean(employmentLabel && !normalizedEmploymentLabel.startsWith('nessuno'));

            if (!genderLabel || !ageLabel) {
                return '--';
            }

            const profileParts = [
                genderLabel,
                ageLabel,
                includeEmployment ? employmentLabel : null,
            ].filter(Boolean);

            return `${profileParts.join(' ')}${bestRegion?.label ? `, ${formatDemographicLabel(bestRegion.label)}` : ''}`;
        };

        return directDemographicsCourseItems.map((item, index) => {
            const courseId = item?.course?.id ?? `direct-demographic-${index}`;
            const courseName = item?.course?.name || 'Corso sconosciuto';
            const buyerTypeLabel = buildBuyerTypeLabel(item);

            return {
                id: String(courseId),
                course_name: {
                    value: courseName,
                    type: 'string',
                    component: <div>{courseName}</div>,
                },
                buyer_type: {
                    value: buyerTypeLabel,
                    type: 'string',
                    component: <div className={styles.demographicBreakdownCell}>{buyerTypeLabel}</div>,
                },
                purchases: {
                    value: Number(item?.purchases || item?.lead_won_total || 0),
                    type: 'number',
                    component: <div>{formatNumber(item?.purchases || item?.lead_won_total || 0)}</div>,
                },
                age_breakdown: {
                    value: (item?.age || []).map((entry) => `${entry?.label || 'N/D'} ${formatPercentage(entry?.percentage)}`).join(' | '),
                    type: 'string',
                    component: renderBreakdownColumn(item?.age),
                },
                region_breakdown: {
                    value: (item?.regions || []).map((entry) => `${entry?.label || 'N/D'} ${formatPercentage(entry?.percentage)}`).join(' | '),
                    type: 'string',
                    component: renderBreakdownColumn(item?.regions),
                },
                gender_breakdown: {
                    value: (item?.gender || []).map((entry) => `${entry?.label || 'N/D'} ${formatPercentage(entry?.percentage)}`).join(' | '),
                    type: 'string',
                    component: renderBreakdownColumn(item?.gender),
                },
                employment_breakdown: {
                    value: (item?.employment || []).map((entry) => `${entry?.label || 'N/D'} ${formatPercentage(entry?.percentage)}`).join(' | '),
                    type: 'string',
                    component: renderBreakdownColumn(item?.employment),
                },
            };
        });
    }, [directDemographicsCourseItems, renderBreakdownColumn]);

    const filteredDirectDemographicsCourseTableData = useMemo(() => {
        const normalizedSearch = directDemographicsSearch.trim().toLowerCase();
        const selectedType = directDemographicsType?.value;
        const selectedCategory = directDemographicsCategory?.value;

        return directDemographicsCourseTableData.filter((row) => {
            const courseName = (row?.course_name?.value || '').toString();
            const courseMeta = coursesById[String(row?.id)];

            if (selectedType && String(courseMeta?.type) !== String(selectedType)) {
                return false;
            }

            if (selectedCategory && String(courseMeta?.category) !== String(selectedCategory)) {
                return false;
            }

            if (!normalizedSearch) {
                return true;
            }

            return courseName.toLowerCase().includes(normalizedSearch);
        });
    }, [directDemographicsCourseTableData, directDemographicsSearch, directDemographicsType, directDemographicsCategory, coursesById]);

    const nonApplicationCourseSalesTableData = useMemo(() => {
        const rows = Array.isArray(analytics?.actual?.non_application_course_sales) ? analytics.actual.non_application_course_sales : [];

        return rows.map((item, index) => {
            const courseName = item?.course?.name || 'Corso sconosciuto';
            const courseId = item?.course?.id;
            const rowId = item?.course?.id ?? `non-application-course-${index}`;
            const courseMeta = courseId !== null && courseId !== undefined ? coursesById[String(courseId)] : null;
            const isPack = String(courseMeta?.product?.type || '').toLowerCase() === 'pack';
            const uniqueUsers = Number(item?.unique_users || 0);
            const purchases = Number(item?.purchases || 0);
            const soldTotal = Number(item?.sold_total || 0);
            const conversionRate = Number(item?.conversion_rate || 0);
            const isExpanded = Boolean(expandedDirectPackRows[rowId]);
            const isLoading = Boolean(directPackBreakdownLoadingByRow[rowId]);
            const breakdownError = directPackBreakdownErrorByRow[rowId];
            const breakdown = directPackBreakdownByRow[rowId];

            return {
                id: rowId,
                product_id: courseMeta?.product?.id || null,
                is_pack: isPack,
                course_name: {
                    value: courseName,
                    type: 'string',
                    component: <div>{courseName}{isPack ? ' (Pack - clicca per dettaglio)' : ''}</div>,
                },
                unique_users: {
                    value: uniqueUsers,
                    type: 'number',
                    component: <div>{formatNumber(uniqueUsers)}</div>,
                },
                purchases: {
                    value: purchases,
                    type: 'number',
                    component: <div>{formatNumber(purchases)}</div>,
                },
                sold_total: {
                    value: soldTotal,
                    type: 'number',
                    component: (
                        <div>
                            <div>{formatCurrencyFromCents(soldTotal)}</div>
                            {isPack && isExpanded && (
                                <div style={{ marginTop: '.5rem', fontSize: '.85rem', lineHeight: 1.35 }}>
                                    {isLoading && <div>Caricamento dettaglio...</div>}
                                    {!isLoading && breakdownError && <div style={{ color: 'var(--sf-red)' }}>{breakdownError}</div>}
                                    {!isLoading && !breakdownError && breakdown && (
                                        <>
                                            {(breakdown.components || []).map((component) => (
                                                <div key={`${rowId}-${component.product_id}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '.75rem' }}>
                                                    <span>{component.product_name}</span>
                                                    <span>{((component.weight || 0) * 100).toFixed(2)}% • <strong>{formatCurrencyFromCents(component.allocated_amount_cents || 0)}</strong></span>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    ),
                },
                conversion_rate: {
                    value: conversionRate,
                    type: 'number',
                    component: (
                        <div className={`${styles.productLeadConversionBadge} ${conversionRate >= 20 ? styles.high : conversionRate >= 10 ? styles.medium : styles.low}`}>
                            {conversionRate.toFixed(2)}%
                        </div>
                    ),
                },
            };
        });
    }, [
        analytics?.actual?.non_application_course_sales,
        coursesById,
        expandedDirectPackRows,
        directPackBreakdownByRow,
        directPackBreakdownLoadingByRow,
        directPackBreakdownErrorByRow,
    ]);

    const onDirectRevenueRowClick = useCallback(async (row) => {
        if (!row?.is_pack || !row?.product_id) {
            return;
        }

        const rowId = row.id;
        if (expandedDirectPackRows[rowId]) {
            setExpandedDirectPackRows((prev) => ({ ...prev, [rowId]: false }));
            return;
        }

        setExpandedDirectPackRows((prev) => ({ ...prev, [rowId]: true }));
        if (directPackBreakdownByRow[rowId] || directPackBreakdownLoadingByRow[rowId]) {
            return;
        }

        setDirectPackBreakdownLoadingByRow((prev) => ({ ...prev, [rowId]: true }));
        setDirectPackBreakdownErrorByRow((prev) => ({ ...prev, [rowId]: null }));
        try {
            const params = new URLSearchParams({
                product_id: String(row.product_id),
            });

            const startDate = formatDateAsRomeDay(filters?.from_date);
            const endDate = formatDateAsRomeDay(filters?.to_date);
            if (startDate) params.append('start_date', startDate);
            if (endDate) params.append('end_date', endDate);

            const response = await api.get(`/admin/reports/sales/direct-pack-breakdown?${params.toString()}`);
            if (!response?.is_pack) {
                setDirectPackBreakdownErrorByRow((prev) => ({ ...prev, [rowId]: 'Il prodotto selezionato non e un pack.' }));
                return;
            }

            setDirectPackBreakdownByRow((prev) => ({ ...prev, [rowId]: response }));
        } catch (error) {
            console.error(error);
            setDirectPackBreakdownErrorByRow((prev) => ({ ...prev, [rowId]: 'Impossibile caricare il dettaglio pack.' }));
        } finally {
            setDirectPackBreakdownLoadingByRow((prev) => ({ ...prev, [rowId]: false }));
        }
    }, [
        filters?.from_date,
        filters?.to_date,
        expandedDirectPackRows,
        directPackBreakdownByRow,
        directPackBreakdownLoadingByRow,
    ]);

    const nonApplicationCourseSalesUtmTableData = useMemo(() => {
        const rows = Array.isArray(analytics?.actual?.non_application_course_sales_utm?.[directSalesUtmBreakdownState])
            ? analytics.actual.non_application_course_sales_utm[directSalesUtmBreakdownState]
            : [];

        return rows.map((item, index) => {
            const utmValue = (item?.value || 'unknown').toString();
            const rowId = `direct-sales-utm-${directSalesUtmBreakdownState}-${utmValue}-${index}`;
            const isExpanded = Boolean(expandedDirectSalesUtmRows[rowId]);
            const purchases = Number(item?.purchases || 0);
            const soldTotal = Number(item?.sold_total || 0);
            const purchasesPct = Number(item?.percentage_purchases || 0);
            const courses = Array.isArray(item?.courses) ? item.courses : [];

            const handleToggle = (ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                toggleDirectSalesUtmRow(rowId);
            };

            return {
                id: rowId,
                utm_value: {
                    value: utmValue,
                    type: 'string',
                    component: (
                        <div>
                            <details open={isExpanded} onClick={(ev) => ev.stopPropagation()} className={styles.productLeadDetails}>
                                <summary onClick={handleToggle} className={styles.productLeadSummary}>
                                    <div className={styles.productLeadSummaryMain}>{utmValue}</div>
                                </summary>
                                <div className={styles.productLeadBreakdown}>
                                    {courses.length === 0 && (
                                        <div className={styles.productLeadEmpty}>
                                            Nessun dettaglio corso
                                        </div>
                                    )}
                                    {courses.map((entry) => {
                                        const courseId = entry?.course?.id ?? 'na';
                                        const courseName = entry?.course?.name || 'Corso sconosciuto';
                                        const coursePurchases = Number(entry?.purchases || 0);
                                        const courseValue = Number(entry?.sold_total || 0);
                                        return (
                                            <div key={`${rowId}-${courseId}`} className={styles.productLeadBreakdownRow}>
                                                <span className={styles.productLeadSourceName}>{courseName}</span>
                                                <span className={styles.productLeadSourceStats}>{formatNumber(coursePurchases)} • {formatCurrencyFromCents(courseValue)}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </details>
                        </div>
                    ),
                },
                purchases: {
                    value: purchases,
                    type: 'number',
                    component: <div>{formatNumber(purchases)}</div>,
                },
                sold_total: {
                    value: soldTotal,
                    type: 'number',
                    component: <div>{formatCurrencyFromCents(soldTotal)}</div>,
                },
                purchases_share: {
                    value: purchasesPct,
                    type: 'number',
                    component: <div>{purchasesPct.toFixed(2)}%</div>,
                },
            };
        });
    }, [analytics?.actual?.non_application_course_sales_utm, directSalesUtmBreakdownState, expandedDirectSalesUtmRows, toggleDirectSalesUtmRow]);

    const handleExportNonApplicationCourseSalesCsv = useCallback(() => {
        const rows = Array.isArray(analytics?.actual?.non_application_course_sales)
            ? analytics.actual.non_application_course_sales
            : [];

        const rangeStart = formatDateAsRomeDay(filters?.from_date) || 'na';
        const rangeEnd = formatDateAsRomeDay(filters?.to_date) || 'na';
        const suffix = `${rangeStart}_${rangeEnd}`;

        const headers = ['Corso', 'Utenti Unici', 'Acquisti', 'Totale Venduto (cent)', 'Conv %'];
        const csvRows = rows.map((item) => ([
            item?.course?.name || 'Corso sconosciuto',
            Number(item?.unique_users || 0),
            Number(item?.purchases || 0),
            Number(item?.sold_total || 0),
            Number(item?.conversion_rate || 0).toFixed(2),
        ]));

        downloadCsv(`acquisti_diretti_${suffix}.csv`, buildCsvContent(headers, csvRows));
    }, [analytics?.actual?.non_application_course_sales, filters?.from_date, filters?.to_date]);

    const handleExportNonApplicationCourseDemographicsCsv = useCallback(() => {
        const rows = Array.isArray(filteredDirectDemographicsCourseTableData) ? filteredDirectDemographicsCourseTableData : [];

        const rangeStart = formatDateAsRomeDay(filters?.from_date) || 'na';
        const rangeEnd = formatDateAsRomeDay(filters?.to_date) || 'na';
        const suffix = `${rangeStart}_${rangeEnd}`;

        const headers = ['Corso', 'Acquirente Tipo', 'Acquisti', 'Eta', 'Provenienza', 'Genere', 'Occupazione'];
        const csvRows = rows.map((row) => ([
            row?.course_name?.value || 'Corso sconosciuto',
            row?.buyer_type?.value || '--',
            Number(row?.purchases?.value || 0),
            row?.age_breakdown?.value || '--',
            row?.region_breakdown?.value || '--',
            row?.gender_breakdown?.value || '--',
            row?.employment_breakdown?.value || '--',
        ]));

        downloadCsv(`acquisti_diretti_breakdown_${suffix}.csv`, buildCsvContent(headers, csvRows));
    }, [filteredDirectDemographicsCourseTableData, filters?.from_date, filters?.to_date]);

    const behavioralStatsCoursesTableData = useMemo(() => {
        const rows = Array.isArray(analytics?.actual?.behavioral_stats?.courses)
            ? analytics.actual.behavioral_stats.courses
            : [];
        const previousRows = Array.isArray(analytics?.previous?.behavioral_stats?.courses)
            ? analytics.previous.behavioral_stats.courses
            : [];

        const previousByCourse = previousRows.reduce((acc, course) => {
            const key = `${course?.object_type || 'course'}:${course?.id}`;
            acc[key] = course;
            return acc;
        }, {});

        const buildMetricCell = (actualValue, previousValue, formatter) => {
            const delta = calculatePercentageDifference(actualValue, previousValue);
            const showDelta = Number.isFinite(delta) && Math.abs(delta) > 0;

            return (
                <div>
                    <div>{formatter(actualValue)}</div>
                    {showDelta && (
                        <div style={{ fontSize: '0.75rem', color: delta > 0 ? 'var(--sf-green)' : 'var(--sf-red)' }}>
                            {delta > 0 ? '+' : ''}{delta.toFixed(2)}%
                        </div>
                    )}
                </div>
            );
        };

        const courseRows = rows.map((item, index) => {
            const courseId = item?.id ?? `behavioral-course-${index}`;
            const courseName = item?.name || 'Corso sconosciuto';
            const objectType = item?.object_type || 'course';
            const previousItem = previousByCourse[`${objectType}:${item?.id}`] || {};

            const pageViewsTotal = Number(item?.page_views_total || 0);
            const uniqueUsersTotal = Number(item?.unique_users_total || 0);
            const uniqueSessions = Number(item?.unique_sessions || 0);
            const viewLead = Number(item?.view_lead || 0);
            const closeLead = Number(item?.close_lead || 0);
            const generateLead = Number(item?.generate_lead || 0);
            const checkoutStart = Number(item?.checkout_start || 0);
            const checkoutFill = Number(item?.checkout_fill || 0);
            const purchase = Number(item?.purchase || 0);
            const avgTimeOnCourseSeconds = Number(item?.avg_time_on_course_seconds || 0);
            const medianTimeOnCourseSeconds = Number(item?.median_time_on_course_seconds || 0);

            return {
                id: courseId,
                course_name: {
                    value: courseName,
                    type: 'string',
                    component: <div>{courseName}</div>,
                },
                object_type: {
                    value: objectType,
                    type: 'string',
                    component: <div>{objectType}</div>,
                },
                page_views_total: {
                    value: pageViewsTotal,
                    previousValue: Number(previousItem?.page_views_total || 0),
                    type: 'number',
                    component: buildMetricCell(pageViewsTotal, Number(previousItem?.page_views_total || 0), formatNumber),
                },
                unique_users_total: {
                    value: uniqueUsersTotal,
                    previousValue: Number(previousItem?.unique_users_total || 0),
                    type: 'number',
                    component: buildMetricCell(uniqueUsersTotal, Number(previousItem?.unique_users_total || 0), formatNumber),
                },
                unique_sessions: {
                    value: uniqueSessions,
                    previousValue: Number(previousItem?.unique_sessions || 0),
                    type: 'number',
                    component: buildMetricCell(uniqueSessions, Number(previousItem?.unique_sessions || 0), formatNumber),
                },
                view_lead: {
                    value: viewLead,
                    previousValue: Number(previousItem?.view_lead || 0),
                    type: 'number',
                    component: buildMetricCell(viewLead, Number(previousItem?.view_lead || 0), formatNumber),
                },
                close_lead: {
                    value: closeLead,
                    previousValue: Number(previousItem?.close_lead || 0),
                    type: 'number',
                    component: buildMetricCell(closeLead, Number(previousItem?.close_lead || 0), formatNumber),
                },
                generate_lead: {
                    value: generateLead,
                    previousValue: Number(previousItem?.generate_lead || 0),
                    type: 'number',
                    component: buildMetricCell(generateLead, Number(previousItem?.generate_lead || 0), formatNumber),
                },
                checkout_start: {
                    value: checkoutStart,
                    previousValue: Number(previousItem?.checkout_start || 0),
                    type: 'number',
                    component: buildMetricCell(checkoutStart, Number(previousItem?.checkout_start || 0), formatNumber),
                },
                checkout_fill: {
                    value: checkoutFill,
                    previousValue: Number(previousItem?.checkout_fill || 0),
                    type: 'number',
                    component: buildMetricCell(checkoutFill, Number(previousItem?.checkout_fill || 0), formatNumber),
                },
                purchase: {
                    value: purchase,
                    previousValue: Number(previousItem?.purchase || 0),
                    type: 'number',
                    component: buildMetricCell(purchase, Number(previousItem?.purchase || 0), formatNumber),
                },
                avg_time_on_course_seconds: {
                    value: avgTimeOnCourseSeconds,
                    previousValue: Number(previousItem?.avg_time_on_course_seconds || 0),
                    type: 'number',
                    component: buildMetricCell(avgTimeOnCourseSeconds, Number(previousItem?.avg_time_on_course_seconds || 0), formatSeconds),
                },
                median_time_on_course_seconds: {
                    value: medianTimeOnCourseSeconds,
                    previousValue: Number(previousItem?.median_time_on_course_seconds || 0),
                    type: 'number',
                    component: buildMetricCell(medianTimeOnCourseSeconds, Number(previousItem?.median_time_on_course_seconds || 0), formatSeconds),
                },
            };
        });

        return courseRows;
    }, [
        analytics?.actual?.behavioral_stats?.courses,
        analytics?.previous?.behavioral_stats?.courses,
    ]);

    const filteredBehavioralStatsCoursesTableData = useMemo(() => {
        const normalizedSearch = behavioralCourseSearch.trim().toLowerCase();
        const selectedType = behavioralCourseType?.value;
        const selectedCategory = behavioralCourseCategory?.value;
        const buildMetricCell = (actualValue, previousValue, formatter) => {
            const delta = calculatePercentageDifference(actualValue, previousValue);
            const showDelta = Number.isFinite(delta) && Math.abs(delta) > 0;

            return (
                <div>
                    <div>{formatter(actualValue)}</div>
                    {showDelta && (
                        <div style={{ fontSize: '0.75rem', color: delta > 0 ? 'var(--sf-green)' : 'var(--sf-red)' }}>
                            {delta > 0 ? '+' : ''}{delta.toFixed(2)}%
                        </div>
                    )}
                </div>
            );
        };

        const filteredCourseRows = behavioralStatsCoursesTableData.filter((row) => {
            if (!row || row?.id === 'all-selected-courses') {
                return false;
            }

            const courseName = (row?.course_name?.value || '').toString().toLowerCase();
            const courseMeta = coursesById[String(row?.id)];

            if (selectedType && String(courseMeta?.type) !== String(selectedType)) {
                return false;
            }

            if (selectedCategory && String(courseMeta?.category) !== String(selectedCategory)) {
                return false;
            }

            if (!normalizedSearch) {
                return true;
            }

            return courseName.includes(normalizedSearch);
        });

        const aggregateMetrics = filteredCourseRows.reduce((acc, row) => {
            const uniqueSessions = Number(row?.unique_sessions?.value || 0);
            const previousUniqueSessions = Number(row?.unique_sessions?.previousValue || 0);

            acc.page_views_total += Number(row?.page_views_total?.value || 0);
            acc.previous_page_views_total += Number(row?.page_views_total?.previousValue || 0);
            acc.unique_users_total += Number(row?.unique_users_total?.value || 0);
            acc.previous_unique_users_total += Number(row?.unique_users_total?.previousValue || 0);
            acc.unique_sessions += uniqueSessions;
            acc.previous_unique_sessions += previousUniqueSessions;
            acc.view_lead += Number(row?.view_lead?.value || 0);
            acc.previous_view_lead += Number(row?.view_lead?.previousValue || 0);
            acc.close_lead += Number(row?.close_lead?.value || 0);
            acc.previous_close_lead += Number(row?.close_lead?.previousValue || 0);
            acc.generate_lead += Number(row?.generate_lead?.value || 0);
            acc.previous_generate_lead += Number(row?.generate_lead?.previousValue || 0);
            acc.checkout_start += Number(row?.checkout_start?.value || 0);
            acc.previous_checkout_start += Number(row?.checkout_start?.previousValue || 0);
            acc.checkout_fill += Number(row?.checkout_fill?.value || 0);
            acc.previous_checkout_fill += Number(row?.checkout_fill?.previousValue || 0);
            acc.purchase += Number(row?.purchase?.value || 0);
            acc.previous_purchase += Number(row?.purchase?.previousValue || 0);

            if (uniqueSessions > 0) {
                acc.avg_time_weighted_sum += Number(row?.avg_time_on_course_seconds?.value || 0) * uniqueSessions;
                acc.median_time_weighted_sum += Number(row?.median_time_on_course_seconds?.value || 0) * uniqueSessions;
                acc.time_weight += uniqueSessions;
            }

            if (previousUniqueSessions > 0) {
                acc.previous_avg_time_weighted_sum += Number(row?.avg_time_on_course_seconds?.previousValue || 0) * previousUniqueSessions;
                acc.previous_median_time_weighted_sum += Number(row?.median_time_on_course_seconds?.previousValue || 0) * previousUniqueSessions;
                acc.previous_time_weight += previousUniqueSessions;
            }

            return acc;
        }, {
            page_views_total: 0,
            previous_page_views_total: 0,
            unique_users_total: 0,
            previous_unique_users_total: 0,
            unique_sessions: 0,
            previous_unique_sessions: 0,
            view_lead: 0,
            previous_view_lead: 0,
            close_lead: 0,
            previous_close_lead: 0,
            generate_lead: 0,
            previous_generate_lead: 0,
            checkout_start: 0,
            previous_checkout_start: 0,
            checkout_fill: 0,
            previous_checkout_fill: 0,
            purchase: 0,
            previous_purchase: 0,
            avg_time_weighted_sum: 0,
            median_time_weighted_sum: 0,
            time_weight: 0,
            previous_avg_time_weighted_sum: 0,
            previous_median_time_weighted_sum: 0,
            previous_time_weight: 0,
        });

        const aggregateAvgTime = aggregateMetrics.time_weight > 0
            ? aggregateMetrics.avg_time_weighted_sum / aggregateMetrics.time_weight
            : 0;
        const aggregateMedianTime = aggregateMetrics.time_weight > 0
            ? aggregateMetrics.median_time_weighted_sum / aggregateMetrics.time_weight
            : 0;
        const previousAggregateAvgTime = aggregateMetrics.previous_time_weight > 0
            ? aggregateMetrics.previous_avg_time_weighted_sum / aggregateMetrics.previous_time_weight
            : 0;
        const previousAggregateMedianTime = aggregateMetrics.previous_time_weight > 0
            ? aggregateMetrics.previous_median_time_weighted_sum / aggregateMetrics.previous_time_weight
            : 0;

        const aggregateRow = {
            id: 'all-selected-courses',
            course_name: {
                value: 'Tutti i corsi selezionati',
                type: 'string',
                component: <div style={{ fontWeight: 600 }}>Tutti i corsi selezionati</div>,
            },
            object_type: {
                value: 'all',
                type: 'string',
                component: <div style={{ fontWeight: 600 }}>all</div>,
            },
            page_views_total: {
                value: aggregateMetrics.page_views_total,
                type: 'number',
                component: buildMetricCell(aggregateMetrics.page_views_total, aggregateMetrics.previous_page_views_total, formatNumber),
            },
            unique_users_total: {
                value: aggregateMetrics.unique_users_total,
                type: 'number',
                component: buildMetricCell(aggregateMetrics.unique_users_total, aggregateMetrics.previous_unique_users_total, formatNumber),
            },
            unique_sessions: {
                value: aggregateMetrics.unique_sessions,
                type: 'number',
                component: buildMetricCell(aggregateMetrics.unique_sessions, aggregateMetrics.previous_unique_sessions, formatNumber),
            },
            view_lead: {
                value: aggregateMetrics.view_lead,
                type: 'number',
                component: buildMetricCell(aggregateMetrics.view_lead, aggregateMetrics.previous_view_lead, formatNumber),
            },
            close_lead: {
                value: aggregateMetrics.close_lead,
                type: 'number',
                component: buildMetricCell(aggregateMetrics.close_lead, aggregateMetrics.previous_close_lead, formatNumber),
            },
            generate_lead: {
                value: aggregateMetrics.generate_lead,
                type: 'number',
                component: buildMetricCell(aggregateMetrics.generate_lead, aggregateMetrics.previous_generate_lead, formatNumber),
            },
            checkout_start: {
                value: aggregateMetrics.checkout_start,
                type: 'number',
                component: buildMetricCell(aggregateMetrics.checkout_start, aggregateMetrics.previous_checkout_start, formatNumber),
            },
            checkout_fill: {
                value: aggregateMetrics.checkout_fill,
                type: 'number',
                component: buildMetricCell(aggregateMetrics.checkout_fill, aggregateMetrics.previous_checkout_fill, formatNumber),
            },
            purchase: {
                value: aggregateMetrics.purchase,
                type: 'number',
                component: buildMetricCell(aggregateMetrics.purchase, aggregateMetrics.previous_purchase, formatNumber),
            },
            avg_time_on_course_seconds: {
                value: aggregateAvgTime,
                type: 'number',
                component: buildMetricCell(aggregateAvgTime, previousAggregateAvgTime, formatSeconds),
            },
            median_time_on_course_seconds: {
                value: aggregateMedianTime,
                type: 'number',
                component: buildMetricCell(aggregateMedianTime, previousAggregateMedianTime, formatSeconds),
            },
        };

        return [aggregateRow, ...filteredCourseRows];
    }, [behavioralStatsCoursesTableData, behavioralCourseSearch, behavioralCourseType, behavioralCourseCategory, coursesById]);

    const handleExportBehavioralStatsCsv = useCallback(() => {
        const rows = Array.isArray(filteredBehavioralStatsCoursesTableData) ? filteredBehavioralStatsCoursesTableData : [];

        const rangeStart = formatDateAsRomeDay(filters?.from_date) || 'na';
        const rangeEnd = formatDateAsRomeDay(filters?.to_date) || 'na';
        const suffix = `${rangeStart}_${rangeEnd}`;

        const headers = [
            'Corso',
            'Views',
            'Utenti',
            'Sessioni',
            'Aperture',
            'Chiusure',
            'Lead Gen',
            'Chk Start',
            'Chk Fill',
            'Purchase',
            'Tempo Medio (s)',
            'Tempo Mediano (s)',
        ];

        const mapRow = (row) => ([
            row?.course_name?.value || '',
            Number(row?.page_views_total?.value || 0),
            Number(row?.unique_users_total?.value || 0),
            Number(row?.unique_sessions?.value || 0),
            Number(row?.view_lead?.value || 0),
            Number(row?.close_lead?.value || 0),
            Number(row?.generate_lead?.value || 0),
            Number(row?.checkout_start?.value || 0),
            Number(row?.checkout_fill?.value || 0),
            Number(row?.purchase?.value || 0),
            Number(row?.avg_time_on_course_seconds?.value || 0).toFixed(2),
            Number(row?.median_time_on_course_seconds?.value || 0).toFixed(2),
        ]);

        const allRows = rows.map(mapRow);

        downloadCsv(`behavioral_stats_${suffix}.csv`, buildCsvContent(headers, allRows));
    }, [filteredBehavioralStatsCoursesTableData, filters?.from_date, filters?.to_date]);

    const behavioralStatsSourcesTableData = useMemo(() => {
        const actualRows = Array.isArray(analytics?.actual?.behavioral_stats?.utm?.[behavioralBreakdownKey])
            ? analytics.actual.behavioral_stats.utm[behavioralBreakdownKey]
            : [];
        const previousRows = Array.isArray(analytics?.previous?.behavioral_stats?.utm?.[behavioralBreakdownKey])
            ? analytics.previous.behavioral_stats.utm[behavioralBreakdownKey]
            : [];

        const previousByValue = previousRows.reduce((acc, row) => {
            const key = String(row?.value || 'unknown').trim().toLowerCase() || 'unknown';
            acc[key] = row;
            return acc;
        }, {});

        const buildMetricCell = (actualValue, previousValue, formatter) => {
            const delta = calculatePercentageDifference(actualValue, previousValue);
            const showDelta = Number.isFinite(delta) && Math.abs(delta) > 0;

            return (
                <div>
                    <div>{formatter(actualValue)}</div>
                    {showDelta && (
                        <div style={{ fontSize: '0.75rem', color: delta > 0 ? 'var(--sf-green)' : 'var(--sf-red)' }}>
                            {delta > 0 ? '+' : ''}{delta.toFixed(2)}%
                        </div>
                    )}
                </div>
            );
        };

        return actualRows.map((item, index) => {
            const label = String(item?.value || 'unknown').trim() || 'unknown';
            const rowId = `behavioral-utm-${behavioralBreakdownKey}-${label}-${index}`;
            const previousItem = previousByValue[label.toLowerCase()] || {};

            const pageViewsTotal = Number(item?.page_views_total || 0);
            const uniqueUsersTotal = Number(item?.unique_users_total || 0);
            const uniqueSessions = Number(item?.unique_sessions || 0);
            const viewLead = Number(item?.view_lead || 0);
            const closeLead = Number(item?.close_lead || 0);
            const generateLead = Number(item?.generate_lead || 0);
            const checkoutStart = Number(item?.checkout_start || 0);
            const checkoutFill = Number(item?.checkout_fill || 0);
            const purchase = Number(item?.purchase || 0);
            const avgTimeOnCourseSeconds = Number(item?.avg_time_on_course_seconds || 0);
            const medianTimeOnCourseSeconds = Number(item?.median_time_on_course_seconds || 0);

            return {
                id: rowId,
                utm_value: {
                    value: label,
                    type: 'string',
                    component: <div>{label}</div>,
                },
                page_views_total: {
                    value: pageViewsTotal,
                    previousValue: Number(previousItem?.page_views_total || 0),
                    type: 'number',
                    component: buildMetricCell(pageViewsTotal, Number(previousItem?.page_views_total || 0), formatNumber),
                },
                unique_users_total: {
                    value: uniqueUsersTotal,
                    previousValue: Number(previousItem?.unique_users_total || 0),
                    type: 'number',
                    component: buildMetricCell(uniqueUsersTotal, Number(previousItem?.unique_users_total || 0), formatNumber),
                },
                unique_sessions: {
                    value: uniqueSessions,
                    previousValue: Number(previousItem?.unique_sessions || 0),
                    type: 'number',
                    component: buildMetricCell(uniqueSessions, Number(previousItem?.unique_sessions || 0), formatNumber),
                },
                view_lead: {
                    value: viewLead,
                    previousValue: Number(previousItem?.view_lead || 0),
                    type: 'number',
                    component: buildMetricCell(viewLead, Number(previousItem?.view_lead || 0), formatNumber),
                },
                close_lead: {
                    value: closeLead,
                    previousValue: Number(previousItem?.close_lead || 0),
                    type: 'number',
                    component: buildMetricCell(closeLead, Number(previousItem?.close_lead || 0), formatNumber),
                },
                generate_lead: {
                    value: generateLead,
                    previousValue: Number(previousItem?.generate_lead || 0),
                    type: 'number',
                    component: buildMetricCell(generateLead, Number(previousItem?.generate_lead || 0), formatNumber),
                },
                checkout_start: {
                    value: checkoutStart,
                    previousValue: Number(previousItem?.checkout_start || 0),
                    type: 'number',
                    component: buildMetricCell(checkoutStart, Number(previousItem?.checkout_start || 0), formatNumber),
                },
                checkout_fill: {
                    value: checkoutFill,
                    previousValue: Number(previousItem?.checkout_fill || 0),
                    type: 'number',
                    component: buildMetricCell(checkoutFill, Number(previousItem?.checkout_fill || 0), formatNumber),
                },
                purchase: {
                    value: purchase,
                    previousValue: Number(previousItem?.purchase || 0),
                    type: 'number',
                    component: buildMetricCell(purchase, Number(previousItem?.purchase || 0), formatNumber),
                },
                avg_time_on_course_seconds: {
                    value: avgTimeOnCourseSeconds,
                    previousValue: Number(previousItem?.avg_time_on_course_seconds || 0),
                    type: 'number',
                    component: buildMetricCell(avgTimeOnCourseSeconds, Number(previousItem?.avg_time_on_course_seconds || 0), formatSeconds),
                },
                median_time_on_course_seconds: {
                    value: medianTimeOnCourseSeconds,
                    previousValue: Number(previousItem?.median_time_on_course_seconds || 0),
                    type: 'number',
                    component: buildMetricCell(medianTimeOnCourseSeconds, Number(previousItem?.median_time_on_course_seconds || 0), formatSeconds),
                },
            };
        });
    }, [
        analytics?.actual?.behavioral_stats?.utm,
        analytics?.previous?.behavioral_stats?.utm,
        behavioralBreakdownKey,
    ]);

    const handleExportBehavioralStatsSourcesCsv = useCallback(() => {
        const rows = Array.isArray(behavioralStatsSourcesTableData) ? behavioralStatsSourcesTableData : [];

        const rangeStart = formatDateAsRomeDay(filters?.from_date) || 'na';
        const rangeEnd = formatDateAsRomeDay(filters?.to_date) || 'na';
        const suffix = `${rangeStart}_${rangeEnd}`;

        const headers = [
            behavioralBreakdownKey.toUpperCase(),
            'Views',
            'Utenti',
            'Sessioni',
            'Aperture',
            'Chiusure',
            'Lead Gen',
            'Chk Start',
            'Chk Fill',
            'Purchase',
            'Tempo Medio (s)',
            'Tempo Mediano (s)',
        ];

        const allRows = rows.map((row) => ([
            row?.utm_value?.value || '',
            Number(row?.page_views_total?.value || 0),
            Number(row?.unique_users_total?.value || 0),
            Number(row?.unique_sessions?.value || 0),
            Number(row?.view_lead?.value || 0),
            Number(row?.close_lead?.value || 0),
            Number(row?.generate_lead?.value || 0),
            Number(row?.checkout_start?.value || 0),
            Number(row?.checkout_fill?.value || 0),
            Number(row?.purchase?.value || 0),
            Number(row?.avg_time_on_course_seconds?.value || 0).toFixed(2),
            Number(row?.median_time_on_course_seconds?.value || 0).toFixed(2),
        ]));

        downloadCsv(`behavioral_stats_${behavioralBreakdownKey}_${suffix}.csv`, buildCsvContent(headers, allRows));
    }, [behavioralStatsSourcesTableData, behavioralBreakdownKey, filters?.from_date, filters?.to_date]);
    const handleExportReferrerCsv = useCallback(() => {
        const rows = Array.isArray(analytics?.actual?.referrer) ? analytics.actual.referrer : [];

        const rangeStart = formatDateAsRomeDay(filters?.from_date) || 'na';
        const rangeEnd = formatDateAsRomeDay(filters?.to_date) || 'na';
        const suffix = `${rangeStart}_${rangeEnd}`;

        const summaryHeaders = ['Referrer', 'Views', 'Percentuale'];
        const summaryRows = rows.map((item) => ([
            (item?.value || 'unknown').toString(),
            Number(item?.count || 0),
            Number(item?.percentage || 0).toFixed(2),
        ]));

        const detailsHeaders = ['Referrer', 'Corso', 'Views', 'Percentuale'];
        const detailsRows = rows.flatMap((item) => {
            const referrer = (item?.value || 'unknown').toString();
            const courses = Array.isArray(item?.courses) ? item.courses : [];
            return courses.map((entry) => ([
                referrer,
                entry?.course?.name || 'Corso sconosciuto',
                Number(entry?.count || 0),
                Number(entry?.percentage || 0).toFixed(2),
            ]));
        });

        downloadCsv(`referrer_summary_${suffix}.csv`, buildCsvContent(summaryHeaders, summaryRows));
        downloadCsv(`referrer_details_${suffix}.csv`, buildCsvContent(detailsHeaders, detailsRows));
    }, [analytics?.actual?.referrer, filters?.from_date, filters?.to_date]);

    const handleExportUtmCsv = useCallback(() => {
        const rows = Array.isArray(analytics?.actual?.utm?.[utmBreakdownState])
            ? analytics.actual.utm[utmBreakdownState]
            : [];

        const rangeStart = formatDateAsRomeDay(filters?.from_date) || 'na';
        const rangeEnd = formatDateAsRomeDay(filters?.to_date) || 'na';
        const suffix = `${rangeStart}_${rangeEnd}`;

        const summaryHeaders = [utmBreakdownState.toUpperCase(), 'Views', 'Percentuale'];
        const summaryRows = rows.map((item) => ([
            (item?.value || 'unknown').toString(),
            Number(item?.count || 0),
            Number(item?.percentage || 0).toFixed(2),
        ]));

        const detailsHeaders = [utmBreakdownState.toUpperCase(), 'Corso', 'Views', 'Percentuale'];
        const detailsRows = rows.flatMap((item) => {
            const utmValue = (item?.value || 'unknown').toString();
            const courses = Array.isArray(item?.courses) ? item.courses : [];
            return courses.map((entry) => ([
                utmValue,
                entry?.course?.name || 'Corso sconosciuto',
                Number(entry?.count || 0),
                Number(entry?.percentage || 0).toFixed(2),
            ]));
        });

        downloadCsv(`utm_${utmBreakdownState}_summary_${suffix}.csv`, buildCsvContent(summaryHeaders, summaryRows));
        downloadCsv(`utm_${utmBreakdownState}_details_${suffix}.csv`, buildCsvContent(detailsHeaders, detailsRows));
    }, [analytics?.actual?.utm, utmBreakdownState, filters?.from_date, filters?.to_date]);

    const actualLeadMessageTouch = Number((
        analytics?.actual?.lead_staying?.lead_message_touch
        ?? analytics?.actual?.lead_message_sent
    ) || 0);
    const previousLeadMessageTouch = Number((
        analytics?.previous?.lead_staying?.lead_message_touch
        ?? analytics?.previous?.lead_message_sent
    ) || 0);
    const actualLeadCallTouch = Number((
        analytics?.actual?.lead_staying?.lead_call_touch
        ?? analytics?.actual?.lead_call_scheduled
    ) || 0);
    const previousLeadCallTouch = Number((
        analytics?.previous?.lead_staying?.lead_call_touch
        ?? analytics?.previous?.lead_call_scheduled
    ) || 0);

    const actualLeadContacted = Number(
        analytics?.actual?.lead_staying?.funnel_health?.leads_with_touch_total
        ?? (actualLeadMessageTouch + actualLeadCallTouch)
    );
    const previousLeadContacted = Number(
        analytics?.previous?.lead_staying?.funnel_health?.leads_with_touch_total
        ?? (previousLeadMessageTouch + previousLeadCallTouch)
    );
    const leadContactedPercentageDifference = calculatePercentageDifference(actualLeadContacted, previousLeadContacted);
    const leadMessageTouchPercentageDifference = calculatePercentageDifference(actualLeadMessageTouch, previousLeadMessageTouch);
    const leadCallTouchPercentageDifference = calculatePercentageDifference(actualLeadCallTouch, previousLeadCallTouch);

    const actualLeadResponded = Number(analytics?.actual?.lead_approved || 0) + Number(analytics?.actual?.lead_rejected || 0);
    const previousLeadResponded = Number(analytics?.previous?.lead_approved || 0) + Number(analytics?.previous?.lead_rejected || 0);
    const leadRespondedPercentageDifference = calculatePercentageDifference(actualLeadResponded, previousLeadResponded);

    const leadSalesMetrics = [
        {
            title: 'LEAD ARRIVATI',
            color: 'rgba(109, 74, 255, 1)',
            actual: analytics?.actual?.lead,
            previous: analytics?.previous?.lead,
            difference: leadGeneratedPercentageDifference,
            isLoss: leadGeneratedPercentageDifference < 0,
        },
        { title: 'LEAD CONTATTATI', color: 'rgba(84, 135, 255, 1)', actual: actualLeadContacted, previous: previousLeadContacted, difference: leadContactedPercentageDifference, isLoss: leadContactedPercentageDifference < 0 },
        { title: 'CONTATTO VIA MESSAGGIO', color: 'rgba(255, 135, 48, 1)', actual: actualLeadMessageTouch, previous: previousLeadMessageTouch, difference: leadMessageTouchPercentageDifference, isLoss: leadMessageTouchPercentageDifference < 0 },
        { title: 'CONTATTO VIA CHIAMATA', color: 'rgba(251, 217, 101, 1)', actual: actualLeadCallTouch, previous: previousLeadCallTouch, difference: leadCallTouchPercentageDifference, isLoss: leadCallTouchPercentageDifference < 0 },
        { title: 'LEAD CHE HANNO RISPOSTO', color: 'rgba(106, 230, 201, 1)', actual: actualLeadResponded, previous: previousLeadResponded, difference: leadRespondedPercentageDifference, isLoss: leadRespondedPercentageDifference < 0 },
    ];

    const leadStayingActual = useMemo(() => (
        analytics?.actual?.lead_staying || {}
    ), [analytics?.actual?.lead_staying]);
    const leadStayingPrevious = useMemo(() => (
        analytics?.previous?.lead_staying || {}
    ), [analytics?.previous?.lead_staying]);

    const formatSecondsLabel = (value) => {
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric < 0) {
            return '--';
        }
        return formatSeconds(numeric);
    };

    const formatPercentageLabel = (value) => {
        const numeric = Number(value);
        if (!Number.isFinite(numeric)) {
            return '--';
        }
        return `${numeric.toFixed(1)}%`;
    };

    const leadFunnelDynamicsMetrics = [
        {
            title: 'TEMPO MEDIANO PRIMO CONTATTO',
            color: 'rgba(84, 135, 255, 1)',
            actual: Number(leadStayingActual?.first_touch?.median_seconds),
            previous: Number(leadStayingPrevious?.first_touch?.median_seconds),
            formatter: formatSecondsLabel,
            isTimeMetric: true,
        },
        {
            title: 'TEMPO MEDIANO PRIMO CAMBIO',
            color: 'rgba(255, 135, 48, 1)',
            actual: Number(leadStayingActual?.first_status_change?.median_seconds),
            previous: Number(leadStayingPrevious?.first_status_change?.median_seconds),
            formatter: formatSecondsLabel,
            isTimeMetric: true,
        },
        {
            title: 'LEAD CON TOUCH',
            color: 'rgba(106, 230, 201, 1)',
            actual: Number(leadStayingActual?.funnel_health?.leads_with_touch_rate),
            previous: Number(leadStayingPrevious?.funnel_health?.leads_with_touch_rate),
            formatter: formatPercentageLabel,
            isTimeMetric: false,
        },
        {
            title: 'LEAD CON REGRESSIONI',
            color: 'rgba(238, 93, 108, 1)',
            actual: Number(leadStayingActual?.regressions?.lead_rate),
            previous: Number(leadStayingPrevious?.regressions?.lead_rate),
            formatter: formatPercentageLabel,
            isTimeMetric: false,
            isLowerBetter: true,
        },
    ].map((metric) => {
        const hasActual = Number.isFinite(metric.actual);
        const hasPrevious = Number.isFinite(metric.previous);
        const difference = (hasActual && hasPrevious)
            ? calculatePercentageDifference(metric.actual, metric.previous)
            : null;

        return {
            title: metric.title,
            color: metric.color,
            actualValue: hasActual ? metric.formatter(metric.actual) : null,
            previousValue: hasPrevious ? metric.formatter(metric.previous) : null,
            percentageValue: difference,
            isLoss: Number.isFinite(difference)
                ? ((metric.isTimeMetric || metric.isLowerBetter) ? difference > 0 : difference < 0)
                : false,
        };
    });

    const leadFunnelStateDurationRows = useMemo(() => {
        const rows = Array.isArray(leadStayingActual?.state_durations) ? leadStayingActual.state_durations : [];
        return rows.map((row, index) => {
            const stateKey = String(row?.state || `state-${index}`);
            const averageSeconds = Number(row?.average_seconds);
            const medianSeconds = Number(row?.median_seconds);
            const samples = Number(row?.samples || 0);

            return {
                id: `lead-funnel-duration-${stateKey}-${index}`,
                state_label: {
                    value: row?.label || stateKey,
                    type: 'string',
                    component: <div>{row?.label || stateKey}</div>,
                },
                avg_duration: {
                    value: Number.isFinite(averageSeconds) ? averageSeconds : -1,
                    type: 'number',
                    component: <div>{formatSecondsLabel(averageSeconds)}</div>,
                },
                median_duration: {
                    value: Number.isFinite(medianSeconds) ? medianSeconds : -1,
                    type: 'number',
                    component: <div>{formatSecondsLabel(medianSeconds)}</div>,
                },
                samples: {
                    value: samples,
                    type: 'number',
                    component: <div>{formatNumber(samples)}</div>,
                },
            };
        });
    }, [leadStayingActual]);

    const leadFunnelRegressionRows = useMemo(() => {
        const rows = Array.isArray(leadStayingActual?.regressions?.top_transitions)
            ? leadStayingActual.regressions.top_transitions
            : [];

        return rows.map((row, index) => {
            const count = Number(row?.count || 0);
            const share = Number(row?.share || 0);
            const fromLabel = String(row?.from_label || row?.from_state || '--');
            const toLabel = String(row?.to_label || row?.to_state || '--');

            return {
                id: `lead-funnel-regression-${index}`,
                transition: {
                    value: `${fromLabel} -> ${toLabel}`,
                    type: 'string',
                    component: <div>{fromLabel} {'->'} {toLabel}</div>,
                },
                count: {
                    value: count,
                    type: 'number',
                    component: <div>{formatNumber(count)}</div>,
                },
                share: {
                    value: share,
                    type: 'number',
                    component: <div>{formatPercentageLabel(share)}</div>,
                },
            };
        });
    }, [leadStayingActual]);

    return (
        <>
        <HeaderFooterLayout>
            <HelmetProvider>
                <Helmet>
                    <title>Report - Marketing</title>
                </Helmet>
            </HelmetProvider>
            <div className={styles.container}>
                {topContent}
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        <Breadcrumb items={[{ label: "Report" }, { label: "Marketing" }]} />
                        <header className={styles.pageHeading}><h1>Report marketing</h1><p>Traffico, acquisizione e conversioni: confronta i risultati con il periodo precedente.</p></header>
                        <div className={styles.filters}>
                            <div className={styles.filterField}><span>Periodo</span><EasePicker options={options} placeholder="Seleziona Date"
                                startDate={filters.from_date ? new Date(filters.from_date) : undefined}
                                endDate={filters.to_date ? new Date(filters.to_date) : undefined}
                                className={styles.datePicker} /></div>
                            <div className={styles.filterField}><span>Prodotti</span><MultipleSelect appearance="modern" portal
                                selected={courses.filter(c => filters.courses.includes(c.id))}
                                options={courses}
                                customOptions={[
                                    {
                                        id: "masters",
                                        name: "Master",
                                        selected: courses.filter(c => c.type === CourseType.Professional).every(c => filters.courses.includes(c.id)),
                                        onSelect: (selected) => {
                                            let masters_ids = courses.filter(c => c.type === CourseType.Professional)
                                            setFilters({
                                                ...filters,
                                                courses: selected ? [...filters.courses, ...masters_ids.map(c => c.id)] : filters.courses.filter(id => !masters_ids.map(c => c.id).includes(id))
                                            })
                                        }
                                    },
                                    {
                                        id: "masterclass",
                                        name: "Masterclass",
                                        selected: courses.filter(c => c.type === CourseType.Masterclass).every(c => filters.courses.includes(c.id)),
                                        onSelect: (selected) => {
                                            let masters_ids = courses.filter(c => c.type === CourseType.Masterclass)
                                            setFilters({
                                                ...filters,
                                                courses: selected ? [...filters.courses, ...masters_ids.map(c => c.id)] : filters.courses.filter(id => !masters_ids.map(c => c.id).includes(id))
                                            })
                                        }
                                    },
                                    {
                                        id: "bootcamp",
                                        name: "Bootcamp",
                                        selected: courses.find(c => c.type === CourseType.Bootcamp) ? courses.filter(c => c.type === CourseType.Bootcamp).every(c => filters.courses.includes(c.id)) : [],
                                        onSelect: (selected) => {
                                            let masters_ids = courses.filter(c => c.type === CourseType.Bootcamp)
                                            setFilters({
                                                ...filters,
                                                courses: selected ? [...filters.courses, ...masters_ids.map(c => c.id)] : filters.courses.filter(id => !masters_ids.map(c => c.id).includes(id))
                                            })
                                        }
                                    }
                                ]}
                                allLabel="Tutti i prodotti"
                                placeholder={"Tutti i Prodotti"}
                                onSelect={(items) => {
                                    setFilters({
                                        ...filters,
                                        courses: items.map(item => item.id)
                                    })
                                }} 
                                style={{ width: '100%' }}
                                /></div>
                        </div>
                        <nav className={styles.sectionNav} aria-label="Sezioni del report marketing">
                            {[
                                ['panoramica', 'Panoramica'], ['comportamento', 'Comportamento'], ['acquisizione', 'Acquisizione'],
                                ...(showLeadAnalyticsCards ? [['funnel', 'Funnel lead'], ['anagrafica', 'Anagrafica'], ['qualita', 'Qualità lead']] : []),
                                ['acquisti', 'Acquisti diretti'], ['web-vitals', 'Web Vitals'],
                            ].map(([id, label]) => <a key={id} href={`#marketing-${id}`}>{label}</a>)}
                        </nav>
                        <div className={styles.analytics}>
                            <div className={styles.reportSectionHeader}>
                                <h2 id="marketing-panoramica" className={styles.reportSectionTitle}>Panoramica</h2>
                                <div className={styles.reportSectionObjective}>
                                    Trend generali del periodo rispetto al precedente.
                                </div>
                            </div>

                            <Card appearance="modern" title="VISUALIZZAZIONI" style={{ flex: 1, flexBasis: '30%' }}>
                                <div className={styles.analytic}>
                                    {analytics?.actual?.page_views && !loading.behavioral_stats &&
                                        <div className={styles.actualAnalytic}>
                                            {formatNumber(analytics.actual.page_views)}
                                            <PercentageBadge value={pageViewsPercentageDifference} isLoss={pageViewsPercentageDifference < 0} />
                                        </div>
                                    }
                                    {analytics && analytics?.previous?.page_views !== null && !loading.behavioral_stats &&
                                        <div className={styles.previousAnalytic}>
                                            {formatNumber(analytics.previous.page_views)} nel periodo precedente
                                        </div>
                                    }

                                    {analytics && !loading.behavioral_stats &&
                                        <div className={styles.analyticProgresses}>
                                            {analytics && analytics?.previous?.page_views_anonymous !== null && !loading.behavioral_stats &&
                                                <div className={styles.analyticProgress}>
                                                    <div className={styles.analyticProgressTitle}>
                                                        <div className={typo.caption}>
                                                            ANONIMI • {formatNumber(analytics?.actual?.page_views_anonymous || 0)}
                                                        </div>
                                                        <div className={typo.caption}>
                                                            {((analytics?.actual?.page_views_anonymous) / (analytics?.actual?.page_views || 1) * 100).toFixed(2)}%
                                                        </div>
                                                    </div>
                                                    <div className={styles.progressBar} style={{ width: `${(analytics?.actual?.page_views_anonymous) / (analytics?.actual?.page_views || 1) * 100}%`, backgroundColor: 'var(--primary)' }} />
                                                </div>
                                            }
                                            {analytics && analytics?.previous?.page_views_registered !== null && !loading.behavioral_stats &&
                                                <div className={styles.analyticProgress}>
                                                    <div className={styles.analyticProgressTitle}>
                                                        <div className={typo.caption}>
                                                            REGISTRATI • {formatNumber(analytics?.actual?.page_views_registered || 0)}
                                                        </div>
                                                        <div className={typo.caption}>
                                                            {((analytics?.actual?.page_views_registered) / (analytics?.actual?.page_views || 1) * 100).toFixed(2)}%
                                                        </div>
                                                    </div>
                                                    <div className={styles.progressBar} style={{ width: `${(analytics?.actual?.page_views_registered) / (analytics?.actual?.page_views || 1) * 100}%`, backgroundColor: 'var(--secondary)' }} />
                                                </div>
                                            }
                                        </div>
                                    }

                                    {loading.behavioral_stats &&
                                        <div className={styles.analyticSkeleton}>
                                            <Skeleton height={24} width={'70%'} borderRadius={8} />
                                            <Skeleton height={12} width={'100%'} borderRadius={8} />
                                            <div className={styles.analyticProgresses}>
                                                <Skeleton height={24} width={'90%'} borderRadius={8} />
                                                <Skeleton height={24} width={'10%'} borderRadius={8} />
                                            </div>
                                        </div>
                                    }
                                </div>
                            </Card>

                            <Card appearance="modern" title="UTENTI UNICI" style={{ flex: 1, flexBasis: '30%' }}>
                                <div className={styles.analytic}>
                                    {analytics?.actual.unique_users && !loading.behavioral_stats &&
                                        <div className={styles.actualAnalytic}>
                                            {formatNumber(analytics.actual.unique_users)}
                                            <PercentageBadge value={uniqueUsersPercentageDifference} isLoss={uniqueUsersPercentageDifference < 0} />
                                        </div>
                                    }
                                    {analytics && analytics?.previous?.unique_users !== null && !loading.behavioral_stats &&
                                        <div className={styles.previousAnalytic}>
                                            {formatNumber(analytics.previous.unique_users)} nel periodo precedente
                                        </div>
                                    }
                                    {analytics && !loading.behavioral_stats &&
                                        <div className={styles.analyticProgresses}>
                                            {analytics && analytics?.previous?.unique_anonymous_users !== null && !loading.behavioral_stats &&
                                                <div className={styles.analyticProgress}>
                                                    <div className={styles.analyticProgressTitle}>
                                                        <div className={typo.caption}>
                                                            ANONIMI • {formatNumber(analytics?.actual?.unique_anonymous_users || 0)}
                                                        </div>
                                                        <div className={typo.caption}>
                                                            {((analytics?.actual?.unique_anonymous_users) / (analytics?.actual?.unique_users || 1) * 100).toFixed(2)}%
                                                        </div>
                                                    </div>
                                                    <div className={styles.progressBar} style={{ width: `${(analytics?.actual?.unique_anonymous_users) / (analytics?.actual?.unique_users || 1) * 100}%`, backgroundColor: 'var(--primary)' }} />
                                                </div>
                                            }
                                            {analytics && analytics?.previous?.unique_registered_users !== null && !loading.behavioral_stats &&
                                                <div className={styles.analyticProgress}>
                                                    <div className={styles.analyticProgressTitle}>
                                                        <div className={typo.caption}>
                                                            REGISTRATI • {formatNumber(analytics?.actual?.unique_registered_users || 0)}
                                                        </div>
                                                        <div className={typo.caption}>
                                                            {((analytics?.actual?.unique_registered_users) / (analytics?.actual?.unique_users || 1) * 100).toFixed(2)}%
                                                        </div>
                                                    </div>
                                                    <div className={styles.progressBar} style={{ width: `${(analytics?.actual?.unique_registered_users) / (analytics?.actual?.unique_users || 1) * 100}%`, backgroundColor: 'var(--secondary)' }} />
                                                </div>
                                            }
                                        </div>
                                    }

                                    {loading.behavioral_stats &&
                                        <div className={styles.analyticSkeleton}>
                                            <Skeleton height={24} width={'70%'} borderRadius={8} />
                                            <Skeleton height={12} width={'100%'} borderRadius={8} />
                                            <div className={styles.analyticProgresses}>
                                                <Skeleton height={24} width={'90%'} borderRadius={8} />
                                                <Skeleton height={24} width={'10%'} borderRadius={8} />
                                            </div>
                                        </div>
                                    }
                                </div>
                            </Card>

                            <Card appearance="modern" title="SESSIONI" style={{ flex: 1, flexBasis: '30%' }}>
                                <div className={styles.analytic}>
                                    {analytics?.actual?.sessions && !loading.behavioral_stats &&
                                        <div className={styles.actualAnalytic}>
                                            {formatNumber(analytics.actual.sessions)}
                                            <PercentageBadge value={sessionsPercentageDifference} isLoss={sessionsPercentageDifference < 0} />
                                        </div>
                                    }
                                    {analytics && analytics?.previous?.sessions !== null && !loading.behavioral_stats &&
                                        <div className={styles.previousAnalytic}>
                                            {formatNumber(analytics.previous.sessions)} nel periodo precedente
                                        </div>
                                    }
                                    {loading.behavioral_stats &&
                                        <div className={styles.analyticSkeleton}>
                                            <Skeleton height={24} width={'70%'} borderRadius={8} />
                                            <Skeleton height={12} width={'100%'} borderRadius={8} />
                                        </div>
                                    }
                                </div>
                                <div className={styles.analytic}>
                                    <div className={styles.analyticTitle}>DURATA MEDIA</div>

                                    {analytics && analytics?.actual?.session_duration !== null && !loading.behavioral_stats &&
                                        <div className={styles.actualAnalytic}>
                                            {formatSeconds(analytics.actual.session_duration)}
                                            <PercentageBadge value={sessionsDurationPercentageDifference} isLoss={sessionsDurationPercentageDifference < 0} />
                                        </div>
                                    }
                                    {analytics && analytics?.previous?.session_duration !== null && !loading.behavioral_stats &&
                                        <div className={styles.previousAnalytic}>
                                            {formatSeconds(analytics.previous.session_duration)} nel periodo precedente
                                        </div>
                                    }
                                    {loading.behavioral_stats &&
                                        <div className={styles.analyticSkeleton} style={{ marginTop: '0.5rem' }}>
                                            <Skeleton height={24} width={'70%'} borderRadius={8} />
                                            <Skeleton height={12} width={'100%'} borderRadius={8} />
                                        </div>
                                    }
                                </div>
                            </Card>

                            <div className={styles.reportSectionHeader}>
                                <h2 id="marketing-comportamento" className={styles.reportSectionTitle}>Comportamento</h2>
                                <div className={styles.reportSectionObjective}>
                                    Interazioni degli utenti sulle pagine dei corsi.
                                </div>
                            </div>

                            <Card appearance="modern" title="CORSI -  STATISTICHE COMPORTAMENTALI" integratedTable style={{ flex: 1, flexBasis: '100%', minHeight: '400px' }} actions={<Button appearance="subtle" onClick={handleExportBehavioralStatsCsv}><ExportIcon style={{ width: '11px', height: '11px' }} /> Esporta</Button>}>
                                <div className={styles.productLeadSearchWrapper}>
                                    <div className={styles.productLeadFiltersBar}>
                                        <div className={styles.productLeadFilterControl}>
                                            <TextInput appearance="modern"
                                                type="search"
                                                placeholder="Cerca corso..."
                                                value={behavioralCourseSearch}
                                                onKeyUp={(value) => setBehavioralCourseSearch(value ?? '')}
                                            />
                                        </div>
                                        <div className={styles.productLeadFilterControl}>
                                            <Select appearance="modern" portal
                                                selected={behavioralCourseType}
                                                options={CourseTypes}
                                                placeholder="Tutti i tipi"
                                                nullable
                                                nullLabel="Tutti i tipi"
                                                onSelect={(option) => setBehavioralCourseType(option?.value ? option : null)}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                        <div className={styles.productLeadFilterControl}>
                                            <Select appearance="modern" portal
                                                selected={behavioralCourseCategory}
                                                options={CourseCategories}
                                                placeholder="Tutte le categorie"
                                                nullable
                                                nullLabel="Tutte le categorie"
                                                onSelect={(option) => setBehavioralCourseCategory(option?.value ? option : null)}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <Table appearance="minimal"
                                    style={{ borderRadius: 0, '--table-cell-vertical-align': 'middle', maxHeight: '420px', overflowY: 'auto' }}
                                    loading={loading.behavioral_stats}
                                    loadingN={6}
                                    columns={[
                                        { key: 'course_name', label: 'CORSO', type: 'custom', wrap: true, style: { minWidth: '260px', padding: '0.85rem 1rem', alignItems: 'flex-start' } },
                                        { key: 'page_views_total', label: 'VIEWS', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'unique_users_total', label: 'UTENTI', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'unique_sessions', label: 'SESSIONI', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'view_lead', label: 'APERTURE', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'close_lead', label: 'CHIUSURE', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'generate_lead', label: 'LEAD GEN', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'checkout_start', label: 'CHK START', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'checkout_fill', label: 'CHK FILL', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'purchase', label: 'PURCHASE', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'avg_time_on_course_seconds', label: 'TEMPO MEDIO', type: 'custom', style: { minWidth: '130px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'median_time_on_course_seconds', label: 'TEMPO MEDIANO', type: 'custom', style: { minWidth: '145px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                    ]}
                                    elements={filteredBehavioralStatsCoursesTableData}
                                    defaultSort={{ key: 'page_views_total', direction: 'desc' }}
                                />
                            </Card>

                            <Card appearance="modern" title="FONTI -  STATISTICHE COMPORTAMENTALI" integratedTable style={{ flex: 1, flexBasis: '100%', minHeight: '400px' }} actions={<Button appearance="subtle" onClick={handleExportBehavioralStatsSourcesCsv}><ExportIcon style={{ width: '11px', height: '11px' }} /> Esporta</Button>}>
                                <div className={styles.productLeadSearchWrapper}>
                                    <div className={styles.productLeadFiltersBar}>
                                        <div className={styles.productLeadFilterControl} style={{ maxWidth: '280px' }}>
                                            <Select appearance="modern" portal
                                                selected={behavioralUtmBreakdownOptions.find((option) => option.value === behavioralBreakdownKey) || behavioralUtmBreakdownOptions[0]}
                                                options={behavioralUtmBreakdownOptions}
                                                placeholder="Raggruppa per"
                                                onSelect={(option) => setBehavioralUtmBreakdownState(option?.value || 'source')}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <Table appearance="minimal"
                                    style={{ borderRadius: 0, '--table-cell-vertical-align': 'middle', maxHeight: '420px', overflowY: 'auto' }}
                                    loading={loading.behavioral_stats}
                                    loadingN={6}
                                    columns={[
                                        { key: 'utm_value', label: behavioralBreakdownKey.toUpperCase(), type: 'custom', wrap: true, style: { minWidth: '260px', padding: '0.85rem 1rem', alignItems: 'flex-start' } },
                                        { key: 'page_views_total', label: 'VIEWS', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'unique_users_total', label: 'UTENTI', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'unique_sessions', label: 'SESSIONI', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'view_lead', label: 'APERTURE', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'close_lead', label: 'CHIUSURE', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'generate_lead', label: 'LEAD GEN', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'checkout_start', label: 'CHK START', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'checkout_fill', label: 'CHK FILL', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'purchase', label: 'PURCHASE', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'avg_time_on_course_seconds', label: 'TEMPO MEDIO', type: 'custom', style: { minWidth: '130px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'median_time_on_course_seconds', label: 'TEMPO MEDIANO', type: 'custom', style: { minWidth: '145px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                    ]}
                                    elements={behavioralStatsSourcesTableData}
                                    defaultSort={{ key: 'page_views_total', direction: 'desc' }}
                                />
                            </Card>

                            <div className={styles.reportSectionHeader}>
                                <h2 id="marketing-acquisizione" className={styles.reportSectionTitle}>Acquisizione traffico</h2>
                                <div className={styles.reportSectionObjective}>
                                    Canali che generano più visite e il loro peso per singolo corso.
                                </div>
                            </div>

                            <div className={styles.utmReferrerCardsRow}>
                                <Card appearance="modern" title="REFERRER - STATISTICHE" integratedTable style={{ flex: 1 }} actions={<Button appearance="subtle" onClick={handleExportReferrerCsv}><ExportIcon style={{ width: '11px', height: '11px' }} /> Esporta</Button>}>
                                    <Table appearance="minimal"
                                        style={{ borderRadius: 0, '--table-cell-vertical-align': 'top', maxHeight: '420px', overflowY: 'auto' }}
                                        loading={loading.referrers}
                                        loadingN={8}
                                        columns={[
                                            { key: 'referrer', label: 'REFERRER', type: 'custom', wrap: true, style: { minWidth: '470px', padding: '0.85rem 1rem', alignItems: 'flex-start', justifyContent: 'flex-start' } },
                                            { key: 'count', label: 'VIEWS', type: 'custom', style: { minWidth: '150px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                            { key: 'percentage', label: 'PERCENTUALE', type: 'custom', style: { minWidth: '160px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        ]}
                                        elements={referrerTableData}
                                        defaultSort={{ key: 'count', direction: 'desc' }}
                                        onRowClick={(row) => {
                                            if (!row?.id) return;
                                            toggleReferrerRow(row.id);
                                        }}
                                    />
                                </Card>

                                <Card appearance="modern" title="UTM - STATISTICHE" integratedTable style={{ flex: 1 }} actions={<Button appearance="subtle" onClick={handleExportUtmCsv}><ExportIcon style={{ width: '11px', height: '11px' }} /> Esporta</Button>}>
                                    <div className={styles.productLeadSearchWrapper}>
                                        <MultiStateSwitch
                                            style={{ padding: '0.5rem 0' }}
                                            statesStyle={{ fontSize: '0.75rem', padding: '0' }}
                                            states={["SOURCE", "MEDIUM", "CAMPAIGN"]}
                                            selected={["source", "medium", "campaign"].indexOf(utmBreakdownState)}
                                            onStateChange={(index) => setUtmBreakdownState(["source", "medium", "campaign"][index] || "source")}
                                        />
                                    </div>
                                    <Table appearance="minimal"
                                        style={{ borderRadius: 0, '--table-cell-vertical-align': 'top', maxHeight: '420px', overflowY: 'auto' }}
                                        loading={loading.utm}
                                        loadingN={8}
                                        columns={[
                                            { key: 'utm_value', label: utmBreakdownState.toUpperCase(), type: 'custom', wrap: true, style: { minWidth: '470px', padding: '0.85rem 1rem', alignItems: 'flex-start', justifyContent: 'flex-start' } },
                                            { key: 'count', label: 'VIEWS', type: 'custom', style: { minWidth: '150px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                            { key: 'percentage', label: 'PERCENTUALE', type: 'custom', style: { minWidth: '160px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        ]}
                                        elements={utmTableData}
                                        defaultSort={{ key: 'count', direction: 'desc' }}
                                        onRowClick={(row) => {
                                            if (!row?.id) return;
                                            toggleUtmRow(row.id);
                                        }}
                                    />
                                </Card>
                            </div>

                            {showLeadAnalyticsCards && <div className={styles.reportSectionHeader}>
                                <h2 id="marketing-funnel" className={styles.reportSectionTitle}>Funnel lead</h2>
                                <div className={styles.reportSectionObjective}>
                                    Attivita commerciale lead nel periodo: volumi in ingresso, contatti e risposte.
                                </div>
                            </div>}

                            {showLeadAnalyticsCards && <Card appearance="modern" title="LEAD - OPERATIVITÀ COMMERCIALE" style={{ flex: 1, flexBasis: '100%' }}>
                                <div className={styles.analyticRow}>
                                    {leadSalesMetrics.map((metric) => (
                                        <MetricStatBlock
                                            key={metric.title}
                                            title={metric.title}
                                            titleColor={metric.color}
                                            actualValue={metric.actual !== null && metric.actual !== undefined ? formatNumber(metric.actual) : null}
                                            previousValue={metric.previous !== null && metric.previous !== undefined ? formatNumber(metric.previous) : null}
                                            percentageValue={metric.difference}
                                            isLoss={metric.isLoss}
                                            loading={loading.leads}
                                            containerClassName={styles.analytic}
                                            titleClassName={styles.analyticTitle}
                                            actualClassName={styles.actualAnalytic}
                                            previousClassName={styles.previousAnalytic}
                                            skeletonClassName={styles.analyticSkeleton}
                                        />
                                    ))}
                                </div>
                            </Card>}

                            {showLeadAnalyticsCards && <Card appearance="modern" title="LEAD FUNNEL - TEMPI E DINAMICA" style={{ flex: 1, flexBasis: '100%' }}>
                                <div className={styles.analyticRow}>
                                    {leadFunnelDynamicsMetrics.map((metric) => (
                                        <MetricStatBlock
                                            key={metric.title}
                                            title={metric.title}
                                            titleColor={metric.color}
                                            actualValue={metric.actualValue}
                                            previousValue={metric.previousValue}
                                            percentageValue={metric.percentageValue}
                                            isLoss={metric.isLoss}
                                            loading={loading.lead_staying}
                                            containerClassName={styles.analytic}
                                            titleClassName={styles.analyticTitle}
                                            actualClassName={styles.actualAnalytic}
                                            previousClassName={styles.previousAnalytic}
                                            skeletonClassName={styles.analyticSkeleton}
                                        />
                                    ))}
                                </div>
                            </Card>}

                            {showLeadAnalyticsCards && <div className={styles.utmReferrerCardsRow}>
                                <Card appearance="modern" title="TEMPI MEDI E MEDIANI DI PERMANENZA PER STATO" integratedTable style={{ flex: 1 }}>
                                    <Table appearance="minimal"
                                        style={{ borderRadius: 0, '--table-cell-vertical-align': 'middle', maxHeight: '360px', overflowY: 'auto' }}
                                        loading={loading.lead_staying}
                                        loadingN={8}
                                        columns={[
                                            { key: 'state_label', label: 'STATO', type: 'custom', wrap: true, style: { minWidth: '230px', padding: '0.85rem 1rem', alignItems: 'flex-start' } },
                                            { key: 'avg_duration', label: 'TEMPO MEDIO', type: 'custom', style: { minWidth: '150px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                            { key: 'median_duration', label: 'TEMPO MEDIANO', type: 'custom', style: { minWidth: '160px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                            { key: 'samples', label: 'CAMPIONI', type: 'custom', style: { minWidth: '120px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        ]}
                                        elements={leadFunnelStateDurationRows}
                                        defaultSort={{ key: 'avg_duration', direction: 'desc' }}
                                    />
                                </Card>

                                <Card appearance="modern" title="REGRESSIONI DI STATO" integratedTable style={{ flex: 1 }}>
                                    <Table appearance="minimal"
                                        style={{ borderRadius: 0, '--table-cell-vertical-align': 'middle', maxHeight: '360px', overflowY: 'auto' }}
                                        loading={loading.lead_staying}
                                        loadingN={8}
                                        columns={[
                                            { key: 'transition', label: 'TRANSIZIONE', type: 'custom', wrap: true, style: { minWidth: '260px', padding: '0.85rem 1rem', alignItems: 'flex-start' } },
                                            { key: 'count', label: 'EVENTI', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                            { key: 'share', label: 'PESO SULLE REGRESSIONI', type: 'custom', style: { minWidth: '190px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        ]}
                                        elements={leadFunnelRegressionRows}
                                        defaultSort={{ key: 'count', direction: 'desc' }}
                                    />
                                </Card>
                            </div>}

                            {showLeadAnalyticsCards && <Card appearance="modern" title="SNAPSHOT STATO LEAD - PER CORSO" integratedTable style={{ flex: 1, flexBasis: '100%' }}>
                                <Table appearance="minimal"
                                    style={{ borderRadius: 0, '--table-cell-vertical-align': 'middle', maxHeight: '420px', overflowY: 'auto' }}
                                    loading={loading.lead_score_quality}
                                    loadingN={8}
                                    columns={leadScoreSnapshotColumns}
                                    elements={leadScoreQualitySnapshotTableData}
                                    defaultSort={{ key: 'lead_total', direction: 'desc' }}
                                    onRowClick={(row) => onLeadScoreSnapshotRowClick(row)}
                                />
                            </Card>}

                            {showLeadAnalyticsCards && <div className={styles.reportSectionHeader}>
                                <h2 id="marketing-anagrafica" className={styles.reportSectionTitle}>Anagrafica lead</h2>
                                <div className={styles.reportSectionObjective}>
                                    Distribuzione dei lead per età, provenienza, genere e occupazione con relativi tassi di conversione.
                                </div>
                            </div>}

                            {showLeadAnalyticsCards && <div className={styles.utmReferrerCardsRow}>
                                <Card appearance="modern" title="FASCIA D'ETÀ" style={{ flex: 1 }}>
                                    <div className={styles.analytic} style={{ marginBottom: '0.75rem' }}>
                                        <div className={styles.analyticTitle}>ETÀ MEDIA LEAD</div>
                                        <div className={styles.actualAnalytic}>
                                            {Number.isFinite(Number(leadDemographicsSummary.avg_age))
                                                ? `${Number(leadDemographicsSummary.avg_age).toFixed(1)} anni`
                                                : '--'}
                                        </div>
                                    </div>
                                    {loading.lead_demographics && (
                                        <div className={styles.analyticSkeleton}>
                                            <Skeleton height={220} width={'100%'} borderRadius={8} />
                                        </div>
                                    )}
                                    {!loading.lead_demographics && (
                                        <div className={styles.demographicChartContainer}>
                                            <ResponsiveContainer width="100%" height={280}>
                                                <ComposedChart data={leadDemographicsAgeChartData} margin={{ top: 8, right: 18, left: 4, bottom: 0 }}>
                                                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(var(--text-color-rgb), 8%)" />
                                                    <Legend verticalAlign="top" iconSize={8} wrapperStyle={{ fontSize: 11, paddingBottom: 12 }} />
                                                    <XAxis dataKey="label" stroke="rgba(var(--text-color-rgb), 48%)" fontSize={11} interval={0} />
                                                    <YAxis yAxisId="left" tickFormatter={(value) => `${value}%`} stroke="rgba(var(--text-color-rgb), 48%)" fontSize={11} width={35} domain={[0, 100]} />
                                                    <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => `${value}%`} stroke="rgba(var(--text-color-rgb), 42%)" fontSize={11} width={35} domain={[0, 100]} />
                                                    <Tooltip content={<DemographicTooltip />} />
                                                    <Bar yAxisId="left" dataKey="percentage" name="Quota lead" fill={LEAD_DEMOGRAPHICS_BAR_COLOR} radius={[6, 6, 0, 0]}>
                                                        {leadDemographicsAgeChartData.map((entry, index) => (
                                                            <Cell key={`lead-age-cell-${entry.label}-${index}`} fill={LEAD_DEMOGRAPHICS_BAR_COLORS[index % LEAD_DEMOGRAPHICS_BAR_COLORS.length]} />
                                                        ))}
                                                    </Bar>
                                                    <Line yAxisId="right" type="monotone" dataKey="conversion_rate" name="Conversione" stroke={LEAD_DEMOGRAPHICS_LINE_COLOR} strokeWidth={2.25} dot={{ r: 3, fill: LEAD_DEMOGRAPHICS_LINE_COLOR }} activeDot={{ r: 4 }} />
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </Card>

                                <Card appearance="modern" title="PROVENIENZA (REGIONE)" style={{ flex: 1 }}>
                                    {loading.lead_demographics && (
                                        <div className={styles.analyticSkeleton}>
                                            <Skeleton height={220} width={'100%'} borderRadius={8} />
                                        </div>
                                    )}
                                    {!loading.lead_demographics && (
                                        <div className={styles.demographicChartContainer} style={{ height: '348px' }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <ComposedChart data={leadDemographicsRegionChartData} margin={{ top: 8, right: 18, left: 4, bottom: 0 }}>
                                                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(var(--text-color-rgb), 8%)" />
                                                    <Legend verticalAlign="top" iconSize={8} wrapperStyle={{ fontSize: 11, paddingBottom: 12 }} />
                                                    <XAxis dataKey="label" stroke="rgba(var(--text-color-rgb), 48%)" fontSize={10} interval={0} angle={-20} textAnchor="end" height={60} />
                                                    <YAxis yAxisId="left" tickFormatter={(value) => `${value}%`} stroke="rgba(var(--text-color-rgb), 48%)" fontSize={11} width={35} domain={[0, 100]} />
                                                    <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => `${value}%`} stroke="rgba(var(--text-color-rgb), 42%)" fontSize={11} width={35} domain={[0, 100]} />
                                                    <Tooltip content={<DemographicTooltip />} />
                                                    <Bar yAxisId="left" dataKey="percentage" name="Quota lead" fill={LEAD_DEMOGRAPHICS_BAR_COLOR} radius={[6, 6, 0, 0]}>
                                                        {leadDemographicsRegionChartData.map((entry, index) => (
                                                            <Cell key={`lead-region-cell-${entry.label}-${index}`} fill={LEAD_DEMOGRAPHICS_BAR_COLORS[index % LEAD_DEMOGRAPHICS_BAR_COLORS.length]} />
                                                        ))}
                                                    </Bar>
                                                    <Line yAxisId="right" type="monotone" dataKey="conversion_rate" name="Conversione" stroke={LEAD_DEMOGRAPHICS_LINE_COLOR} strokeWidth={2.25} dot={{ r: 3, fill: LEAD_DEMOGRAPHICS_LINE_COLOR }} activeDot={{ r: 4 }} />
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </Card>
                            </div>}

                            {showLeadAnalyticsCards && <div className={styles.utmReferrerCardsRow}>
                                <Card appearance="modern" title="GENERE LEAD" style={{ flex: 1 }}>
                                    {loading.lead_demographics && <Skeleton height={260} width={'100%'} borderRadius={8} />}
                                    {!loading.lead_demographics && (
                                        <div className={styles.demographicChartContainer}>
                                            <ResponsiveContainer width="100%" height={280}>
                                                <ComposedChart data={leadDemographicsGenderChartData} margin={{ top: 8, right: 18, left: 4, bottom: 0 }}>
                                                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(var(--text-color-rgb), 8%)" />
                                                    <Legend verticalAlign="top" iconSize={8} wrapperStyle={{ fontSize: 11, paddingBottom: 12 }} />
                                                    <XAxis dataKey="label" stroke="rgba(var(--text-color-rgb), 48%)" fontSize={11} interval={0} />
                                                    <YAxis yAxisId="left" tickFormatter={(value) => `${value}%`} stroke="rgba(var(--text-color-rgb), 48%)" fontSize={11} width={35} domain={[0, 100]} />
                                                    <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => `${value}%`} stroke="rgba(var(--text-color-rgb), 42%)" fontSize={11} width={35} domain={[0, 100]} />
                                                    <Tooltip content={<DemographicTooltip />} />
                                                    <Bar yAxisId="left" dataKey="percentage" name="Quota lead" fill={LEAD_DEMOGRAPHICS_BAR_COLOR} radius={[6, 6, 0, 0]}>
                                                        {leadDemographicsGenderChartData.map((entry, index) => (
                                                            <Cell key={`lead-gender-cell-${entry.label}-${index}`} fill={LEAD_DEMOGRAPHICS_BAR_COLORS[index % LEAD_DEMOGRAPHICS_BAR_COLORS.length]} />
                                                        ))}
                                                    </Bar>
                                                    <Line yAxisId="right" type="monotone" dataKey="conversion_rate" name="Conversione" stroke={LEAD_DEMOGRAPHICS_LINE_COLOR} strokeWidth={2.25} dot={{ r: 3, fill: LEAD_DEMOGRAPHICS_LINE_COLOR }} activeDot={{ r: 4 }} />
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </Card>

                                <Card appearance="modern" title="OCCUPAZIONE LEAD" style={{ flex: 1 }}>
                                    {loading.lead_demographics && <Skeleton height={260} width={'100%'} borderRadius={8} />}
                                    {!loading.lead_demographics && (
                                        <div className={styles.demographicChartContainer}>
                                            <ResponsiveContainer width="100%" height={280}>
                                                <ComposedChart data={leadDemographicsOccupationChartData} margin={{ top: 8, right: 18, left: 4, bottom: 0 }}>
                                                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(var(--text-color-rgb), 8%)" />
                                                    <Legend verticalAlign="top" iconSize={8} wrapperStyle={{ fontSize: 11, paddingBottom: 12 }} />
                                                    <XAxis dataKey="label" stroke="rgba(var(--text-color-rgb), 48%)" fontSize={11} interval={0} />
                                                    <YAxis yAxisId="left" tickFormatter={(value) => `${value}%`} stroke="rgba(var(--text-color-rgb), 48%)" fontSize={11} width={35} domain={[0, 100]} />
                                                    <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => `${value}%`} stroke="rgba(var(--text-color-rgb), 42%)" fontSize={11} width={35} domain={[0, 100]} />
                                                    <Tooltip content={<DemographicTooltip />} />
                                                    <Bar yAxisId="left" dataKey="percentage" name="Quota lead" fill={LEAD_DEMOGRAPHICS_BAR_COLOR} radius={[6, 6, 0, 0]}>
                                                        {leadDemographicsOccupationChartData.map((entry, index) => (
                                                            <Cell key={`lead-occupation-cell-${entry.label}-${index}`} fill={LEAD_DEMOGRAPHICS_BAR_COLORS[index % LEAD_DEMOGRAPHICS_BAR_COLORS.length]} />
                                                        ))}
                                                    </Bar>
                                                    <Line yAxisId="right" type="monotone" dataKey="conversion_rate" name="Conversione" stroke={LEAD_DEMOGRAPHICS_LINE_COLOR} strokeWidth={2.25} dot={{ r: 3, fill: LEAD_DEMOGRAPHICS_LINE_COLOR }} activeDot={{ r: 4 }} />
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </Card>
                            </div>}

                            {showLeadAnalyticsCards && <Card appearance="modern" title="ANAGRAFICA LEAD - BREAKDOWN PER CORSO" integratedTable style={{ flex: 1, flexBasis: '100%' }} actions={<Button appearance="subtle" onClick={handleExportLeadDemographicsCsv}><ExportIcon style={{ width: '11px', height: '11px' }} /> Esporta</Button>}>
                                <div className={styles.productLeadSearchWrapper}>
                                    <div className={styles.productLeadFiltersBar}>
                                        <div className={styles.productLeadFilterControl}>
                                            <TextInput appearance="modern"
                                                type="search"
                                                placeholder="Cerca corso..."
                                                value={leadDemographicsSearch}
                                                onKeyUp={(value) => setLeadDemographicsSearch(value ?? '')}
                                            />
                                        </div>
                                        <div className={styles.productLeadFilterControl}>
                                            <Select appearance="modern" portal
                                                selected={leadDemographicsType}
                                                options={CourseTypes}
                                                placeholder="Tutti i tipi"
                                                nullable
                                                nullLabel="Tutti i tipi"
                                                onSelect={(option) => setLeadDemographicsType(option?.value ? option : null)}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                        <div className={styles.productLeadFilterControl}>
                                            <Select appearance="modern" portal
                                                selected={leadDemographicsCategory}
                                                options={CourseCategories}
                                                placeholder="Tutte le categorie"
                                                nullable
                                                nullLabel="Tutte le categorie"
                                                onSelect={(option) => setLeadDemographicsCategory(option?.value ? option : null)}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <Table appearance="minimal"
                                    style={{ borderRadius: 0, '--table-cell-vertical-align': 'middle', maxHeight: '600px', overflowY: 'auto' }}
                                    loading={loading.lead_demographics}
                                    loadingN={8}
                                    columns={[
                                        { key: 'course_name', label: 'CORSO', type: 'custom', wrap: true, style: { width: '100%', minWidth: '300px', padding: '0.85rem 1rem', alignItems: 'flex-start' } },
                                        { key: 'lead_type', label: 'LEAD TIPO', type: 'custom', wrap: true, style: { minWidth: '240px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'customer_type', label: 'CLIENTE TIPO', type: 'custom', wrap: true, style: { minWidth: '260px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'lead_total', label: 'LEAD', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'lead_won_total', label: 'LEAD VINTI', type: 'custom', style: { minWidth: '130px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'conversion_rate', label: 'CONV %', type: 'custom', style: { minWidth: '130px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'age_breakdown', label: 'ETÀ', type: 'custom', wrap: true, style: { minWidth: '200px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'region_breakdown', label: 'PROVENIENZA', type: 'custom', wrap: true, style: { minWidth: '220px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'gender_breakdown', label: 'GENERE', type: 'custom', wrap: true, style: { minWidth: '170px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'employment_breakdown', label: 'OCCUPAZIONE', type: 'custom', wrap: true, style: { minWidth: '210px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                    ]}
                                    elements={filteredLeadDemographicsCourseTableData}
                                    defaultSort={{ key: 'lead_total', direction: 'desc' }}
                                />
                            </Card>}

                            {showLeadAnalyticsCards && <div className={styles.reportSectionHeader}>
                                <h2 id="marketing-qualita" className={styles.reportSectionTitle}>Qualità lead</h2>
                                <div className={styles.reportSectionObjective}>
                                    Quantità e conversione dei lead per corso e per sorgente.
                                </div>
                            </div>}

                            {showLeadAnalyticsCards && <Card appearance="modern" title="SCORING LEAD - AGGREGATO PER CORSO" integratedTable style={{ flex: 1, flexBasis: '100%' }} actions={<Button appearance="subtle" onClick={handleExportLeadScoreQualityCsv}><ExportIcon style={{ width: '11px', height: '11px' }} /> Esporta</Button>}>
                                <Table appearance="minimal"
                                    style={{ borderRadius: 0, '--table-cell-vertical-align': 'middle', maxHeight: '420px', overflowY: 'auto' }}
                                    loading={loading.lead_score_quality}
                                    loadingN={8}
                                    columns={[
                                        { key: 'course_name', label: 'CORSO', type: 'custom', wrap: true, style: { width: '100%', minWidth: '280px', padding: '0.85rem 1rem', alignItems: 'flex-start' } },
                                        { key: 'lead_total', label: 'LEAD', type: 'custom', style: { minWidth: '100px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'avg_score', label: 'SCORE MEDIO', type: 'custom', style: { minWidth: '140px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'avg_initial_score', label: 'SCORE MEDIO INIZIALE', type: 'custom', style: { minWidth: '170px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'score_mix', label: 'MIX HOT/WARM/COLD', type: 'custom', wrap: true, style: { minWidth: '230px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'touch', label: 'TOUCH', type: 'custom', wrap: true, style: { minWidth: '190px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'touch_quality', label: 'TOUCH QUALITY', type: 'custom', wrap: true, style: { minWidth: '230px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'purchase_propensity', label: 'PROPENSIONE ACQUISTO', type: 'custom', wrap: true, style: { minWidth: '220px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'arrival_heat', label: 'ARRIVA HOT %', type: 'custom', style: { minWidth: '130px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'warming_rate', label: 'WARMED %', type: 'custom', style: { minWidth: '120px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'cooling_rate', label: 'COOLED %', type: 'custom', style: { minWidth: '120px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'avg_delta', label: 'DELTA SCORE', type: 'custom', style: { minWidth: '120px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                    ]}
                                    elements={leadScoreQualityTableData}
                                    defaultSort={{ key: 'lead_total', direction: 'desc' }}
                                />
                            </Card>}

                            {showLeadAnalyticsCards && <Card appearance="modern" title="SCORING LEAD - BREAKDOWN UTM" integratedTable style={{ flex: 1, flexBasis: '100%' }}>
                                <div className={styles.productLeadSearchWrapper}>
                                    <div className={styles.productLeadFiltersBar}>
                                        <div className={styles.productLeadFilterControl} style={{ maxWidth: '280px' }}>
                                            <Select appearance="modern" portal
                                                selected={leadScoreQualityBreakdown}
                                                options={sourceLeadBreakdownOptions}
                                                placeholder="Raggruppa per"
                                                onSelect={(option) => setLeadScoreQualityBreakdown(option?.value ? option : sourceLeadBreakdownOptions[0])}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <Table appearance="minimal"
                                    style={{ borderRadius: 0, '--table-cell-vertical-align': 'middle', maxHeight: '420px', overflowY: 'auto' }}
                                    loading={loading.lead_score_quality_utm}
                                    loadingN={8}
                                    columns={[
                                        { key: 'course_name', label: `${String(leadScoreQualityBreakdown?.name || 'Source').toUpperCase()} UTM`, type: 'custom', wrap: true, style: { width: '100%', minWidth: '240px', padding: '0.85rem 1rem', alignItems: 'flex-start' } },
                                        { key: 'lead_total', label: 'LEAD', type: 'custom', style: { minWidth: '100px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'avg_score', label: 'SCORE MEDIO', type: 'custom', style: { minWidth: '140px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'avg_initial_score', label: 'SCORE MEDIO INIZIALE', type: 'custom', style: { minWidth: '170px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'score_mix', label: 'MIX HOT/WARM/COLD', type: 'custom', wrap: true, style: { minWidth: '230px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'touch', label: 'TOUCH', type: 'custom', wrap: true, style: { minWidth: '190px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'touch_quality', label: 'TOUCH QUALITY', type: 'custom', wrap: true, style: { minWidth: '230px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'purchase_propensity', label: 'PROPENSIONE ACQUISTO', type: 'custom', wrap: true, style: { minWidth: '220px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'arrival_heat', label: 'ARRIVA HOT %', type: 'custom', style: { minWidth: '130px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'warming_rate', label: 'WARMED %', type: 'custom', style: { minWidth: '120px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'cooling_rate', label: 'COOLED %', type: 'custom', style: { minWidth: '120px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'avg_delta', label: 'DELTA SCORE', type: 'custom', style: { minWidth: '120px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                    ]}
                                    elements={leadScoreQualityUtmTableData}
                                    defaultSort={{ key: 'lead_total', direction: 'desc' }}
                                />
                            </Card>}

                            {showLeadAnalyticsCards && <Card appearance="modern" title="TOUCH LEAD - OUTCOME E FEEDBACK PER CORSO" integratedTable style={{ flex: 1, flexBasis: '100%' }}>
                                <Table appearance="minimal"
                                    style={{ borderRadius: 0, '--table-cell-vertical-align': 'top', maxHeight: '420px', overflowY: 'auto' }}
                                    loading={loading.lead_score_quality}
                                    loadingN={8}
                                    columns={leadTouchBreakdownColumns}
                                    elements={leadTouchBreakdownCourseTableData}
                                    defaultSort={{ key: 'touch_total', direction: 'desc' }}
                                />
                            </Card>}

                            {showLeadAnalyticsCards && <Card appearance="modern" title="TOUCH LEAD - OUTCOME E FEEDBACK PER SOURCE" integratedTable style={{ flex: 1, flexBasis: '100%' }}>
                                <Table appearance="minimal"
                                    style={{ borderRadius: 0, '--table-cell-vertical-align': 'top', maxHeight: '420px', overflowY: 'auto' }}
                                    loading={loading.lead_score_quality}
                                    loadingN={8}
                                    columns={leadTouchBreakdownSourceColumns}
                                    elements={leadTouchBreakdownSourceTableData}
                                    defaultSort={{ key: 'touch_total', direction: 'desc' }}
                                />
                            </Card>}

                            {showLeadAnalyticsCards && <div className={styles.utmReferrerCardsRow}>
                                <Card appearance="modern" title="CORSI - LEAD" integratedTable style={{ flex: 1, minHeight: '400px' }} actions={<Button appearance="subtle" onClick={handleExportProductLeadCsv}><ExportIcon style={{ width: '11px', height: '11px' }} /> Esporta</Button>}>
                                    <div className={styles.productLeadSearchWrapper}>
                                        <div className={styles.productLeadFiltersBar}>
                                            <div className={styles.productLeadFilterControl}>
                                                <TextInput appearance="modern"
                                                    type="search"
                                                    placeholder="Cerca corso..."
                                                    value={productLeadSearch}
                                                    onKeyUp={(value) => setProductLeadSearch(value ?? '')}
                                                />
                                            </div>
                                            <div className={styles.productLeadFilterControl}>
                                                <Select appearance="modern" portal
                                                    selected={productLeadType}
                                                    options={CourseTypes}
                                                    placeholder="Tutti i tipi"
                                                    nullable
                                                    nullLabel="Tutti i tipi"
                                                    onSelect={(option) => setProductLeadType(option?.value ? option : null)}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                            <div className={styles.productLeadFilterControl}>
                                                <Select appearance="modern" portal
                                                    selected={productLeadCategory}
                                                    options={CourseCategories}
                                                    placeholder="Tutte le categorie"
                                                    nullable
                                                    nullLabel="Tutte le categorie"
                                                    onSelect={(option) => setProductLeadCategory(option?.value ? option : null)}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <Table appearance="minimal"
                                        style={{ borderRadius: 0, '--table-cell-vertical-align': 'top', maxHeight: '420px', overflowY: 'auto' }}
                                        loading={loading.product_lead_stats}
                                        loadingN={8}
                                        columns={[
                                            { key: 'course_name', label: 'CORSO', type: 'custom', wrap: true, style: { padding: '0.85rem 1rem', alignItems: 'flex-start' } },
                                            { key: 'lead_total', label: 'LEAD', type: 'custom', wrap: true, style: { padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                            { key: 'lead_won_total', label: 'LEAD VINTI', type: 'custom', wrap: true, style: {padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                            { key: 'lead_won_rate', label: 'CONV %', type: 'custom', style: { padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        ]}
                                        elements={filteredProductLeadStatsTableData}
                                        defaultSort={{ key: 'lead_total', direction: 'desc' }}
                                        onRowClick={(row) => {
                                            if (!row?.id) return;
                                            toggleProductLeadRow(row.id);
                                        }}
                                    />
                                </Card>

                                <Card appearance="modern" title="UTM - LEAD" integratedTable style={{ flex: 1 }} actions={<Button appearance="subtle" onClick={handleExportSourceLeadCsv}><ExportIcon style={{ width: '11px', height: '11px' }} /> Esporta</Button>}>
                                    <div className={styles.productLeadSearchWrapper}>
                                        <div className={styles.productLeadFiltersBar}>
                                            <div className={styles.productLeadFilterControl}>
                                                <TextInput appearance="modern"
                                                    type="search"
                                                    placeholder="Cerca valore UTM o corso..."
                                                    value={sourceLeadSearch}
                                                    onKeyUp={(value) => setSourceLeadSearch(value ?? '')}
                                                />
                                            </div>
                                            <div className={styles.productLeadFilterControl}>
                                                <Select appearance="modern" portal
                                                    selected={sourceLeadBreakdown}
                                                    options={sourceLeadBreakdownOptions}
                                                    placeholder="Raggruppa per"
                                                    onSelect={(option) => setSourceLeadBreakdown(option?.value ? option : sourceLeadBreakdownOptions[0])}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <Table appearance="minimal"
                                        style={{ borderRadius: 0, '--table-cell-vertical-align': 'top', maxHeight: '420px', overflowY: 'auto' }}
                                        loading={loading.source_lead_stats}
                                        loadingN={8}
                                        columns={[
                                            { key: 'utm_source', label: `${String(sourceLeadBreakdown?.name || 'Source').toUpperCase()} UTM`, type: 'custom', wrap: true, style: { padding: '0.85rem 1rem', alignItems: 'flex-start', justifyContent: 'flex-start' } },
                                            { key: 'lead_total', label: 'LEAD', type: 'custom', wrap: true, style: { padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                            { key: 'lead_won_total', label: 'LEAD VINTI', type: 'custom', wrap: true, style: { padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                            { key: 'lead_won_rate', label: 'CONV %', type: 'custom', style: { padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        ]}
                                        elements={sourceLeadStatsTableData}
                                        defaultSort={{ key: 'lead_total', direction: 'desc' }}
                                        onRowClick={(row) => {
                                            if (!row?.id) return;
                                            toggleSourceLeadRow(row.id);
                                        }}
                                    />
                                </Card>
                            </div>}

                            {showLeadAnalyticsCards && <Card appearance="modern" title="PIANIFICAZIONE LEAD PER TARGET ISCRITTI" integratedTable style={{ flex: 1, flexBasis: '100%' }}>
                                <div className={styles.productLeadSearchWrapper}>
                                    <div className={styles.productLeadFiltersBar}>
                                        <div className={styles.productLeadFilterControl} style={{ maxWidth: '320px' }}>
                                            <TextInput appearance="modern"
                                                type="number"
                                                placeholder="Target iscritti per corso"
                                                value={leadTargetEnrollments}
                                                min={0}
                                                onKeyUp={(numericValue, rawValue) => {
                                                    if (rawValue === '') {
                                                        setLeadTargetEnrollments('');
                                                        return;
                                                    }
                                                    setLeadTargetEnrollments(Math.max(0, Number(numericValue || 0)));
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <Table appearance="minimal"
                                    style={{ borderRadius: 0, '--table-cell-vertical-align': 'middle', maxHeight: '460px', overflowY: 'auto' }}
                                    loading={loading.product_lead_stats_12m || loading.lead_since_last_edition}
                                    loadingN={8}
                                    columns={[
                                        { key: 'course_name', label: 'CORSO', type: 'custom', wrap: true, style: { minWidth: '280px', padding: '0.85rem 1rem', alignItems: 'flex-start' } },
                                        { key: 'historical_leads', label: 'LEAD 12M', type: 'custom', style: { minWidth: '120px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'historical_won', label: 'ISCRITTI STORICI', type: 'custom', style: { minWidth: '150px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'historical_conversion', label: 'CONV STORICA', type: 'custom', style: { minWidth: '130px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'conversion_source', label: 'FONTE CONV', type: 'custom', style: { minWidth: '130px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'target_enrollments', label: 'TARGET ISCRITTI/CORSO', type: 'custom', style: { minWidth: '180px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'required_leads', label: 'LEAD PREVISTI', type: 'custom', style: { minWidth: '140px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'lead_from_last_edition', label: 'LEAD DA ULTIMA EDIZ.', type: 'custom', style: { minWidth: '170px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'lead_gap', label: 'DIFFERENZA', type: 'custom', style: { minWidth: '130px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                    ]}
                                    elements={courseLeadForecastTableData}
                                    defaultSort={{ key: 'required_leads', direction: 'desc' }}
                                />

                                {!loading.product_lead_stats_12m && !loading.lead_since_last_edition && leadForecastTotals.hasRows && (
                                    <div className={styles.productLeadSearchWrapper} style={{ paddingBottom: '1rem' }}>
                                        <div className={typo.caption} style={{ color: 'rgba(var(--text-color-rgb), 66%)' }}>
                                            Lead previsti totali: {formatNumber(leadForecastTotals.totalRequiredLeads)} • Lead da ultima edizione: {formatNumber(leadForecastTotals.totalLeadsFromLastEdition)} • Differenza: {formatNumber(leadForecastTotals.totalRequiredLeads - leadForecastTotals.totalLeadsFromLastEdition)}
                                        </div>
                                    </div>
                                )}
                            </Card>}

                            {showLeadAnalyticsCards && <Card appearance="modern" title="CONVERSIONE PER EDIZIONE" integratedTable style={{ flex: 1, flexBasis: '100%', overflow: 'visible' }}>
                                <Table appearance="minimal"
                                    loading={loading.conversion_by_edition}
                                    loadingN={8}
                                    style={{ borderRadius: 0, '--table-cell-vertical-align': 'middle', maxHeight: '380px', overflowY: 'auto' }}
                                    columns={[
                                        { key: 'course_name', label: 'CORSO', type: 'custom', wrap: true, style: { minWidth: '340px', padding: '0.85rem 1rem', alignItems: 'flex-start' } },
                                        { key: 'conversion_rate', label: 'CONV %', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'lead_total', label: 'LEAD', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'enrolled_total', label: 'ISCRITTI', type: 'custom', style: { minWidth: '110px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                    ]}
                                    elements={editionConversionTrendTableData}
                                    defaultSort={{ key: 'conversion_rate', direction: 'desc' }}
                                />

                                {!loading.conversion_by_edition && editionConversionTrendTableData.length === 0 && (
                                    <div className={typo.caption} style={{ color: 'var(--text-secondary)', padding: '1rem' }}>
                                        Nessun dato edizioni disponibile al momento.
                                    </div>
                                )}
                            </Card>}

                            <div className={styles.reportSectionHeader}>
                                <h2 id="marketing-acquisti" className={styles.reportSectionTitle}>Acquisti diretti</h2>
                                <div className={styles.reportSectionObjective}>
                                    Performance economica dei corsi ad acquisto diretto.
                                </div>
                            </div>

                            <Card appearance="modern" title="CORSI - ACQUISTI DIRETTI" integratedTable style={{ flex: 1, flexBasis: '100%' }} actions={<Button appearance="subtle" onClick={handleExportNonApplicationCourseSalesCsv}><ExportIcon style={{ width: '11px', height: '11px' }} /> Esporta</Button>}>
                                <Table appearance="minimal"
                                    style={{ borderRadius: 0, '--table-cell-vertical-align': 'middle' }}
                                    loading={loading.non_application_course_sales}
                                    loadingN={8}
                                    columns={[
                                        { key: 'course_name', label: 'CORSO', type: 'custom', wrap: true, style: { width: '100%', minWidth: '470px', padding: '0.85rem 1rem', alignItems: 'flex-start' } },
                                        { key: 'unique_users', label: 'UTENTI UNICI', type: 'custom', style: { minWidth: '150px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'purchases', label: 'ACQUISTI', type: 'custom', style: { minWidth: '120px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'sold_total', label: 'TOTALE VENDUTO', type: 'custom', style: { minWidth: '160px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'conversion_rate', label: 'CONV%', type: 'custom', style: { minWidth: '190px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                    ]}
                                    elements={nonApplicationCourseSalesTableData}
                                    defaultSort={{ key: 'purchases', direction: 'desc' }}
                                    onRowClick={onDirectRevenueRowClick}
                                />
                            </Card>

                            <Card appearance="modern" title="ANAGRAFICA ACQUISTO DIRETTO - BREAKDOWN PER CORSO" integratedTable style={{ flex: 1, flexBasis: '100%' }} actions={<Button appearance="subtle" onClick={handleExportNonApplicationCourseDemographicsCsv}><ExportIcon style={{ width: '11px', height: '11px' }} /> Esporta</Button>}>
                                <div className={styles.productLeadSearchWrapper}>
                                    <div className={styles.productLeadFiltersBar}>
                                        <div className={styles.productLeadFilterControl}>
                                            <TextInput appearance="modern"
                                                type="search"
                                                placeholder="Cerca corso..."
                                                value={directDemographicsSearch}
                                                onKeyUp={(value) => setDirectDemographicsSearch(value ?? '')}
                                            />
                                        </div>
                                        <div className={styles.productLeadFilterControl}>
                                            <Select appearance="modern" portal
                                                selected={directDemographicsType}
                                                options={CourseTypes}
                                                placeholder="Tutti i tipi"
                                                nullable
                                                nullLabel="Tutti i tipi"
                                                onSelect={(option) => setDirectDemographicsType(option?.value ? option : null)}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                        <div className={styles.productLeadFilterControl}>
                                            <Select appearance="modern" portal
                                                selected={directDemographicsCategory}
                                                options={CourseCategories}
                                                placeholder="Tutte le categorie"
                                                nullable
                                                nullLabel="Tutte le categorie"
                                                onSelect={(option) => setDirectDemographicsCategory(option?.value ? option : null)}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <Table appearance="minimal"
                                    style={{ borderRadius: 0, '--table-cell-vertical-align': 'middle', maxHeight: '600px', overflowY: 'auto' }}
                                    loading={loading.non_application_course_demographics}
                                    loadingN={8}
                                    columns={[
                                        { key: 'course_name', label: 'CORSO', type: 'custom', wrap: true, style: { width: '100%', minWidth: '300px', padding: '0.85rem 1rem', alignItems: 'flex-start' } },
                                        { key: 'buyer_type', label: 'ACQUIRENTE TIPO', type: 'custom', wrap: true, style: { minWidth: '260px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'purchases', label: 'ACQUISTI', type: 'custom', style: { minWidth: '120px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'age_breakdown', label: 'ETA', type: 'custom', wrap: true, style: { minWidth: '190px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'region_breakdown', label: 'PROVENIENZA', type: 'custom', wrap: true, style: { minWidth: '220px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'gender_breakdown', label: 'GENERE', type: 'custom', wrap: true, style: { minWidth: '170px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'employment_breakdown', label: 'OCCUPAZIONE', type: 'custom', wrap: true, style: { minWidth: '210px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                    ]}
                                    elements={filteredDirectDemographicsCourseTableData}
                                    defaultSort={{ key: 'purchases', direction: 'desc' }}
                                />
                            </Card>

                            <Card appearance="modern" title="ACQUISTI - UTM" integratedTable style={{ flex: 1, flexBasis: '100%' }}>
                                <div className={styles.productLeadSearchWrapper}>
                                    <MultiStateSwitch
                                        style={{ padding: '0.5rem 0' }}
                                        statesStyle={{ fontSize: '0.75rem', padding: '0' }}
                                        states={["SOURCE", "MEDIUM", "CAMPAIGN"]}
                                        selected={["source", "medium", "campaign"].indexOf(directSalesUtmBreakdownState)}
                                        onStateChange={(index) => setDirectSalesUtmBreakdownState(["source", "medium", "campaign"][index] || "source")}
                                    />
                                </div>
                                <Table appearance="minimal"
                                    style={{ borderRadius: 0, '--table-cell-vertical-align': 'top', maxHeight: '420px', overflowY: 'auto' }}
                                    loading={loading.non_application_course_sales_utm}
                                    loadingN={8}
                                    columns={[
                                        { key: 'utm_value', label: directSalesUtmBreakdownState.toUpperCase(), type: 'custom', wrap: true, style: { minWidth: '470px', padding: '0.85rem 1rem', alignItems: 'flex-start', justifyContent: 'flex-start' } },
                                        { key: 'purchases', label: 'ACQUISTI', type: 'custom', style: { minWidth: '130px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'sold_total', label: 'VALORE', type: 'custom', style: { minWidth: '160px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                        { key: 'purchases_share', label: '% ACQUISTI', type: 'custom', style: { minWidth: '130px', padding: '0.7rem 0.9rem', alignItems: 'center' } },
                                    ]}
                                    elements={nonApplicationCourseSalesUtmTableData}
                                    defaultSort={{ key: 'purchases', direction: 'desc' }}
                                    onRowClick={(row) => {
                                        if (!row?.id) return;
                                        toggleDirectSalesUtmRow(row.id);
                                    }}
                                />
                            </Card>

                            <Dialog appearance="modern"
                                open={Boolean(leadScoreSnapshotDialogRow)}
                                title={`Breakdown UTM source - ${leadScoreSnapshotDialogRow?.course_name?.value || 'Corso'}`}
                                onClose={() => setLeadScoreSnapshotDialogRow(null)}
                                style={{ width: '96vw', maxWidth: '1560px' }}
                                contentMaxHeight="76vh"
                                overflow="auto"
                            >
                                {leadScoreSnapshotDialogLoading && (
                                    <div className={styles.productLeadEmpty}>Caricamento breakdown per UTM source...</div>
                                )}
                                {!leadScoreSnapshotDialogLoading && leadScoreSnapshotDialogError && (
                                    <div className={styles.productLeadEmpty} style={{ color: 'var(--sf-red)' }}>{leadScoreSnapshotDialogError}</div>
                                )}
                                {!leadScoreSnapshotDialogLoading && !leadScoreSnapshotDialogError && leadScoreSnapshotDialogRows.length === 0 && (
                                    <div className={styles.productLeadEmpty}>Nessun dato disponibile per UTM source su questo corso.</div>
                                )}
                                {!leadScoreSnapshotDialogLoading && !leadScoreSnapshotDialogError && leadScoreSnapshotDialogRows.length > 0 && (
                                    <Table appearance="minimal"
                                        style={{ borderRadius: 8, '--table-cell-vertical-align': 'middle', maxHeight: '64vh', overflowY: 'auto' }}
                                        columns={leadScoreSnapshotSourceColumns}
                                        elements={leadScoreSnapshotDialogRows}
                                        defaultSort={{ key: 'lead_total', direction: 'desc' }}
                                    />
                                )}
                            </Dialog>

                            <div className={styles.reportSectionHeader}>
                                <h2 id="marketing-web-vitals" className={styles.reportSectionTitle}>Web Vitals</h2>
                                <div className={styles.reportSectionObjective}>
                                    Andamento Core Web Vitals aggregati per dispositivo e confronto con il periodo precedente.
                                </div>
                            </div>

                            <Card appearance="modern" title="WEB VITALS" style={{ flex: 1, flexBasis: '100%' }}>
                                <div className={styles.productLeadFiltersBar} style={{ marginBottom: '1rem' }}>
                                    <div className={styles.productLeadFilterControl} style={{ maxWidth: '240px' }}>
                                        <Select appearance="modern" portal
                                            options={WEB_VITALS_DEVICE_OPTIONS}
                                            selected={selectedWebVitalsDevice}
                                            onSelect={(item) => setSelectedWebVitalsDevice(item || WEB_VITALS_DEVICE_OPTIONS[0])}
                                            placeholder="Seleziona dispositivo"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>

                                <div className={styles.analyticRow}>
                                    {webVitalsMetricBlocks.slice(0, 3).map((metric) => (
                                        <MetricStatBlock
                                            key={metric.key}
                                            title={metric.title}
                                            titleColor={metric.titleColor}
                                            actualValue={metric.actualValue}
                                            previousValue={metric.previousValue}
                                            percentageValue={metric.percentageValue}
                                            isLoss={metric.isLoss}
                                            loading={loading.web_vitals}
                                            badgeDecimals={2}
                                            containerClassName={styles.analytic}
                                            titleClassName={styles.analyticTitle}
                                            actualClassName={styles.actualAnalytic}
                                            previousClassName={styles.previousAnalytic}
                                            skeletonClassName={styles.analyticSkeleton}
                                        />
                                    ))}
                                </div>

                                <div className={styles.analyticRow} style={{ marginTop: '1rem' }}>
                                    {webVitalsMetricBlocks.slice(3).map((metric) => (
                                        <MetricStatBlock
                                            key={metric.key}
                                            title={metric.title}
                                            titleColor={metric.titleColor}
                                            actualValue={metric.actualValue}
                                            previousValue={metric.previousValue}
                                            percentageValue={metric.percentageValue}
                                            isLoss={metric.isLoss}
                                            loading={loading.web_vitals}
                                            badgeDecimals={2}
                                            containerClassName={styles.analytic}
                                            titleClassName={styles.analyticTitle}
                                            actualClassName={styles.actualAnalytic}
                                            previousClassName={styles.previousAnalytic}
                                            skeletonClassName={styles.analyticSkeleton}
                                        />
                                    ))}
                                </div>
                            </Card>

                        </div>
                    </div>
                </div>
            </div>
        </HeaderFooterLayout>

        </>
    )
}

export default MarketingReports;
